import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// 🔧 CREDENTIALS
// ─────────────────────────────────────────────────────────────────────────────

const SUPABASE_URL      = "https://etrcqlgwyakljjnnvrzg.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV0cmNxbGd3eWFrbGpqbm52cnpnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ3OTkxMjEsImV4cCI6MjA5MDM3NTEyMX0.0b8Tmnc4hDhrOvmyVdlVohWRLd00msmxOllZ3TLT7Xw";

// 🔑 Service Role Key is NOT stored here — it lives securely in
// Supabase Edge Functions on the server. The browser never sees it.

const ADMIN_SECRET_PATH = process.env.REACT_APP_ADMIN_PATH;
const ADMIN_EMAIL       = "vijayendra.bhoir@gmail.com";

// 📱 Your WhatsApp number — include country code, no + sign, no spaces
// Example: India number 98765 43210 becomes "919876543210"
const WHATSAPP_NUMBER   = "919930057678";

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL STYLES injected once
// ─────────────────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Outfit:wght@300;400;500;600;700;800&display=swap');

  /* ── FUELDOST DESIGN TOKENS ── */
  :root {
    --orange:    #F97316;
    --orange-dk: #EA580C;
    --gold:      #FFAA00;
    --blue:      #1D6FC4;
    --blue-lt:   #2980D4;
    --green:     #3DB54A;
    --bg:        #FDF3EE;
    --surface:   #FFFFFF;
    --text:      #1A1A1A;
    --text2:     #475569;
    --text3:     #9CA3AF;
    --border:    #E5D5CC;
    --orange-10: rgba(249,115,22,0.1);
    --orange-20: rgba(249,115,22,0.2);
    --blue-10:   rgba(29,111,196,0.1);
    --green-10:  rgba(61,181,74,0.1);

    /* ── New FuelDost brand palette ── */
    --fd-primary:        #7B2D00;
    --fd-primary-mid:    #C84B00;
    --fd-accent:         #F97316;
    --fd-accent-light:   #FB923C;
    --fd-bg-page:        #FDF3EE;
    --fd-bg-warm:        #FDE8D8;
    --fd-bg-input:       #F5EDE8;
    --fd-text-dark:      #1A1A1A;
    --fd-text-gray:      #6B7280;
    --fd-text-light:     #9CA3AF;
    --fd-available:      #16A34A;
    --fd-petrol:         #F97316;
    --fd-diesel:         #2563EB;
    --fd-power:          #22C55E;
    --fd-premium-bg:     #FEF3C7;
    --fd-premium-text:   #92400E;
    --fd-amber:          #D97706;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-text-size-adjust: 100%; }
  body {
    background: linear-gradient(180deg, #FDE8D8 0%, #FDF3EE 30%, #FDF3EE 100%) fixed;
    min-height: 100vh;
    overscroll-behavior-y: contain;
  }

  /* ── ANIMATIONS ── */
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(16px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position:  400px 0; }
  }
  @keyframes pulse-ring {
    0%   { transform:scale(1);   opacity:0.6; }
    100% { transform:scale(1.6); opacity:0; }
  }
  @keyframes bar-fill { from { width:0%; } }
  @keyframes float {
    0%,100% { transform:translateY(0); }
    50%     { transform:translateY(-6px); }
  }
  @keyframes spin-slow  { to { transform:rotate(360deg); } }
  @keyframes slideUp {
    from { transform:translateY(100%); opacity:0; }
    to   { transform:translateY(0);    opacity:1; }
  }
  @keyframes slideInRight {
    from { opacity:0; transform:translateX(120%); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes card-nudge {
    0%   { transform:translateY(0) scale(1); }
    40%  { transform:translateY(-5px) scale(1.015); box-shadow:0 16px 40px rgba(249,115,22,0.18); border-color:#F9731688; }
    100% { transform:translateY(0) scale(1); }
  }
  @keyframes live-dot {
    0%,100% { box-shadow:0 0 0 0 rgba(61,181,74,0.5); }
    50%     { box-shadow:0 0 0 5px rgba(61,181,74,0); }
  }

  /* ── PUMP CARD ── */
  .pub-card {
    background: #FFFFFF;
    border-radius: 16px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.07);
    overflow: hidden;
    margin-bottom: 16px;
    cursor: pointer;
    transition: transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s;
    position: relative;
    animation: fadeUp 0.4s ease both;
    -webkit-tap-highlight-color: transparent;
    padding: 0;
    border: none;
  }
  .pub-card.nudge { animation: fadeUp 0.4s ease both, card-nudge 0.65s ease 1.2s both; }
  @media (hover:hover) {
    .pub-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.13);
    }
  }
  .pub-card:active { transform:scale(0.98); box-shadow:0 1px 6px rgba(0,0,0,0.07); }
  .pub-card.closed-pump { opacity:0.6; filter:grayscale(0.3); }
  .pub-card.closed-pump:hover { opacity:0.85; filter:grayscale(0); }

  /* ── TAP HINT ── */
  .tap-hint {
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 10px; font-weight: 600; color: var(--text3);
    font-family: 'Plus Jakarta Sans', sans-serif;
    pointer-events: none;
  }

  /* ── FILTER CHIPS ── */
  .filter-chip {
    padding: 0 14px; border-radius: 999px;
    font-size: 14px; font-weight: 500; cursor: pointer;
    border: 1px solid #E5D5CC;
    background: #F5EDE8; color: #7B2D00;
    transition: all 0.15s;
    font-family: 'Plus Jakarta Sans', sans-serif;
    white-space: nowrap; height: 36px;
    display: flex; align-items: center; gap: 6px;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
  }
  .filter-chip.active {
    background: linear-gradient(135deg, #7B2D00 0%, #C84B00 100%);
    border-color: transparent; color: #fff;
  }
  .filter-chip:not(.active):hover { border-color: #C84B00; color: #C84B00; }

  /* ── BRAND CHIPS ── */
  .brand-chip {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 14px; border-radius: 99px;
    font-size: 12px; font-weight: 700; cursor: pointer;
    border: 1.5px solid var(--border);
    background: var(--surface); color: var(--text2);
    transition: all 0.15s; white-space: nowrap; min-height: 40px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    -webkit-tap-highlight-color: transparent;
  }
  .brand-chip.active { border-color:var(--bc); background:var(--bb); color:var(--bc); }
  .brand-dot { width:8px; height:8px; border-radius:50%; background:var(--bc); flex-shrink:0; }

  /* ── CHIPS SCROLL ── */
  .chips-scroll {
    display: flex; gap: 8px;
    overflow-x: auto; -webkit-overflow-scrolling: touch;
    scrollbar-width: none; padding-bottom: 2px;
  }
  .chips-scroll::-webkit-scrollbar { display:none; }

  /* ── DIRECTIONS BUTTON — logo blue ── */
  .dir-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    padding: 11px 18px; border-radius: 12px;
    font-size: 13px; font-weight: 700; color: #fff;
    background: linear-gradient(135deg, var(--blue), var(--blue-lt));
    border: none; cursor: pointer;
    font-family: 'Plus Jakarta Sans', sans-serif;
    transition: transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 3px 12px var(--blue-10);
    min-height: 44px; -webkit-tap-highlight-color: transparent;
  }
  .dir-btn:active { transform:scale(0.95); }
  @media (hover:hover) {
    .dir-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(29,111,196,0.35); }
  }

  /* ── LOCATION BUTTON ── */
  .loc-btn {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 18px; border-radius: 12px;
    font-size: 13px; font-weight: 700;
    color: var(--blue); background: var(--blue-10);
    border: 1.5px solid rgba(29,111,196,0.25);
    cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
    transition: all 0.18s; white-space: nowrap; min-height: 44px;
    -webkit-tap-highlight-color: transparent;
  }
  .loc-btn:active { background: rgba(29,111,196,0.2); }

  /* ── FUEL BARS ── */
  .fuel-bar-track {
    height: 10px; background: #F5EDE8;
    border-radius: 999px; overflow: hidden;
  }
  .fuel-bar-fill {
    height: 100%; border-radius: 999px;
    animation: bar-fill 0.85s cubic-bezier(0.4,0,0.2,1) both;
    animation-delay: var(--delay, 0.1s);
  }

  /* ── SKELETON ── */
  .skeleton {
    background: linear-gradient(90deg, #E2E8F0 25%, #EEF2FF 50%, #E2E8F0 75%);
    background-size: 400px 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 10px;
  }

  /* ── SEARCH ── */
  .search-input {
    flex: 1; border: none; background: transparent;
    font-size: 15px; font-family: 'Plus Jakarta Sans', sans-serif;
    color: #1A1A1A; outline: none;
    padding: 0 8px; -webkit-appearance: none;
  }
  .search-input::placeholder { color: #9CA3AF; }
  .search-bar-wrap {
    display: flex; align-items: center;
    background: #F5EDE8; border-radius: 999px; height: 48px;
    padding: 0 8px 0 16px; width: 100%;
  }

  /* ── MODAL — bottom sheet mobile, centred desktop ── */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(15,23,42,0.5); backdrop-filter: blur(6px);
    z-index: 200; display: flex;
    align-items: flex-end; justify-content: center;
    animation: fadeIn 0.2s ease;
  }
  .modal-box {
    background: var(--surface);
    border-radius: 24px 24px 0 0;
    padding: 20px 20px 32px; width: 100%; max-width: 100%;
    box-shadow: 0 -8px 48px rgba(15,23,42,0.18);
    animation: slideUp 0.3s cubic-bezier(.34,1.2,.64,1);
    max-height: 92vh; overflow-y: auto; -webkit-overflow-scrolling: touch;
  }
  .modal-handle {
    width: 40px; height: 4px; background: var(--border);
    border-radius: 99px; margin: 0 auto 16px;
  }
  @media (min-width: 600px) {
    .modal-overlay { align-items: center; padding: 20px; }
    .modal-box { border-radius: 24px; max-width: 440px; padding: 28px; animation: fadeUp 0.3s cubic-bezier(.34,1.56,.64,1); }
    .modal-handle { display:none; }
  }

  /* ── LAYOUT HELPERS ── */
  .controls-row { display:flex; gap:10px; margin-bottom:12px; align-items:stretch; }
  .sort-row { display:flex; gap:8px; margin-bottom:18px; align-items:center; flex-wrap:wrap; }
  .sort-label { font-size:12px; font-weight:600; color:var(--text3); white-space:nowrap; }
  .sort-btn {
    flex:1; min-width:0; padding:10px 12px; border-radius:10px;
    font-size:12px; font-weight:600; cursor:pointer;
    font-family:'Plus Jakarta Sans', sans-serif; transition:all 0.15s;
    min-height:42px; white-space:nowrap; -webkit-tap-highlight-color:transparent;
  }
  .hero-kpi-row { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; max-width:340px; margin:0 auto; }
  .cards-grid { display:grid; grid-template-columns:1fr; gap:12px; }
  @media (min-width:600px) { .cards-grid { grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:16px; } }

  /* ── PRODUCT PILL ── */
  .product-pill {
    display:inline-flex; align-items:center; gap:4px;
    font-size:10px; font-weight:700; color:var(--blue);
    background:var(--blue-10); border:1px solid rgba(29,111,196,0.2);
    border-radius:99px; padding:3px 8px; cursor:pointer;
    font-family:'Plus Jakarta Sans', sans-serif; transition:all 0.15s;
  }
  .product-pill:hover { background:rgba(29,111,196,0.18); }

  /* ── W&M SHIELD ── */
  @keyframes shield-pulse {
    0%,100% { box-shadow:0 0 0 0 rgba(61,181,74,0.4); }
    50%      { box-shadow:0 0 0 5px rgba(61,181,74,0); }
  }
  .wm-shield {
    display:inline-flex; align-items:center; gap:4px;
    background:linear-gradient(135deg,#DCFCE7,#BBF7D0);
    border:1.5px solid #86EFAC; border-radius:8px;
    padding:3px 8px; font-size:10px; font-weight:800;
    color:#15803D; cursor:pointer;
    font-family:'Plus Jakarta Sans', sans-serif;
    transition:all 0.18s; -webkit-tap-highlight-color:transparent;
    animation:shield-pulse 3s ease-in-out infinite;
  }
  .wm-shield.expired { background:#FEF9C3; border-color:#FDE047; color:#A16207; animation:none; }

  /* ── HOURS BADGE ── */
  .hours-badge {
    display:inline-flex; align-items:center; gap:4px;
    font-size:10px; font-weight:700; border-radius:7px;
    padding:3px 8px; font-family:'Plus Jakarta Sans', sans-serif; white-space:nowrap;
  }

  /* ── HIGHWAY NAV BUTTON ── */
  @keyframes nav-glow {
    0%,100% { box-shadow:0 0 0 0 rgba(29,111,196,0.45), 0 6px 20px rgba(29,111,196,0.3); }
    50%      { box-shadow:0 0 0 8px rgba(29,111,196,0), 0 6px 20px rgba(29,111,196,0.3); }
  }
  @keyframes nav-launch {
    0% { transform:scale(1); } 30% { transform:scale(0.93); }
    60% { transform:scale(1.04); } 100% { transform:scale(1); }
  }
  @keyframes nav-chevron {
    0%,100% { transform:translateX(0); } 50% { transform:translateX(5px); }
  }
  .hwy-nav-btn {
    display:flex; align-items:center; justify-content:space-between;
    width:100%; padding:0;
    background:linear-gradient(135deg,var(--blue),var(--blue-lt) 60%,#60A5FA);
    border:none; border-radius:18px; cursor:pointer; font-family:inherit;
    transition:filter 0.15s; min-height:64px; overflow:hidden;
    position:relative; -webkit-tap-highlight-color:transparent;
    animation:nav-glow 2.5s ease-in-out infinite;
  }
  .hwy-nav-btn::before {
    content:""; position:absolute; top:0; left:0; right:0; height:50%;
    background:rgba(255,255,255,0.1); border-radius:18px 18px 0 0; pointer-events:none;
  }
  .hwy-nav-btn:active { filter:brightness(1.12); animation:nav-launch 0.3s ease forwards; }
  .hwy-nav-btn .nav-left { display:flex; flex-direction:column; align-items:flex-start; gap:3px; padding:14px 0 14px 18px; flex:1; }
  .hwy-nav-btn .nav-label { font-size:17px; font-weight:900; color:#fff; letter-spacing:-0.02em; font-family:'Outfit',sans-serif; }
  .hwy-nav-btn .nav-sub   { font-size:11px; font-weight:600; color:rgba(255,255,255,0.65); }
  .hwy-nav-btn .nav-right { display:flex; align-items:center; justify-content:center; width:64px; align-self:stretch; background:rgba(0,0,0,0.18); border-left:1px solid rgba(255,255,255,0.12); flex-shrink:0; }
  .hwy-nav-chevron { animation:nav-chevron 1.4s ease-in-out infinite; }

  /* ── BOTTOM NAV ── */
  .bottom-nav {
    position:fixed; bottom:0; left:0; right:0;
    background:#FFFFFF;
    box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
    display:flex; z-index:100; height:64px;
    justify-content: space-around; align-items: center;
    padding-bottom: env(safe-area-inset-bottom, 4px);
  }
  .bottom-nav-tab {
    flex:1; display:flex; flex-direction:column; align-items:center;
    justify-content:center; padding:4px 8px; cursor:pointer;
    border:none; background:transparent;
    font-family:'Plus Jakarta Sans', sans-serif;
    transition:all 0.15s; -webkit-tap-highlight-color:transparent;
    gap:4px;
  }
  .bottom-nav-tab .nav-icon-wrap {
    border-radius: 999px; padding: 4px 8px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .bottom-nav-tab.active .nav-icon-wrap {
    background: #FDE8D8; padding: 4px 16px;
  }
  .bottom-nav-tab .nav-label { font-size:11px; font-weight:500; color:#9CA3AF; }
  .bottom-nav-tab.active .nav-label { color:#7B2D00; font-weight:600; }
  .bottom-nav-tab:active .nav-icon-wrap { transform:scale(0.9); }
  @media (min-width:768px) { .bottom-nav { display:none; } }

  /* ── HIGHWAY MODE extras ── */
  @keyframes hwy-pulse { 0%,100%{opacity:1;transform:scale(1);}50%{opacity:.7;transform:scale(.97);} }
  @keyframes hwy-slide { from{opacity:0;transform:translateX(40px);}to{opacity:1;transform:translateX(0);} }
  @keyframes hwy-spin  { to{transform:rotate(360deg);} }
  .hwy-card { background:#111827; border:2px solid #374151; border-radius:20px; padding:20px; cursor:pointer; transition:border-color 0.2s,transform 0.15s; -webkit-tap-highlight-color:transparent; animation:hwy-slide 0.35s ease both; }
  .hwy-card:active { transform:scale(0.97); }
  .hwy-card.hwy-best { border-color:var(--orange); box-shadow:0 0 0 1px rgba(249,115,22,0.3); }

  @keyframes scan-bar {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(400%); }
  }
  .hwy-scan-bar {
    height: 3px; background: #111827; border-radius: 99px;
    overflow: hidden; margin: 8px 0;
  }
  .hwy-scan-fill {
    height: 100%; width: 40%;
    background: linear-gradient(90deg, transparent, #F97316, transparent);
    animation: scan-bar 1.2s ease-in-out infinite;
  }
  .hwy-autocomplete-input {
    width: 100%; padding: 12px 14px;
    background: #111827; border: 1.5px solid #374151;
    border-radius: 12px; color: #F9FAFB;
    font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
    outline: none; transition: border-color 0.18s;
  }
  .hwy-autocomplete-input:focus { border-color: #F97316; }
  .hwy-autocomplete-input::placeholder { color: #6B7280; }
`

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

function loadGooglePlaces(apiKey) {
  if (!apiKey) return Promise.reject(new Error("Missing Google API key"));
  if (typeof window !== "undefined" && window.google?.maps?.places) return Promise.resolve();
  const existing = document.querySelector('script[data-fc-google-maps="1"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.maps?.places) {
        resolve();
        return;
      }
      const onLoad = () => {
        if (window.google?.maps?.places) resolve();
        else reject(new Error("Google Maps failed to load"));
      };
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", () => reject(new Error("Google script failed")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.fcGoogleMaps = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google script failed"));
    document.head.appendChild(script);
  });
}

function injectStyles() {
  if (document.getElementById("fc-styles")) return;
  // Viewport meta — ensures correct scaling on mobile
  if (!document.querySelector('meta[name="viewport"]')) {
    const mv = document.createElement("meta");
    mv.name = "viewport";
    mv.content = "width=device-width, initial-scale=1, maximum-scale=5";
    document.head.appendChild(mv);
  }
  const s = document.createElement("style");
  s.id = "fc-styles";
  s.textContent = GLOBAL_CSS;
  document.head.appendChild(s);
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUTING
// ─────────────────────────────────────────────────────────────────────────────
function getInitialPage() {
  const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");
  if (path === ADMIN_SECRET_PATH) return "admin-login";
  return "public";
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPABASE
// ─────────────────────────────────────────────────────────────────────────────
const hdrs = () => ({ "Content-Type":"application/json","apikey":SUPABASE_ANON_KEY,"Authorization":`Bearer ${SUPABASE_ANON_KEY}` });
const authHdrs = t => ({ "Content-Type":"application/json","apikey":SUPABASE_ANON_KEY,"Authorization":`Bearer ${t}` });
async function sb(path, opts={}) {
  const res = await fetch(`${SUPABASE_URL}${path}`, opts);
  if (!res.ok) { const e=await res.json().catch(()=>({})); throw new Error(e.message||e.error_description||"Request failed"); }
  return res.json().catch(()=>null);
}
const getPumps       = ()        => sb(`/rest/v1/pumps?select=*&is_suspended=eq.false&order=name.asc`, { headers:hdrs() });
const getAllPumps     = t        => sb(`/rest/v1/pumps?select=*&order=name.asc`, { headers:authHdrs(t) });
const loginApi       = (e,p)    => sb(`/auth/v1/token?grant_type=password`, { method:"POST",headers:hdrs(),body:JSON.stringify({email:e,password:p}) });
const getMyPump      = (e,t)    => sb(`/rest/v1/pumps?owner_email=eq.${encodeURIComponent(e)}&select=*`,{headers:authHdrs(t)}).then(r=>r?.[0]||null);
const updateStock    = (id,pe,di,cn,premiumValues,t) => {
  const now=new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
  return sb(`/rest/v1/pumps?id=eq.${id}`,{method:"PATCH",headers:{...authHdrs(t),"Prefer":"return=representation"},body:JSON.stringify({petrol:pe,diesel:di,cng:cn,...(premiumValues||{}),last_updated:now})});
};
const adminAddPump   = (d,t)    => sb(`/rest/v1/pumps`,{method:"POST",headers:{...authHdrs(t),"Prefer":"return=representation"},body:JSON.stringify(d)});
const adminUpdatePump= (id,d,t) => sb(`/rest/v1/pumps?id=eq.${id}`,{method:"PATCH",headers:{...authHdrs(t),"Prefer":"return=representation"},body:JSON.stringify(d)});
const adminDeletePump= (id,t)   => sb(`/rest/v1/pumps?id=eq.${id}`,{method:"DELETE",headers:authHdrs(t)});
const adminSuspend   = (id,s,t) => adminUpdatePump(id,{is_suspended:s},t);
const getSubs        = t        => sb(`/rest/v1/subscriptions?select=*&order=pump_id.asc`,{headers:authHdrs(t)});
const getSubForPump  = (pid,t)  => sb(`/rest/v1/subscriptions?pump_id=eq.${pid}&select=id`,{headers:authHdrs(t)});
const insertSub      = (d,t)    => sb(`/rest/v1/subscriptions`,{method:"POST",headers:{...authHdrs(t),"Prefer":"return=representation"},body:JSON.stringify(d)});
const patchSub       = (id,d,t) => sb(`/rest/v1/subscriptions?id=eq.${id}`,{method:"PATCH",headers:{...authHdrs(t),"Prefer":"return=representation"},body:JSON.stringify(d)});
const getOwnerClicks = (id,t)   => { const f=new Date(); f.setDate(f.getDate()-6); return sb(`/rest/v1/directions_clicks?pump_id=eq.${id}&date=gte.${f.toISOString().slice(0,10)}&select=date,fuel_type&order=date.asc`,{headers:authHdrs(t)}); };
const getAllClicks    = t        => { const f=new Date(); f.setDate(f.getDate()-29); return sb(`/rest/v1/directions_clicks?date=gte.${f.toISOString().slice(0,10)}&select=pump_id,date,fuel_type`,{headers:authHdrs(t)}); };
async function logClick(pid,ft){ const n=new Date(); try{ await sb(`/rest/v1/directions_clicks`,{method:"POST",headers:{...hdrs(),"Prefer":"return=minimal"},body:JSON.stringify({pump_id:pid,fuel_type:ft,date:n.toISOString().slice(0,10),hour:n.getHours()})}); }catch(_){} }

// ── Admin Auth API — calls Supabase Edge Functions (service key stays on server)
// The browser sends the admin's JWT token; the Edge Function verifies it is
// the admin before doing anything with elevated privileges.

// Helper — gets a fresh token by re-reading from Supabase session
// Avoids "Invalid JWT" errors caused by expired tokens
async function getFreshToken() {
  // Call Supabase to refresh the session and get a current token
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: "POST",
    headers: { "Content-Type":"application/json", "apikey": SUPABASE_ANON_KEY },
    body: JSON.stringify({ refresh_token: window.__fuelstockRefreshToken || "" }),
  });
  // If refresh fails (e.g. no refresh token stored), fall back to stored token
  if (!res.ok) return window.__fuelstockAdminToken || "";
  const data = await res.json();
  if (data.access_token) {
    window.__fuelstockAdminToken = data.access_token;
    if (data.refresh_token) window.__fuelstockRefreshToken = data.refresh_token;
  }
  return window.__fuelstockAdminToken || "";
}

async function adminCreateUser(email, password, adminToken) {
  // Use passed token directly — it was just obtained from login
  const token = adminToken || window.__fuelstockAdminToken || "";
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-create-user`, {
    method:  "POST",
    headers: {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey":        SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || "Could not create user account");
  return data;
}

async function adminResetPassword(email, newPassword, adminToken) {
  const token = adminToken || window.__fuelstockAdminToken || "";
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-reset-password`, {
    method:  "POST",
    headers: {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey":        SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password: newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || "Could not reset password");
  return data;
}

// ── Stock Update Log API ─────────────────────────────────────────────────────
const logStockUpdate  = (pump_id,t) => sb(`/rest/v1/stock_updates`, {
  method:"POST",
  headers:{...authHdrs(t),"Prefer":"return=minimal"},
  body:JSON.stringify({ pump_id, updated_at: new Date().toISOString(), date: new Date().toISOString().slice(0,10) })
});
const getTodayLog     = t => {
  const today = new Date().toISOString().slice(0,10);
  return sb(`/rest/v1/stock_updates?date=eq.${today}&select=pump_id,updated_at`, { headers:authHdrs(t) });
};
const getUpdateLog    = (days,t) => {
  const from = new Date(); from.setDate(from.getDate()-(days-1));
  return sb(`/rest/v1/stock_updates?date=gte.${from.toISOString().slice(0,10)}&select=pump_id,updated_at,date&order=date.desc,updated_at.desc`, { headers:authHdrs(t) });
};

// ── Products API ──────────────────────────────────────────────────────────────
const getProductsByPump = (pumpId)    => sb(`/rest/v1/pump_products?pump_id=eq.${pumpId}&order=category.asc,name.asc`, { headers:hdrs() });
const getAllProducts     = t           => sb(`/rest/v1/pump_products?select=*&order=pump_id.asc,category.asc`, { headers:authHdrs(t) });
const adminAddProduct   = (d,t)       => sb(`/rest/v1/pump_products`, { method:"POST", headers:{...authHdrs(t),"Prefer":"return=representation"}, body:JSON.stringify(d) });
const adminUpdateProduct= (id,d,t)    => sb(`/rest/v1/pump_products?id=eq.${id}`, { method:"PATCH", headers:{...authHdrs(t),"Prefer":"return=representation"}, body:JSON.stringify(d) });
const adminDeleteProduct= (id,t)      => sb(`/rest/v1/pump_products?id=eq.${id}`, { method:"DELETE", headers:authHdrs(t) });

// ── Owner product management ───────────────────────────────────────────────────
const ownerGetProducts  = (pumpId,t)  => sb(`/rest/v1/pump_products?pump_id=eq.${pumpId}&order=category.asc,name.asc`, { headers:authHdrs(t) });
const ownerAddProduct   = (d,t)       => sb(`/rest/v1/pump_products`, { method:"POST", headers:{...authHdrs(t),"Prefer":"return=representation"}, body:JSON.stringify(d) });
const ownerUpdateProduct= (id,d,t)    => sb(`/rest/v1/pump_products?id=eq.${id}`, { method:"PATCH", headers:{...authHdrs(t),"Prefer":"return=representation"}, body:JSON.stringify(d) });
const ownerDeleteProduct= (id,t)      => sb(`/rest/v1/pump_products?id=eq.${id}`, { method:"DELETE", headers:authHdrs(t) });

// ── Sales API ──────────────────────────────────────────────────────────────────
const recordSale        = (d,t)       => sb(`/rest/v1/product_sales`, { method:"POST", headers:{...authHdrs(t),"Prefer":"return=representation"}, body:JSON.stringify(d) });
const getOwnerSales     = (pumpId,t)  => {
  const from=new Date(); from.setDate(from.getDate()-6);
  return sb(`/rest/v1/product_sales?pump_id=eq.${pumpId}&sold_at=gte.${from.toISOString()}&select=*&order=sold_at.desc`, { headers:authHdrs(t) });
};
const getAllSales        = (t)         => {
  const from=new Date(); from.setDate(from.getDate()-29);
  return sb(`/rest/v1/product_sales?sold_at=gte.${from.toISOString()}&select=*&order=sold_at.desc`, { headers:authHdrs(t) });
};

// ── Brands ────────────────────────────────────────────────────────────────────
const BRANDS = [
  { id:"all",         label:"All Brands",   color:"#D97706", bg:"#FEF3C7" },
  { id:"Indian Oil",  label:"Indian Oil",   color:"#DC2626", bg:"#FEE2E2" },
  { id:"HPCL",        label:"HP",           color:"#15803D", bg:"#DCFCE7" },
  { id:"BPCL",        label:"BPCL",         color:"#1D4ED8", bg:"#DBEAFE" },
  { id:"Reliance",    label:"Reliance",     color:"#7C3AED", bg:"#EDE9FE" },
  { id:"Shell",       label:"Shell",        color:"#D97706", bg:"#FEF3C7" },
  { id:"Essar",       label:"Nayara",       color:"#0F766E", bg:"#CCFBF1" },
  { id:"Other",       label:"Other",        color:"#64748B", bg:"#F1F5F9" },
];

const PRODUCT_CATEGORIES = ["Engine Oil","Lubricants","Snacks & Beverages","Auto Accessories","Cleaning Products","Air Fresheners","Wiper Fluid","Tyres & Tubes","Other"];

// Premium fuel variants — keyed by brand
const PREMIUM_FUELS = {
  "HPCL":      [{ key:"power_petrol", label:"Power Petrol", color:"#16A34A", icon:"🟩", max_key:"max_power_petrol", default:5000 },
                { key:"power_diesel",  label:"Power Diesel",  color:"#2563EB", icon:"🟦", max_key:"max_power_diesel",  default:8000 }],
  "Indian Oil":[{ key:"xp100",        label:"XP100",         color:"#DC2626", icon:"🟥", max_key:"max_xp100",         default:2000 },
                { key:"xtra_premium", label:"Xtra Premium",  color:"#EA580C", icon:"🟧", max_key:"max_xtra_premium",  default:3000 },
                { key:"xtra_mile",    label:"Xtra Mile",     color:"#0891B2", icon:"🟦", max_key:"max_xtra_mile",     default:5000 }],
  "BPCL":      [{ key:"speed_petrol", label:"Speed Petrol",  color:"#7C3AED", icon:"🟪", max_key:"max_speed_petrol",  default:5000 },
                { key:"speed_diesel", label:"Speed Diesel",  color:"#4F46E5", icon:"🟦", max_key:"max_speed_diesel",  default:8000 }],
  "Shell":     [{ key:"vpower",       label:"V-Power",       color:"#B91C1C", icon:"🟥", max_key:"max_vpower",        default:4000 }],
  "Reliance":  [{ key:"rel_premium",  label:"Rel Premium",   color:"#0F766E", icon:"🟩", max_key:"max_rel_premium",   default:4000 }],
  "Essar":     [{ key:"nayara_power", label:"Nayara Power",  color:"#0369A1", icon:"🟦", max_key:"max_nayara_power",  default:4000 }],
};

// Get premium fuels for a given pump brand
function getPremiumFuels(brand) { return PREMIUM_FUELS[brand] || []; }

// ─────────────────────────────────────────────────────────────────────────────
// WEIGHTS & MEASURES HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function wmStatus(pump) {
  // Returns: "verified" | "expired" | "none"
  if (!pump.wm_cert_number || !pump.wm_audit_date) return "none";
  if (!pump.wm_valid_until) return "verified"; // no expiry set — treat as valid
  const expiry = new Date(pump.wm_valid_until);
  const today  = new Date(); today.setHours(0,0,0,0);
  return expiry >= today ? "verified" : "expired";
}
function wmYear(pump) {
  if (!pump.wm_audit_date) return "";
  return new Date(pump.wm_audit_date).getFullYear();
}
function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
}

// ─────────────────────────────────────────────────────────────────────────────
// OPERATING HOURS HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

// Default hours if pump hasn't set them — standard Indian pump hours
const DEFAULT_HOURS = { open:"06:00", close:"22:00", is24h:false, isClosed:false };

// Get today's schedule for a pump
function getTodaySchedule(pump) {
  const day = DAYS[new Date().getDay()];
  const key = `hours_${day}`;
  if (!pump[key]) return DEFAULT_HOURS;
  try {
    return JSON.parse(pump[key]);
  } catch {
    return DEFAULT_HOURS;
  }
}

// Returns: { isOpen, opensAt, closesAt, minutesUntilClose, minutesUntilOpen, is24h, label, color, bg }
function getOpenStatus(pump) {
  const sched = getTodaySchedule(pump);

  if (sched.isClosed) return {
    isOpen:false, is24h:false, label:"Closed Today",
    color:"#DC2626", bg:"#FEE2E2", minutesUntilClose:null, minutesUntilOpen:null
  };
  if (sched.is24h) return {
    isOpen:true, is24h:true, label:"Open 24 hrs",
    color:"#16A34A", bg:"#DCFCE7", minutesUntilClose:null, minutesUntilOpen:null
  };

  const now  = new Date();
  const nowM = now.getHours()*60 + now.getMinutes();

  const [oh,om] = (sched.open  || "06:00").split(":").map(Number);
  const [ch,cm] = (sched.close || "22:00").split(":").map(Number);
  const openM  = oh*60+om;
  const closeM = ch*60+cm;

  const opensAt  = sched.open  || "06:00";
  const closesAt = sched.close || "22:00";

  if (nowM >= openM && nowM < closeM) {
    const minsLeft = closeM - nowM;
    if (minsLeft <= 30) return {
      isOpen:true, label:`Closes in ${minsLeft} min`, opensAt, closesAt,
      color:"#D97706", bg:"#FEF3C7", minutesUntilClose:minsLeft, minutesUntilOpen:null
    };
    return {
      isOpen:true, label:`Open · Closes ${fmtTime(closesAt)}`, opensAt, closesAt,
      color:"#16A34A", bg:"#DCFCE7", minutesUntilClose:minsLeft, minutesUntilOpen:null
    };
  }

  // Closed — calculate when it opens
  let minsUntilOpen;
  if (nowM < openM) {
    minsUntilOpen = openM - nowM;
  } else {
    // Closed for the night — opens tomorrow
    minsUntilOpen = (24*60 - nowM) + openM;
  }
  const hrsUntil = Math.floor(minsUntilOpen/60);
  const openLabel = hrsUntil > 0 ? `Opens in ${hrsUntil}h ${minsUntilOpen%60}m` : `Opens in ${minsUntilOpen} min`;

  return {
    isOpen:false, label:openLabel, opensAt, closesAt,
    color:"#DC2626", bg:"#FEE2E2", minutesUntilClose:null, minutesUntilOpen:minsUntilOpen
  };
}

function fmtTime(t) {
  if (!t) return "";
  const [h,m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12  = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
}

// Get full week schedule display
function getWeekSchedule(pump) {
  return DAYS.map(day => {
    const key = `hours_${day}`;
    let sched = DEFAULT_HOURS;
    if (pump[key]) { try { sched = JSON.parse(pump[key]); } catch {} }
    return { day, sched };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const lvl    = (c,m) => m?Math.round((c/m)*100):0;
const sColor = p     => p>50?"#3DB54A":p>20?"#F97316":"#EF4444";
const sBg    = p     => p>50?"#DCFCE7":p>20?"#FEF3C7":"#FEE2E2";
const sLabel = p     => p>50?"Available":p>20?"Low Stock":p>0?"Critical":"Out";
const fmtL   = n     => n>=1000?(n/1000).toFixed(1)+"k L":n+" L";

function pumpBrandLogo(brand) {
  const logos = {
    "Indian Oil": "/brands/ioc.png",
    "HPCL": "/brands/hpcl.png",
    "HP": "/brands/hpcl.png",
    "BPCL": "/brands/bpcl.png",
    "Shell": "/brands/shell.png",
    "Reliance": "/brands/reliance.png",
    "Nayara": "/brands/nayara.png",
  };
  return logos[brand] || "/brands/ioc.png";
}

function haversine(a,b,c,d){ const R=6371,dA=((c-a)*Math.PI)/180,dB=((d-b)*Math.PI)/180; const x=Math.sin(dA/2)**2+Math.cos((a*Math.PI)/180)*Math.cos((c*Math.PI)/180)*Math.sin(dB/2)**2; return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x)); }

function decodePolyline(encoded) {
  if (!encoded || typeof encoded !== "string") return [];
  const path = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : result >> 1;
    lng += dlng;
    path.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
  }
  return path;
}

function distancePointToSegmentKm(pLat, pLng, a, b) {
  const dAP = haversine(a.lat, a.lng, pLat, pLng);
  const dBP = haversine(b.lat, b.lng, pLat, pLng);
  const dAB = haversine(a.lat, a.lng, b.lat, b.lng);
  if (dAB < 1e-9) return dAP;
  const cosφ = Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  const ax = (a.lng * Math.PI) / 180 * cosφ;
  const ay = (a.lat * Math.PI) / 180;
  const bx = (b.lng * Math.PI) / 180 * cosφ;
  const by = (b.lat * Math.PI) / 180;
  const px = (pLng * Math.PI) / 180 * cosφ;
  const py = (pLat * Math.PI) / 180;
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  const t = Math.max(0, Math.min(1, ab2 < 1e-18 ? 0 : (apx * abx + apy * aby) / ab2));
  const qx = ax + t * abx;
  const qy = ay + t * aby;
  const qLat = (qy * 180) / Math.PI;
  const qLng = (qx / cosφ * 180) / Math.PI;
  return haversine(pLat, pLng, qLat, qLng);
}

function minDistancePointToPolylineKm(pLat, pLng, polyline) {
  if (!polyline || polyline.length < 2) return Infinity;
  let minD = Infinity;
  for (let i = 0; i < polyline.length - 1; i++) {
    const d = distancePointToSegmentKm(pLat, pLng, polyline[i], polyline[i + 1]);
    if (d < minD) minD = d;
  }
  return minD;
}

function dayLabel(s){ const d=new Date(s+"T00:00:00"),t=new Date();t.setHours(0,0,0,0); const diff=Math.round((t-d)/86400000); if(diff===0)return"Today";if(diff===1)return"Yesterday"; return d.toLocaleDateString("en-IN",{weekday:"short",day:"numeric"}); }
function dominantFuel(p){ const a=lvl(p.petrol,p.max_petrol),b=lvl(p.diesel,p.max_diesel),c=p.max_cng?lvl(p.cng,p.max_cng):101; if(a<=b&&a<=c)return"petrol";if(b<=a&&b<=c)return"diesel";return"cng"; }

function openDirections(e, pump) {
  e.stopPropagation();
  logClick(pump.id, dominantFuel(pump));
  const isIOS=/iPhone|iPad|iPod/i.test(navigator.userAgent), isAndroid=/Android/i.test(navigator.userAgent);
  const dest=`${pump.lat},${pump.lng}`, name=encodeURIComponent(pump.name);
  if(isIOS) window.open(`maps://?daddr=${dest}&q=${name}`,"_blank");
  else if(isAndroid) window.open(`google.navigation:q=${dest}`,"_blank");
  else window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}&destination_place_name=${name}`,"_blank");
}

// ─────────────────────────────────────────────────────────────────────────────
// LIGHT FUEL BAR (public page)
// ─────────────────────────────────────────────────────────────────────────────
function LightFuelBar({ label, current, max, delay=0 }) {
  const pct = max?Math.min(100,Math.round((current/max)*100)):0;
  const color = sColor(pct);
  const icons = { Petrol:"🟢", Diesel:"🔵", CNG:"🟣" };
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5 }}>
        <span style={{ fontSize:12,fontWeight:600,color:"#78716C",display:"flex",alignItems:"center",gap:5 }}>
          <span style={{ fontSize:10 }}>{icons[label]||"⚪"}</span>{label}
        </span>
        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
          <span style={{ fontSize:12,fontWeight:700,color:"var(--text)" }}>{max?fmtL(current):"—"}</span>
          {max>0&&<span style={{ fontSize:10,fontWeight:700,color,background:sBg(pct),borderRadius:5,padding:"1px 7px" }}>{pct}%</span>}
        </div>
      </div>
      {max>0&&(
        <div className="fuel-bar-track">
          <div className="fuel-bar-fill" style={{ width:`${pct}%`, background:`linear-gradient(90deg,${color}99,${color})`, "--delay":`${delay}s` }}/>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PREMIUM FUEL BAR
// ─────────────────────────────────────────────────────────────────────────────
function PremiumFuelBar({ fuel, current, max, delay=0 }) {
  const pct = max ? Math.min(100, Math.round((current/max)*100)) : 0;
  const available = pct > 0;
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5 }}>
        <span style={{ fontSize:12,fontWeight:700,color:available?fuel.color:"#C4B9AF",display:"flex",alignItems:"center",gap:5 }}>
          <span style={{ fontSize:10 }}>{fuel.icon}</span>
          {fuel.label}
          <span style={{ fontSize:9,fontWeight:800,color:"#fff",background:fuel.color,borderRadius:4,padding:"1px 5px",letterSpacing:"0.04em" }}>PREMIUM</span>
        </span>
        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
          {available
            ? <><span style={{ fontSize:12,fontWeight:700,color:"#292524" }}>{fmtL(current)}</span>
                <span style={{ fontSize:10,fontWeight:700,color:fuel.color,background:fuel.color+"18",borderRadius:5,padding:"1px 7px" }}>{pct}%</span></>
            : <span style={{ fontSize:11,color:"var(--text3)",fontStyle:"italic" }}>Not available</span>
          }
        </div>
      </div>
      <div className="fuel-bar-track" style={{ background:`${fuel.color}18` }}>
        {available&&<div className="fuel-bar-fill" style={{ width:`${pct}%`,background:`linear-gradient(90deg,${fuel.color}88,${fuel.color})`,"--delay":`${delay}s` }}/>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FUEL ROW (new card design)
// ─────────────────────────────────────────────────────────────────────────────
function FuelRowNew({ label, current, max, dotColor, barGrad, isPremium=false, delay=0 }) {
  const pct = max ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const pctBg  = pct > 50 ? "#E8F5E9" : pct > 20 ? "#FEF3C7" : "#FEE2E2";
  const pctCol = pct > 50 ? "#16A34A" : pct > 20 ? "#D97706" : "#DC2626";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background:dotColor, flexShrink:0 }}/>
        <span style={{ fontSize:14, fontWeight:500, color:"#1A1A1A", marginLeft:8 }}>{label}</span>
        {isPremium && (
          <span style={{ background:"#7B2D00", color:"#FFFFFF", fontSize:10, fontWeight:700, borderRadius:999, padding:"3px 8px", marginLeft:8 }}>PREMIUM</span>
        )}
        <span style={{ fontSize:14, fontWeight:600, color:"#1A1A1A", marginLeft:"auto" }}>{max ? fmtL(current) : "—"}</span>
        <span style={{ background:pctBg, color:pctCol, borderRadius:999, padding:"2px 8px", fontSize:12, fontWeight:600, marginLeft:8 }}>{pct}%</span>
      </div>
      <div className="fuel-bar-track">
        <div className="fuel-bar-fill" style={{ width:`${pct}%`, background:barGrad, "--delay":`${delay}s`, animationFillMode:"both" }}/>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC PUMP CARD
// ─────────────────────────────────────────────────────────────────────────────
function PumpCard({ pump, distance, onClick, animDelay=0, onProductClick }) {
  const status = getOpenStatus(pump);
  const distanceLabel =
    distance !== null && distance !== undefined
      ? distance < 1 ? `${(distance * 1000).toFixed(0)} m` : `${distance.toFixed(1)} km`
      : null;
  const premiumFuels = getPremiumFuels(pump.brand).filter(pf => pump[pf.max_key] > 0);
  const overallPct = Math.round((lvl(pump.petrol, pump.max_petrol) + lvl(pump.diesel, pump.max_diesel)) / 2);
  const stockLabel = sLabel(overallPct);
  const stockColor = sColor(overallPct);
  const stockBg    = sBg(overallPct);

  return (
    <div
      className={`pub-card${animDelay===0 ? " nudge" : ""}${!status.isOpen ? " closed-pump" : ""}`}
      style={{ animationDelay:`${animDelay}s` }}
      onClick={() => onClick(pump)}
    >
      {/* ── IMAGE ── */}
      <div style={{ position:"relative", height:140, width:"100%", overflow:"hidden", flexShrink:0 }}>
        {pump.photo_url ? (
          <img
            src={pump.photo_url}
            alt={pump.name}
            style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
            onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
          />
        ) : null}
        {/* Brand logo placeholder shown when no photo */}
        <div style={{
          display: pump.photo_url ? "none" : "flex",
          position: "relative",
          width: "100%",
          height: 140,
          background: "#F5EDE8",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}>
          <img
            src={(() => {
              const b = (pump.brand || "").toLowerCase().trim();
              if (b.includes("indian oil") || b.includes("ioc")) return "/brands/ioc.png";
              if (b.includes("hpcl") || b === "hp") return "/brands/hpcl.png";
              if (b.includes("bpcl") || b.includes("bharat")) return "/brands/bpcl.png";
              if (b.includes("shell")) return "/brands/shell.png";
              if (b.includes("reliance")) return "/brands/reliance.png";
              if (b.includes("essar") || b.includes("nayara")) return "/brands/nayara.png";
              return "/brands/ioc.png";
            })()}
            alt={pump.brand}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <div style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.35) 100%)",
            pointerEvents: "none"
          }}/>
        </div>
        {/* Overlay gradient on top of photo */}
        {pump.photo_url && (
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg,transparent 50%,rgba(0,0,0,0.45) 100%)", pointerEvents:"none" }}/>
        )}

        {/* Verified shield — top right */}
        {pump.wm_cert_number && (
          <div style={{
            position:"absolute", top:10, right:10,
            width:32, height:32, borderRadius:"50%",
            background:"linear-gradient(135deg, #166534, #16A34A)",
            border:"2px solid #4ADE80",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:"0 2px 8px rgba(22,101,52,0.4)",
          }}>
            <img
              src="/design/badges/wm-shield.svg"
              alt="W&M Verified"
              style={{ width:16, height:16, filter:"brightness(0) invert(1)" }}
            />
          </div>
        )}

        {/* Distance badge — bottom right */}
        {distanceLabel && (
          <div style={{ position:"absolute", bottom:10, right:10, background:"rgba(0,0,0,0.55)", color:"#FFFFFF", fontSize:12, fontWeight:600, borderRadius:999, padding:"4px 10px", display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:10, lineHeight:1 }}>▲</span>
            {distanceLabel}
          </div>
        )}
      </div>

      {/* ── INFO ── */}
      <div style={{ padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div style={{ flex:1, paddingRight:12 }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#1A1A1A", lineHeight:1.3 }}>{pump.name}</div>
            <div style={{ fontSize:13, fontWeight:400, color:"#6B7280", marginTop:4 }}>{pump.address}</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
            <span style={{ background:stockBg, color:stockColor, borderRadius:999, padding:"3px 10px", fontSize:12, fontWeight:600 }}>{stockLabel}</span>
            <span style={{ fontSize:12, fontWeight:400, color:"#6B7280", display:"flex", alignItems:"center", gap:3, whiteSpace:"nowrap" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {status.label}
            </span>
            {wmStatus(pump) !== "none" && (
              <button
                onClick={e => { e.stopPropagation(); onClick(pump, "wm"); }}
                style={{ background:"transparent", border:"1px solid #D97706", color:"#D97706", borderRadius:999, padding:"3px 10px", fontSize:12, fontWeight:500, display:"flex", alignItems:"center", gap:3, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                <img src="/design/badges/wm-shield.svg" alt="W&M"
                  style={{ width:12, height:12, filter:"brightness(0) saturate(100%) invert(55%) sepia(98%) saturate(500%) hue-rotate(5deg) brightness(95%)" }}/>
                W&amp;M {wmYear(pump)}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── FUEL STOCK ── */}
      <div style={{ background:"#FDF3EE", borderRadius:10, padding:"12px 14px", margin:"0 14px 10px" }}>
        <FuelRowNew label="Petrol" current={pump.petrol} max={pump.max_petrol}
          dotColor="#F97316" barGrad="linear-gradient(90deg,#E8400A 0%,#F97316 50%,#FB923C 100%)" delay={0.1}/>
        <FuelRowNew label="Diesel" current={pump.diesel} max={pump.max_diesel}
          dotColor="#2563EB" barGrad="linear-gradient(90deg,#7B2D00 0%,#C84B00 40%,#3B82F6 100%)" delay={0.2}/>
        {pump.max_cng > 0 && (
          <FuelRowNew label="CNG" current={pump.cng} max={pump.max_cng}
            dotColor="#22C55E" barGrad="linear-gradient(90deg,#16A34A 0%,#22C55E 100%)" delay={0.3}/>
        )}
        {premiumFuels.map((pf, i) => (
          <FuelRowNew key={pf.key}
            label={pf.label} current={pump[pf.key] || 0} max={pump[pf.max_key] || 0}
            dotColor={pf.color} barGrad={`linear-gradient(90deg,${pf.color}88 0%,${pf.color} 100%)`}
            isPremium delay={0.3 + (i + 1) * 0.1}/>
        ))}
      </div>

      {/* ── FOOTER ── */}
      <div style={{ padding:"10px 16px 16px", display:"flex", alignItems:"center", gap:10 }}>
        {pump.brand && (
          <span style={{ background:"#FDE8D8", color:"#7B2D00", border:"none", borderRadius:999, padding:"6px 14px", fontSize:13, fontWeight:600, flexShrink:0 }}>
            {pump.brand}
          </span>
        )}
        {pump.product_count > 0 && (
          <button
            onClick={e => { e.stopPropagation(); onProductClick && onProductClick(pump); }}
            style={{ display:"flex", alignItems:"center", gap:6, background:"#EFF6FF", border:"1px solid #DBEAFE", borderRadius:999, cursor:"pointer", color:"#2563EB", fontSize:12, fontWeight:600, padding:"5px 12px", fontFamily:"inherit", flexShrink:0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
            {pump.product_count} {pump.product_count === 1 ? "product" : "products"}
          </button>
        )}
        <button
          style={{ marginLeft:"auto", background:"linear-gradient(90deg, #7B2D00 0%, #C84B00 40%, #F97316 100%)", color:"#FFFFFF", fontSize:14, fontWeight:600, borderRadius:999, height:40, padding:"0 20px", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, flexShrink:0, fontFamily:"inherit" }}
          onClick={e => openDirections(e, pump)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
          Navigate
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SKELETON CARD
// ─────────────────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{ background:"#fff",border:"1.5px solid #F3EDE4",borderRadius:20,padding:"22px 22px 18px" }}>
      <div style={{ display:"flex",justifyContent:"space-between",marginBottom:14 }}>
        <div style={{ flex:1,paddingRight:10 }}>
          <div className="skeleton" style={{ height:18,width:"70%",marginBottom:8 }}/>
          <div className="skeleton" style={{ height:12,width:"50%" }}/>
        </div>
        <div className="skeleton" style={{ height:26,width:70,borderRadius:8 }}/>
      </div>
      <div style={{ background:"#FFFBF5",borderRadius:12,padding:"12px 14px",marginBottom:12 }}>
        {[0,1,2].map(i=>(
          <div key={i} style={{ marginBottom:10 }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
              <div className="skeleton" style={{ height:12,width:50 }}/>
              <div className="skeleton" style={{ height:12,width:60 }}/>
            </div>
            <div className="skeleton" style={{ height:8,width:"100%",borderRadius:99 }}/>
          </div>
        ))}
      </div>
      <div style={{ display:"flex",justifyContent:"space-between" }}>
        <div className="skeleton" style={{ height:12,width:80 }}/>
        <div className="skeleton" style={{ height:32,width:100,borderRadius:12 }}/>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL MODAL (light)
// ─────────────────────────────────────────────────────────────────────────────
function PumpModal({ pump, onClose, initialTab="fuel" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [products, setProducts] = useState(null);
  const [prodLoading, setProdLoading] = useState(false);
  const status = getOpenStatus(pump);
  const wm = wmStatus(pump);

  useEffect(() => {
    if (activeTab === "products" && products === null) {
      setProdLoading(true);
      getProductsByPump(pump.id)
        .then(r => { setProducts(r || []); setProdLoading(false); })
        .catch(() => { setProducts([]); setProdLoading(false); });
    }
  }, [activeTab]);

  const pp = lvl(pump.petrol, pump.max_petrol);
  const dp = lvl(pump.diesel, pump.max_diesel);
  const premiumFuels = getPremiumFuels(pump.brand)
    .filter(pf => pump[pf.max_key] > 0);

  const tabs = [
    { id:"fuel",     label:"Fuel Stock" },
    { id:"hours",    label:"Hours"      },
    { id:"products", label:"Products"   },
    { id:"wm",       label:"Verified"   },
  ];

  return (
    <div style={{
      position:"fixed", inset:0,
      background:"#FDF3EE",
      zIndex:200,
      display:"flex", flexDirection:"column",
      fontFamily:"'Plus Jakarta Sans', sans-serif",
      overflowY:"auto",
      borderRadius:0,
    }}>

      {/* ── TOP BAR ── */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"12px 16px",
        paddingTop:"calc(12px + env(safe-area-inset-top))",
        background:"#FDF3EE",
        flexShrink:0,
      }}>
        <button onClick={onClose} style={{
          width:36, height:36, borderRadius:"50%",
          background:"#FFFFFF", border:"1px solid #F5EDE8",
          cursor:"pointer", display:"flex", alignItems:"center",
          justifyContent:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
          flexShrink:0,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round"
            strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>

        <div style={{
          flex:1, textAlign:"center", fontSize:15, fontWeight:700,
          color:"#1A1A1A", padding:"0 12px",
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
        }}>
          {pump.name.split("–")[1]?.trim() || pump.name}
        </div>

        <button style={{
          width:36, height:36, borderRadius:"50%",
          background:"#FFFFFF", border:"1px solid #F5EDE8",
          cursor:"pointer", display:"flex", alignItems:"center",
          justifyContent:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
          flexShrink:0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round"
            strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </button>
      </div>

      {/* ── HERO IMAGE — ROUNDED CARD ── */}
      <div style={{
        margin:"0 16px 0", borderRadius:20, height:200,
        overflow:"hidden", flexShrink:0, position:"relative",
      }}>
        {pump.photo_url ? (
          <img src={pump.photo_url} alt={pump.name}
            style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
        ) : (
          <div style={{
            width:"100%", height:"100%",
            background:"linear-gradient(135deg,#1A0800,#2D1200)",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <img
              src={(() => {
                const b = (pump.brand||"").toLowerCase().trim();
                if(b.includes("indian oil")||b.includes("ioc")) return "/brands/ioc.png";
                if(b.includes("hpcl")||b==="hp") return "/brands/hpcl.png";
                if(b.includes("bpcl")||b.includes("bharat")) return "/brands/bpcl.png";
                if(b.includes("shell")) return "/brands/shell.png";
                if(b.includes("reliance")) return "/brands/reliance.png";
                if(b.includes("essar")||b.includes("nayara")) return "/brands/nayara.png";
                return "/brands/ioc.png";
              })()}
              alt={pump.brand}
              style={{ width:"100%", height:"100%", objectFit:"contain", padding:"24px 32px" }}
            />
          </div>
        )}

        {/* Dark gradient overlay */}
        <div style={{
          position:"absolute", inset:0,
          background:"linear-gradient(180deg,transparent 40%,rgba(0,0,0,0.65) 100%)",
          pointerEvents:"none", borderRadius:20,
        }}/>

        {/* GOVT VERIFIED badge */}
        {wmStatus(pump) === "verified" && (
          <div style={{
            position:"absolute", top:14, left:14,
            background:"#16A34A", borderRadius:999, padding:"5px 10px 5px 8px",
            display:"flex", alignItems:"center", gap:6,
          }}>
            <img src="/design/badges/govt-verified.svg" alt="verified"
              style={{ width:14, height:14, filter:"brightness(0) invert(1)" }}/>
            <span style={{ fontSize:11, fontWeight:700, color:"white", letterSpacing:"0.05em" }}>
              GOVT VERIFIED
            </span>
          </div>
        )}

        {/* Pump name bottom of image */}
        <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"14px 16px" }}>
          <div style={{
            fontSize:18, fontWeight:800, color:"white",
            lineHeight:1.3, textShadow:"0 1px 4px rgba(0,0,0,0.4)"
          }}>{pump.name}</div>
        </div>
      </div>

      {/* ── TAB BAR ── */}
      <div style={{
        display:"flex",
        borderBottom:"1px solid #E5D5CC",
        background:"#FDF3EE",
        flexShrink:0,
        position:"sticky", top:0, zIndex:10,
      }}>
        {tabs.map(tab => (
          <button key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex:1, padding:"14px 4px",
              fontSize:13, fontWeight:activeTab===tab.id?700:500,
              color:activeTab===tab.id?"#C84B00":"#9CA3AF",
              background:"transparent", border:"none",
              borderBottom:activeTab===tab.id
                ?"2.5px solid #C84B00":"2.5px solid transparent",
              cursor:"pointer",
              fontFamily:"inherit",
              transition:"all 0.15s",
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── CONTENT AREA ── */}
      <div style={{ flex:1, padding:"20px 16px 100px", overflowY:"auto" }}>

        {/* ════ FUEL STOCK TAB ════ */}
        {activeTab === "fuel" && (
          <div>
            {/* Header */}
            <div style={{ marginBottom:20 }}>
              <div style={{
                fontSize:28, fontWeight:800, color:"#1A1A1A",
                fontFamily:"'Outfit', sans-serif", marginBottom:4
              }}>Fuel Stock</div>
              <div style={{
                display:"flex", alignItems:"center",
                justifyContent:"space-between"
              }}>
                <span style={{ fontSize:14, color:"#6B7280" }}>
                  {pump.name}
                </span>
                <span style={{
                  display:"flex", alignItems:"center", gap:6,
                  background:"#DCFCE7", borderRadius:999,
                  padding:"4px 12px", fontSize:12, fontWeight:700,
                  color:"#16A34A"
                }}>
                  <span style={{
                    width:7, height:7, borderRadius:"50%",
                    background:"#16A34A", display:"inline-block"
                  }}/>
                  LIVE NOW
                </span>
              </div>
            </div>

            {/* KPI cards — Petrol + Diesel */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>

              {/* PETROL CARD */}
              <div style={{ background:"#FFF8F5", borderRadius:16, padding:"16px 14px", position:"relative", overflow:"hidden", border:"1px solid #FFE8DC" }}>
                <div style={{ position:"absolute", bottom:-6, right:-6, opacity:0.1, pointerEvents:"none" }}>
                  <img src="/design/icons/gas-station.svg" alt=""
                    style={{ width:90, height:90, filter:"brightness(0) saturate(100%) invert(25%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(90%)" }}/>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                  <div style={{ transform:"scale(1.1)", transformOrigin:"center", display:"flex", alignItems:"center" }}>
                    <img src="/design/icons/fuel-drop.svg" alt="fuel"
                      style={{ width:22, height:22, filter:"brightness(0) saturate(100%) invert(25%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(80%)" }}/>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:"#E8580A", letterSpacing:"0.08em" }}>PETROL</span>
                </div>
                <div style={{ fontSize:40, fontWeight:900, color:"#1A1A1A", lineHeight:1, fontFamily:"'Outfit', sans-serif", marginBottom:6 }}>{pp}%</div>
                <div style={{ fontSize:13, color:"#9CA3AF", fontWeight:400, lineHeight:1.4 }}>{fmtL(pump.petrol)} L<br/>remaining</div>
              </div>

              {/* DIESEL CARD */}
              <div style={{ background:"#F0F7FF", borderRadius:16, padding:"16px 14px", position:"relative", overflow:"hidden", border:"1px solid #DBEAFE" }}>
                <div style={{ position:"absolute", bottom:-6, right:-6, opacity:0.1, pointerEvents:"none" }}>
                  <img src="/design/icons/diesel-barrel.svg" alt=""
                    style={{ width:80, height:80, filter:"brightness(0) saturate(100%) invert(29%) sepia(98%) saturate(1200%) hue-rotate(210deg) brightness(95%)" }}/>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                  <div style={{ transform:"scale(1.1)", transformOrigin:"center", display:"flex", alignItems:"center" }}>
                    <img src="/design/icons/fuel-drop.svg" alt="fuel"
                      style={{ width:22, height:22, filter:"brightness(0) saturate(100%) invert(20%) sepia(98%) saturate(1400%) hue-rotate(210deg) brightness(85%)" }}/>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:"#1D4ED8", letterSpacing:"0.08em" }}>DIESEL</span>
                </div>
                <div style={{ fontSize:40, fontWeight:900, color:"#2563EB", lineHeight:1, fontFamily:"'Outfit', sans-serif", marginBottom:6 }}>{dp}%</div>
                <div style={{ fontSize:13, color:"#9CA3AF", fontWeight:400, lineHeight:1.4 }}>{fmtL(pump.diesel)} L<br/>remaining</div>
              </div>

            </div>

            {/* Inventory Details */}
            <div style={{
              background:"#FFFFFF", borderRadius:16,
              padding:"16px", marginBottom:16,
              boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
            }}>
              <div style={{
                display:"flex", justifyContent:"space-between",
                alignItems:"center", marginBottom:16
              }}>
                <span style={{
                  fontSize:15, fontWeight:700, color:"#1A1A1A"
                }}>Inventory Details</span>
                <span style={{
                  fontSize:11, color:"#9CA3AF",
                  display:"flex", alignItems:"center", gap:4
                }}>
                  <IC.Clock size={11} color="#9CA3AF"/>
                  LAST UPDATED {(() => {
                    const ts = pump.last_updated;
                    if (!ts) return "—";
                    try {
                      const d = new Date(ts);
                      if (isNaN(d)) return ts;
                      return d.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", hour12:true });
                    } catch { return ts; }
                  })()}
                </span>
              </div>

              {/* Petrol row */}
              <div style={{ paddingBottom:12, marginBottom:12, borderBottom:"1px solid #F5EDE8" }}>
                <div style={{
                  display:"flex", justifyContent:"space-between",
                  marginBottom:2
                }}>
                  <div>
                    <div style={{
                      fontSize:14, fontWeight:700, color:"#1A1A1A"
                    }}>Petrol</div>
                    <div style={{
                      fontSize:12, color:"#9CA3AF"
                    }}>Standard Grade</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{
                      fontSize:14, fontWeight:600, color:"#1A1A1A"
                    }}>{fmtL(pump.petrol)}</div>
                    <div style={{
                      fontSize:13, fontWeight:700, color:"#F97316"
                    }}>{pp}%</div>
                  </div>
                </div>
                <div className="fuel-bar-track" style={{ height:10, background:"#F5EDE8", borderRadius:999, marginTop:10 }}>
                  <div className="fuel-bar-fill" style={{
                    height:"100%", borderRadius:999,
                    width:`${pp}%`,
                    background:"linear-gradient(90deg,#E8400A,#F97316,#FB923C)"
                  }}/>
                </div>
              </div>

              {/* Diesel row */}
              <div style={{ paddingBottom:12, marginBottom:12, borderBottom:"1px solid #F5EDE8" }}>
                <div style={{
                  display:"flex", justifyContent:"space-between",
                  marginBottom:2
                }}>
                  <div>
                    <div style={{
                      fontSize:14, fontWeight:700, color:"#1A1A1A"
                    }}>Diesel (HSD)</div>
                    <div style={{
                      fontSize:12, color:"#9CA3AF"
                    }}>High Speed</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{
                      fontSize:14, fontWeight:600, color:"#1A1A1A"
                    }}>{fmtL(pump.diesel)}</div>
                    <div style={{
                      fontSize:13, fontWeight:700, color:"#2563EB"
                    }}>{dp}%</div>
                  </div>
                </div>
                <div className="fuel-bar-track" style={{ height:10, background:"#F5EDE8", borderRadius:999, marginTop:10 }}>
                  <div className="fuel-bar-fill" style={{
                    height:"100%", borderRadius:999,
                    width:`${dp}%`,
                    background:"linear-gradient(90deg,#7B2D00,#C84B00,#3B82F6)"
                  }}/>
                </div>
              </div>

              {/* CNG row if available */}
              {pump.max_cng > 0 && (
                <div style={{ paddingBottom:12, marginBottom:12, borderBottom:"1px solid #F5EDE8" }}>
                  <div style={{
                    display:"flex", justifyContent:"space-between",
                    marginBottom:2
                  }}>
                    <div>
                      <div style={{
                        fontSize:14, fontWeight:700, color:"#1A1A1A"
                      }}>CNG</div>
                      <div style={{
                        fontSize:12, color:"#9CA3AF"
                      }}>Compressed Natural Gas</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{
                        fontSize:14, fontWeight:600, color:"#1A1A1A"
                      }}>{fmtL(pump.cng)}</div>
                      <div style={{
                        fontSize:13, fontWeight:700, color:"#22C55E"
                      }}>{lvl(pump.cng, pump.max_cng)}%</div>
                    </div>
                  </div>
                  <div className="fuel-bar-track" style={{ height:10, background:"#F5EDE8", borderRadius:999, marginTop:10 }}>
                    <div className="fuel-bar-fill" style={{
                      height:"100%", borderRadius:999,
                      width:`${lvl(pump.cng,pump.max_cng)}%`,
                      background:"linear-gradient(90deg,#16A34A,#22C55E)"
                    }}/>
                  </div>
                </div>
              )}

              {/* Premium fuels */}
              {premiumFuels.map(pf => {
                const pct = lvl(pump[pf.key]||0, pump[pf.max_key]||1);
                return (
                  <div key={pf.key} style={{ paddingBottom:12, marginBottom:12, borderBottom:"1px solid #F5EDE8" }}>
                    <div style={{
                      display:"flex", justifyContent:"space-between",
                      marginBottom:2
                    }}>
                      <div>
                        <div style={{
                          fontSize:14, fontWeight:700, color:"#1A1A1A",
                          display:"flex", alignItems:"center", gap:6
                        }}>
                          {pf.label}
                          <span style={{
                            fontSize:9, fontWeight:800,
                            color:"#fff",
                            background:pf.color,
                            borderRadius:4, padding:"2px 6px"
                          }}>PREMIUM</span>
                        </div>
                        <div style={{
                          fontSize:12, color:"#9CA3AF"
                        }}>{pct}% Premium</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{
                          fontSize:14, fontWeight:600, color:"#1A1A1A"
                        }}>{fmtL(pump[pf.key]||0)}</div>
                        <div style={{
                          fontSize:13, fontWeight:700, color:pf.color
                        }}>{pct}%</div>
                      </div>
                    </div>
                    <div className="fuel-bar-track" style={{ height:10, background:"#F5EDE8", borderRadius:999, marginTop:10 }}>
                      <div className="fuel-bar-fill" style={{
                        height:"100%", borderRadius:999,
                        width:`${pct}%`,
                        background:`linear-gradient(90deg,${pf.color}88,${pf.color})`
                      }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Info note */}
            <div style={{
              background:"#FFF7F0",
              border:"1px solid #FED7AA",
              borderRadius:12, padding:"14px 16px",
              display:"flex", gap:10, alignItems:"flex-start"
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="#F97316" strokeWidth="2"
                strokeLinecap="round" style={{ flexShrink:0, marginTop:1 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ fontSize:13, color:"#6B7280", lineHeight:1.6 }}>
                Stock levels are synchronized in real-time from the
                fuel station terminal. Reserve may vary during peak hours.
              </span>
            </div>
          </div>
        )}

        {/* ════ HOURS TAB ════ */}
        {activeTab === "hours" && (() => {
          const schedule = getWeekSchedule(pump);
          const todayIdx = new Date().getDay();
          return (
            <div>
              {/* Status banner */}
              <div style={{
                background:"#FFFFFF", borderRadius:16,
                padding:"16px", marginBottom:20,
                display:"flex", alignItems:"center",
                gap:14, boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
              }}>
                <div style={{
                  width:48, height:48,
                  background:status.isOpen?"#DCFCE7":"#FEE2E2",
                  borderRadius:"50%",
                  display:"flex", alignItems:"center",
                  justifyContent:"center", flexShrink:0
                }}>
                  <IC.Clock size={22}
                    color={status.isOpen?"#16A34A":"#DC2626"}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{
                    fontSize:20, fontWeight:800,
                    color:status.isOpen?"#16A34A":"#DC2626",
                    fontFamily:"'Outfit', sans-serif"
                  }}>{status.label}</div>
                  <div style={{
                    fontSize:13, color:"#6B7280", marginTop:2
                  }}>Standard operating hours</div>
                </div>
                {status.isOpen && (
                  <div style={{
                    background:"#16A34A", borderRadius:999,
                    padding:"6px 14px",
                    fontSize:12, fontWeight:700, color:"white"
                  }}>LIVE</div>
                )}
              </div>

              {/* Weekly schedule */}
              <div style={{
                background:"#FFFFFF", borderRadius:16,
                overflow:"hidden", marginBottom:16,
                boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
              }}>
                <div style={{
                  padding:"14px 16px",
                  display:"flex", alignItems:"center", gap:8,
                  borderBottom:"1px solid #F5EDE8"
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24"
                    fill="none" stroke="#C84B00" strokeWidth="2"
                    strokeLinecap="round">
                    <rect x="3" y="4" width="18" height="18"
                      rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span style={{
                    fontSize:15, fontWeight:700, color:"#1A1A1A"
                  }}>Weekly Schedule</span>
                </div>
                {schedule.map(({ day, sched }, i) => {
                  const isToday = i === todayIdx;
                  return (
                    <div key={day} style={{
                      display:"flex",
                      justifyContent:"space-between",
                      alignItems:"center",
                      padding:"14px 16px",
                      borderBottom: i < 6
                        ? "1px solid #F5EDE8" : "none",
                      borderLeft: isToday
                        ? "3px solid #C84B00" : "3px solid transparent",
                      background: isToday ? "#FFF7F0" : "transparent"
                    }}>
                      <span style={{
                        fontSize:14,
                        fontWeight:isToday ? 700 : 500,
                        color:isToday ? "#C84B00" : "#1A1A1A",
                        textTransform:"capitalize"
                      }}>
                        {day.charAt(0).toUpperCase()+day.slice(1)}
                      </span>
                      {sched.isClosed ? (
                        <span style={{
                          fontSize:13, fontWeight:600,
                          color:"#DC2626"
                        }}>Closed</span>
                      ) : sched.is24h ? (
                        <span style={{
                          fontSize:13, fontWeight:600,
                          color:isToday?"#C84B00":"#16A34A"
                        }}>Open 24 hours</span>
                      ) : (
                        <span style={{
                          fontSize:13, fontWeight:600,
                          color:"#1A1A1A"
                        }}>
                          {fmtTime(sched.open)} – {fmtTime(sched.close)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Note */}
              <div style={{
                background:"#FFF7F0",
                border:"1px solid #FED7AA",
                borderRadius:12, padding:"14px 16px",
                display:"flex", gap:10, alignItems:"flex-start"
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="#F97316" strokeWidth="2"
                  strokeLinecap="round" style={{ flexShrink:0 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span style={{
                  fontSize:13, color:"#6B7280", lineHeight:1.6
                }}>
                  Fuel availability and prices may fluctuate during
                  national holidays. Contact the station directly for
                  real-time stock updates.
                </span>
              </div>
            </div>
          );
        })()}

        {/* ════ PRODUCTS TAB ════ */}
        {activeTab === "products" && (
          <div>
            {prodLoading && (
              <div style={{
                textAlign:"center", padding:"48px 0",
                color:"#9CA3AF", fontSize:14
              }}>Loading products...</div>
            )}
            {!prodLoading && products && products.length === 0 && (
              <div style={{
                textAlign:"center", padding:"48px 20px"
              }}>
                <IC.ShoppingBag size={48} color="#E5D5CC"/>
                <div style={{
                  fontSize:16, fontWeight:700,
                  color:"#1A1A1A", marginTop:16
                }}>No products listed yet</div>
                <div style={{
                  fontSize:13, color:"#9CA3AF", marginTop:6
                }}>
                  This pump hasn't added any products
                </div>
              </div>
            )}
            {!prodLoading && products && products.length > 0 && (
              <div>
                {[...new Set(products.map(p => p.category))].map(cat => (
                  <div key={cat} style={{ marginBottom:24 }}>
                    {/* Category header */}
                    <div style={{
                      display:"flex", alignItems:"center",
                      justifyContent:"space-between", marginBottom:12
                    }}>
                      <span style={{
                        fontSize:13, fontWeight:800,
                        color:"#1A1A1A", letterSpacing:"0.06em",
                        textTransform:"uppercase"
                      }}>{cat}</span>
                      <span style={{
                        fontSize:11, fontWeight:600,
                        color:"#7B2D00",
                        background:"#FDE8D8",
                        borderRadius:999, padding:"3px 10px"
                      }}>
                        {products.filter(p=>p.category===cat&&p.in_stock).length} in stock
                      </span>
                    </div>

                    {/* Product cards */}
                    {products.filter(p => p.category === cat).map(prod => (
                      <div key={prod.id} style={{
                        background:"#FFFFFF",
                        borderRadius:16, padding:"14px",
                        marginBottom:10,
                        boxShadow:"0 2px 12px rgba(0,0,0,0.06)",
                        display:"flex", gap:12,
                        alignItems:"center",
                        opacity: prod.in_stock ? 1 : 0.6
                      }}>
                        {/* Product icon placeholder */}
                        <div style={{
                          width:60, height:60,
                          background:"#F5EDE8",
                          borderRadius:12, flexShrink:0,
                          display:"flex", alignItems:"center",
                          justifyContent:"center"
                        }}>
                          <IC.Package size={24} color="#C84B00"/>
                        </div>

                        {/* Product info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{
                            display:"flex", alignItems:"center",
                            gap:8, marginBottom:4
                          }}>
                            <span style={{
                              fontSize:15, fontWeight:700,
                              color:"#1A1A1A"
                            }}>{prod.name}</span>
                            <span style={{
                              fontSize:10, fontWeight:700,
                              color: prod.in_stock?"#16A34A":"#DC2626",
                              background: prod.in_stock?"#DCFCE7":"#FEE2E2",
                              borderRadius:999, padding:"2px 8px",
                              flexShrink:0
                            }}>
                              {prod.in_stock ? "IN STOCK" : "OUT"}
                            </span>
                          </div>
                          {prod.quantity != null && (
                            <div style={{
                              fontSize:12, color:"#9CA3AF"
                            }}>Qty: {prod.quantity}</div>
                          )}
                          <div style={{
                            fontSize:18, fontWeight:800,
                            color:"#1A1A1A", marginTop:4,
                            fontFamily:"'Outfit', sans-serif"
                          }}>₹{Number(prod.price).toLocaleString("en-IN")}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════ VERIFIED TAB ════ */}
        {activeTab === "wm" && (
          <div>
            {/* Hero verification card */}
            <div style={{
              background:"#F0FDF4",
              border:"1px solid #BBF7D0",
              borderRadius:20, padding:"28px 20px",
              textAlign:"center", marginBottom:20,
              position:"relative", overflow:"hidden"
            }}>
              {/* Background shield watermark */}
              <div style={{
                position:"absolute", right:-20, top:-20,
                opacity:0.06
              }}>
                <IC.Shield size={140} color="#16A34A"/>
              </div>

              {/* Shield icon */}
              <div style={{
                width:64, height:64,
                background:"linear-gradient(135deg,#166534,#15803D)",
                borderRadius:"50%",
                border:"3px solid #4ADE80",
                display:"flex", alignItems:"center",
                justifyContent:"center",
                margin:"0 auto 16px",
                boxShadow:"0 4px 16px rgba(22,101,52,0.3)"
              }}>
                <img src="/design/badges/wm-shield.svg" alt="verified"
                  style={{ width:30, height:30, filter:"brightness(0) invert(1)" }}/>
              </div>

              <div style={{
                fontSize:24, fontWeight:800, color:"#1A1A1A",
                fontFamily:"'Outfit', sans-serif", marginBottom:10
              }}>Government Verified</div>

              <div style={{
                fontSize:13, color:"#6B7280", lineHeight:1.7,
                maxWidth:280, margin:"0 auto"
              }}>
                The fuel dispensers at this station are certified
                and sealed by the{" "}
                <span style={{ color:"#16A34A", fontWeight:600 }}>
                  Weights &amp; Measures
                </span>{" "}
                department.
              </div>
            </div>

            {/* Certificate details */}
            <div style={{
              background:"#FFFFFF", borderRadius:16,
              padding:"16px", marginBottom:16,
              boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
            }}>
              <div style={{
                display:"flex", alignItems:"center",
                gap:8, marginBottom:16
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="#C84B00" strokeWidth="2"
                  strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span style={{
                  fontSize:13, fontWeight:700,
                  color:"#1A1A1A", letterSpacing:"0.06em",
                  textTransform:"uppercase"
                }}>Certificate Details</span>
              </div>

              <div style={{
                display:"grid", gridTemplateColumns:"1fr 1fr",
                gap:16
              }}>
                {[
                  ["CERTIFICATE NO", pump.wm_cert_number||"—"],
                  ["AUDIT DATE",     fmtDate(pump.wm_audit_date)],
                  ["VALID UNTIL",    fmtDate(pump.wm_valid_until)],
                  ["ISSUED BY",      "Dept. of Legal Metrology"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{
                      fontSize:10, fontWeight:600,
                      color:"#9CA3AF", letterSpacing:"0.08em",
                      marginBottom:4
                    }}>{label}</div>
                    <div style={{
                      fontSize:14, fontWeight:700,
                      color: label==="VALID UNTIL"?"#16A34A":"#1A1A1A",
                      lineHeight:1.3
                    }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* What this means */}
            <div style={{
              background:"#FFFFFF", borderRadius:16,
              padding:"16px",
              boxShadow:"0 2px 12px rgba(0,0,0,0.06)"
            }}>
              <div style={{
                display:"flex", alignItems:"center",
                gap:8, marginBottom:16
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="#C84B00" strokeWidth="2"
                  strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span style={{
                  fontSize:13, fontWeight:700,
                  color:"#1A1A1A", letterSpacing:"0.06em",
                  textTransform:"uppercase"
                }}>What This Means For You</span>
              </div>

              {[
                ["Precision Quantity",
                 "Every drop you pay for is exactly what enters your tank, guaranteed by physical seal audits."],
                ["Tamper Proof",
                 "Meters are sealed with specialized government-issued lead wires and tags to prevent any adjustment."],
                ["Regular Monitoring",
                 "Surprise inspections by the Metrology Department ensure continued compliance with national standards."],
              ].map(([title, desc]) => (
                <div key={title} style={{
                  display:"flex", gap:12,
                  marginBottom:14, alignItems:"flex-start"
                }}>
                  <div style={{
                    width:24, height:24,
                    background:"#DCFCE7", borderRadius:"50%",
                    display:"flex", alignItems:"center",
                    justifyContent:"center", flexShrink:0
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24"
                      fill="none" stroke="#16A34A" strokeWidth="3"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 12 10 17 19 8"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{
                      fontSize:14, fontWeight:700,
                      color:"#1A1A1A", marginBottom:3
                    }}>{title}</div>
                    <div style={{
                      fontSize:13, color:"#6B7280", lineHeight:1.6
                    }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── FIXED BOTTOM BUTTONS ── */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0,
        background:"#FFFFFF",
        borderTop:"1px solid #F5EDE8",
        padding:"12px 16px",
        paddingBottom:"calc(12px + env(safe-area-inset-bottom))",
        display:"flex", gap:12, zIndex:10
      }}>
        {/* Call button */}
        <button
          onClick={() => pump.owner_phone &&
            window.open(`tel:${pump.owner_phone}`)}
          style={{
            flex:1, height:48, borderRadius:999,
            background:"transparent",
            border:"1.5px solid #E5D5CC",
            display:"flex", alignItems:"center",
            justifyContent:"center", gap:8,
            fontSize:15, fontWeight:700,
            color:"#7B2D00", cursor:"pointer",
            fontFamily:"inherit"
          }}>
          <img src="/design/icons/call-outline.svg" alt="call"
            style={{ width:18, height:18, filter:"brightness(0) saturate(100%) invert(25%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(90%)" }}/>
          Call
        </button>

        {/* Navigate button */}
        <button
          onClick={e => openDirections(e, pump)}
          style={{
            flex:2, height:48, borderRadius:999,
            background:"linear-gradient(135deg,#C84B00,#F97316)",
            border:"none",
            display:"flex", alignItems:"center",
            justifyContent:"center", gap:8,
            fontSize:15, fontWeight:700,
            color:"white", cursor:"pointer",
            fontFamily:"inherit"
          }}>
          <IC.Navigate size={16} color="white"/>
          Navigate
        </button>
      </div>
    </div>
  );
}




// ─────────────────────────────────────────────────────────────────────────────
// FUELDOST CUSTOM SVG ICONS
// ─────────────────────────────────────────────────────────────────────────────

const IC = {
  // ── Tab icons ──────────────────────────────────────────────────────────────
  Home: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 5C14 5 7 11 7 17C7 20.9 10.1 24 14 24C17.9 24 21 20.9 21 17C21 11 14 5 14 5Z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
      <path d="M14 5L14 2" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M14 2L20 2L20 6" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="10.5" y="18" width="7" height="6" rx="1.5" stroke={color} strokeWidth="1.8"/>
      <rect x="13" y="20" width="2.5" height="4" rx="1" fill={color} opacity="0.3"/>
      <path d="M9 19L14 14L19 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  Map: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 8L6 22L11 19L17 22L22 19L22 5L17 8L11 5Z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
      <line x1="11" y1="5" x2="11" y2="19" stroke={color} strokeWidth="1.4" strokeDasharray="2.5 2"/>
      <line x1="17" y1="8" x2="17" y2="22" stroke={color} strokeWidth="1.4" strokeDasharray="2.5 2"/>
      <path d="M9 12C9 12 7 14.2 7 15.6C7 16.9 7.9 18 9 18C10.1 18 11 16.9 11 15.6C11 14.2 9 12 9 12Z" fill={color}/>
      <circle cx="9" cy="15.2" r="1.1" fill="white"/>
    </svg>
  ),

  Highway: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 25L9 7M14 25L19 7" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="14" y1="11" x2="14" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="14" y1="16" x2="14" y2="19" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="14" y1="21" x2="14" y2="24" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="10.5" y1="8.5" x2="6" y2="8.5" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      <line x1="9.5"  y1="12" x2="4" y2="12"   stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
      <line x1="17.5" y1="8.5" x2="22" y2="8.5" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      <line x1="18.5" y1="12" x2="24" y2="12"   stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
      <path d="M14 2C14 2 10.5 5.5 10.5 8C10.5 10 12.1 11.5 14 11.5C15.9 11.5 17.5 10 17.5 8C17.5 5.5 14 2 14 2Z" fill={color}/>
    </svg>
  ),

  Owner: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="10" r="5" stroke={color} strokeWidth="2"/>
      <path d="M4 25C4 20 7.6 17 12 17C16.4 17 20 20 20 25" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <rect x="19" y="6" width="7" height="10" rx="2" stroke={color} strokeWidth="1.8"/>
      <line x1="21" y1="9.5"  x2="24" y2="9.5"  stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="21" y1="12"   x2="24" y2="12"    stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="21" y1="14.5" x2="23" y2="14.5"  stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),

  // ── UI icons ───────────────────────────────────────────────────────────────
  FuelDrop: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3C12 3 5 9 5 15C5 18.9 8.1 22 12 22C15.9 22 19 18.9 19 15C19 9 12 3 12 3Z" fill={color}/>
      <path d="M9 18C9 18 8 16.5 8 15" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    </svg>
  ),

  Navigate: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="3 11 22 2 13 21 11 13 3 11" fill={color}/>
    </svg>
  ),

  Clock: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
      <polyline points="12 7 12 12 15 14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  BarChart: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3"  y="14" width="4" height="7" rx="1" fill={color} opacity="0.5"/>
      <rect x="10" y="9"  width="4" height="12" rx="1" fill={color} opacity="0.75"/>
      <rect x="17" y="4"  width="4" height="17" rx="1" fill={color}/>
    </svg>
  ),

  Package: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 8L12 3L3 8M21 8V16L12 21M21 8L12 13M3 8V16L12 21M3 8L12 13M12 13V21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  Shield: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="9 12 11 14 15 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  ShoppingBag: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="3" y1="6" x2="21" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <path d="M16 10a4 4 0 01-8 0" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  Search: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="2"/>
      <line x1="16.5" y1="16.5" x2="21" y2="21" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  Crosshair: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="2"/>
      <circle cx="12" cy="12" r="2" fill={color}/>
      <line x1="12" y1="2"  x2="12" y2="5"  stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="19" x2="12" y2="22" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="2"  y1="12" x2="5"  y2="12" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="19" y1="12" x2="22" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  Logout: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="16 17 21 12 16 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="21" y1="12" x2="9" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  Star: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),

  Warning: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="12" y1="9"  x2="12" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <line x1="12" y1="17" x2="12.01" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  Pin: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="10" r="3" stroke={color} strokeWidth="2"/>
    </svg>
  ),

  Flame: ({ size=24, color="currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// MAP VIEW (Leaflet.js — free, no billing)
// ─────────────────────────────────────────────────────────────────────────────
function MapView({ pumps, userLoc: initialUserLoc, onSelectPump }) {
  const mapRef           = useRef(null);
  const leafletRef       = useRef(null);
  const markersRef       = useRef([]);
  const userMarker       = useRef(null);       // live blue dot
  const userCircle       = useRef(null);       // accuracy ring
  const clusterGroupRef  = useRef(null);
  const [sheetPump,   setSheetPump]   = useState(null);
  const [locating,    setLocating]    = useState(false);  // spinner state
  const [liveUserLoc, setLiveUserLoc] = useState(initialUserLoc);
  const [locErr,      setLocErr]      = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeBrand, setActiveBrand] = useState("All");

  const filteredPumps = pumps.filter(pump => {
    if (activeBrand !== "All") {
      const brand  = (pump.brand || "").toLowerCase();
      const filter = activeBrand.toLowerCase();
      const matches =
        (filter === "hp"         && (brand.includes("hpcl") || brand === "hp")) ||
        (filter === "indian oil" && (brand.includes("indian oil") || brand.includes("ioc"))) ||
        (filter === "bpcl"       && (brand.includes("bpcl") || brand.includes("bharat"))) ||
        (filter === "shell"      && brand.includes("shell")) ||
        (filter === "reliance"   && brand.includes("reliance"));
      if (!matches) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const inName    = (pump.name    || "").toLowerCase().includes(q);
      const inAddress = (pump.address || "").toLowerCase().includes(q);
      const inBrand   = (pump.brand   || "").toLowerCase().includes(q);
      if (!inName && !inAddress && !inBrand) return false;
    }
    return true;
  });

  // ── Fly to user and update/create the blue dot ─────────────────────────────
  function flyToUser(lat, lng, accuracy) {
    const L   = window.L;
    const map = leafletRef.current;
    if (!L || !map) return;

    // Smooth fly animation — like Google Maps
    map.flyTo([lat, lng], 15, { animate: true, duration: 1.2 });

    // Remove old markers
    if (userMarker.current) { userMarker.current.remove(); userMarker.current = null; }
    if (userCircle.current) { userCircle.current.remove(); userCircle.current = null; }

    // Accuracy ring (light blue halo)
    if (accuracy && accuracy < 500) {
      userCircle.current = L.circle([lat, lng], {
        radius: accuracy, color: "#0EA5E9", fillColor: "#0EA5E9",
        fillOpacity: 0.08, weight: 1, dashArray: "4",
      }).addTo(map);
    }

    // Blue dot with white border — exact Google Maps style
    const icon = L.divIcon({
      className: "",
      html: `<div style="
        width:18px;height:18px;
        background:#0EA5E9;
        border:3px solid #fff;
        border-radius:50%;
        box-shadow:0 2px 8px rgba(14,165,233,0.5);
      "></div>`,
      iconSize:   [18, 18],
      iconAnchor: [9, 9],
    });
    userMarker.current = L.marker([lat, lng], { icon, zIndexOffset: 1000 })
      .addTo(map)
      .bindPopup("📍 You are here");
  }

  // ── Locate me button handler ───────────────────────────────────────────────
  function handleLocate() {
    if (!navigator.geolocation) { setLocErr("GPS not available on this device"); return; }
    setLocating(true);
    setLocErr("");
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setLiveUserLoc({ lat, lng });
        flyToUser(lat, lng, accuracy);
        setLocating(false);
      },
      err => {
        setLocating(false);
        setLocErr(err.code === 1 ? "Please allow location access in your browser settings." : "Could not get your location. Try again.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  useEffect(() => {
    // Load Leaflet CSS + JS dynamically
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id    = "leaflet-css";
      link.rel   = "stylesheet";
      link.href  = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    if (!document.getElementById("leaflet-cluster-css")) {
      const clusterCss = document.createElement("link");
      clusterCss.id   = "leaflet-cluster-css";
      clusterCss.rel  = "stylesheet";
      clusterCss.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.css";
      document.head.appendChild(clusterCss);
    }

    if (!document.getElementById("leaflet-cluster-default-css")) {
      const clusterDefaultCss = document.createElement("link");
      clusterDefaultCss.id   = "leaflet-cluster-default-css";
      clusterDefaultCss.rel  = "stylesheet";
      clusterDefaultCss.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.Default.css";
      document.head.appendChild(clusterDefaultCss);
    }

    function initMap(L) {
      if (leafletRef.current) return;
      const defaultCenter = liveUserLoc
        ? [liveUserLoc.lat, liveUserLoc.lng]
        : [19.076, 72.877]; // Mumbai fallback

      const map = L.map(mapRef.current, { zoomControl: false }) // we add zoom manually positioned
                   .setView(defaultCenter, 13);
      leafletRef.current = map;

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        attribution: "© OpenStreetMap © CARTO",
        maxZoom: 19,
        subdomains: "abcd",
      }).addTo(map);

      // Drop blue dot if we already have location
      if (liveUserLoc) {
        flyToUser(liveUserLoc.lat, liveUserLoc.lng, null);
      }

      // Pump markers
      const clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 60,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          return L.divIcon({
            className: "",
            html: `<div style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              background: #C84B00;
              border: 3px solid #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 3px 10px rgba(200,75,0,0.4);
              font-family: 'Plus Jakarta Sans', sans-serif;
              font-size: 14px;
              font-weight: 800;
              color: white;
            ">${count}</div>`,
            iconSize: [44, 44],
            iconAnchor: [22, 22],
          });
        },
      });
      clusterGroupRef.current = clusterGroup;
      map.addLayer(clusterGroup);

      pumps.forEach(pump => {
        if (!pump.lat || !pump.lng) return;
        const status = getOpenStatus(pump);
        const overall = Math.round(
          (lvl(pump.petrol, pump.max_petrol) +
           lvl(pump.diesel, pump.max_diesel)) / 2
        );
        const pinColor = !status.isOpen ? "#6B7280"
          : overall > 70 ? "#16A34A"
          : overall > 20 ? "#F97316"
          : "#EF4444";

        const brandPin = (() => {
          const b = (pump.brand||"").toLowerCase().trim();
          if(b.includes("indian oil")||b.includes("ioc")) return "/brands/pins/ioc.png";
          if(b.includes("hpcl")||b==="hp") return "/brands/pins/hpcl.png";
          if(b.includes("bpcl")||b.includes("bharat")) return "/brands/pins/bpcl.png";
          if(b.includes("shell")) return "/brands/pins/shell.png";
          if(b.includes("reliance")) return "/brands/pins/reliance.png";
          if(b.includes("essar")||b.includes("nayara")) return "/brands/pins/nayara.png";
          return "/brands/pins/ioc.png";
        })();

        const icon = L.divIcon({
          className: "",
          html: `
            <div style="
              position: relative;
              display: flex;
              align-items: center;
              filter: drop-shadow(0 3px 6px rgba(0,0,0,0.3));
              cursor: pointer;
            ">
              <div style="
                display: flex;
                align-items: center;
                background: white;
                border-radius: 999px;
                border: 2.5px solid ${pinColor};
                overflow: hidden;
                height: 36px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              ">
                <div style="
                  width: 32px;
                  height: 32px;
                  border-radius: 50%;
                  overflow: hidden;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: white;
                  flex-shrink: 0;
                  margin: 1px;
                ">
                  <img src="${brandPin}" style="width:28px;height:28px;object-fit:contain;"/>
                </div>
                <div style="
                  padding: 0 10px 0 6px;
                  font-size: 13px;
                  font-weight: 800;
                  color: ${pinColor};
                  font-family: 'Outfit', sans-serif;
                  white-space: nowrap;
                ">${overall}%</div>
              </div>
              <div style="
                position: absolute;
                bottom: -7px;
                left: 50%;
                transform: translateX(-50%) rotate(45deg);
                width: 10px;
                height: 10px;
                background: ${pinColor};
                border-radius: 1px;
              "/>
            </div>`,
          iconSize: [90, 44],
          iconAnchor: [45, 44],
          popupAnchor: [0, -44],
        });

        L.marker([pump.lat, pump.lng], { icon })
          .on("click", (e) => {
            L.DomEvent.stopPropagation(e);
            setSheetPump(pump);
          })
          .addTo(clusterGroup);
      });
    }

    function loadClusterThenInit() {
      if (window.L.MarkerClusterGroup) {
        initMap(window.L);
        return;
      }
      const clusterScript = document.createElement("script");
      clusterScript.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.js";
      clusterScript.onload = () => initMap(window.L);
      document.body.appendChild(clusterScript);
    }

    if (window.L) {
      loadClusterThenInit();
    } else {
      const script  = document.createElement("script");
      script.src    = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      script.onload = () => loadClusterThenInit();
      document.body.appendChild(script);
    }

    return () => {
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    };
  }, []);

  useEffect(() => {
    const map = leafletRef.current;
    const cg  = clusterGroupRef.current;
    if (!map || !cg || !window.L) return;
    const L = window.L;

    cg.clearLayers();

    filteredPumps.forEach(pump => {
      if (!pump.lat || !pump.lng) return;
      const status  = getOpenStatus(pump);
      const overall = Math.round(
        (lvl(pump.petrol, pump.max_petrol) +
         lvl(pump.diesel, pump.max_diesel)) / 2
      );
      const pinColor = !status.isOpen ? "#6B7280"
        : overall > 70 ? "#16A34A"
        : overall > 20 ? "#F97316"
        : "#EF4444";

      const brandPin = (() => {
        const b = (pump.brand || "").toLowerCase().trim();
        if (b.includes("indian oil") || b.includes("ioc")) return "/brands/pins/ioc.png";
        if (b.includes("hpcl") || b === "hp") return "/brands/pins/hpcl.png";
        if (b.includes("bpcl") || b.includes("bharat")) return "/brands/pins/bpcl.png";
        if (b.includes("shell")) return "/brands/pins/shell.png";
        if (b.includes("reliance")) return "/brands/pins/reliance.png";
        if (b.includes("essar") || b.includes("nayara")) return "/brands/pins/nayara.png";
        return "/brands/pins/ioc.png";
      })();

      const icon = L.divIcon({
        className: "",
        html: `
          <div style="
            position:relative;display:flex;align-items:center;
            filter:drop-shadow(0 3px 6px rgba(0,0,0,0.3));cursor:pointer;
          ">
            <div style="
              display:flex;align-items:center;background:white;
              border-radius:999px;border:2.5px solid ${pinColor};
              overflow:hidden;height:36px;
              box-shadow:0 2px 8px rgba(0,0,0,0.2);
            ">
              <div style="
                width:32px;height:32px;border-radius:50%;overflow:hidden;
                display:flex;align-items:center;justify-content:center;
                background:white;flex-shrink:0;margin:1px;
              ">
                <img src="${brandPin}" style="width:28px;height:28px;object-fit:contain;"/>
              </div>
              <div style="
                padding:0 10px 0 6px;font-size:13px;font-weight:800;
                color:${pinColor};font-family:'Outfit',sans-serif;white-space:nowrap;
              ">${overall}%</div>
            </div>
            <div style="
              position:absolute;bottom:-7px;left:50%;
              transform:translateX(-50%) rotate(45deg);
              width:10px;height:10px;background:${pinColor};border-radius:1px;
            "/>
          </div>`,
        iconSize:   [90, 44],
        iconAnchor: [45, 44],
      });

      L.marker([pump.lat, pump.lng], { icon })
        .on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          setSheetPump(pump);
        })
        .addTo(cg);
    });
  }, [filteredPumps]);

  useEffect(() => {
    if (!leafletRef.current) return;
    leafletRef.current.on("click", () => {
      setSheetPump(null);
    });
  }, [leafletRef.current]);

  const pp  = sheetPump ? lvl(sheetPump.petrol,  sheetPump.max_petrol) : 0;
  const dp  = sheetPump ? lvl(sheetPump.diesel,  sheetPump.max_diesel) : 0;
  const sts = sheetPump ? getOpenStatus(sheetPump) : null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:50, overflow:"hidden" }}>

      {/* Leaflet map — teal tinted */}
      <div style={{ position:"absolute", inset:0, zIndex:1 }}>
        <div ref={mapRef} style={{ width:"100%", height:"100%" }}/>
      </div>

      {/* ── TOP BAR OVERLAY ── */}
      <div style={{
        position:"absolute", top:0, left:0, right:0, zIndex:1000,
        padding:"52px 16px 12px",
        background:"transparent",
      }}>
        {/* Back button + Search row */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
          <button
            onClick={() => onSelectPump && onSelectPump(null)}
            style={{
              width:40, height:40, borderRadius:"50%",
              background:"#FFFFFF", border:"none", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 2px 8px rgba(0,0,0,0.15)", flexShrink:0,
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div style={{
            flex:1, display:"flex", alignItems:"center", gap:8,
            background:"#FFFFFF", borderRadius:999, padding:"0 16px",
            height:44, boxShadow:"0 2px 12px rgba(0,0,0,0.12)",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/>
              <line x1="16.5" y1="16.5" x2="21" y2="21"/>
            </svg>
            <input
              type="text"
              placeholder="Find pumps"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSheetPump(null)}
              style={{
                flex:1, border:"none", background:"transparent",
                fontSize:15, color:"#1A1A1A",
                fontFamily:"'Plus Jakarta Sans', sans-serif",
                outline:"none",
              }}
            />
          </div>
        </div>

        {/* Brand filter pills */}
        <div style={{ display:"flex", gap:8, overflowX:"auto", scrollbarWidth:"none", paddingBottom:4 }}>
          {["All","HP","Indian Oil","BPCL","Shell","Reliance"].map(b => {
            const isActive = activeBrand === b;
            return (
              <button
                key={b}
                onClick={() => setActiveBrand(b)}
                style={{
                  height:34, borderRadius:999, padding:"0 14px",
                  fontSize:13, fontWeight:600, border:"none", cursor:"pointer",
                  whiteSpace:"nowrap", flexShrink:0,
                  fontFamily:"'Plus Jakarta Sans', sans-serif",
                  background: isActive ? "linear-gradient(135deg,#7B2D00,#C84B00)" : "#FFFFFF",
                  color: isActive ? "#FFFFFF" : "#1A1A1A",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.1)",
                  transform: isActive ? "scale(1.04)" : "scale(1)",
                  transition:"all 0.15s ease",
                }}
              >{b}</button>
            );
          })}
        </div>
      </div>

      {/* ── LEGEND — top left ── */}
      <div style={{
        position:"absolute", top:200, left:16, zIndex:1000,
        background:"rgba(255,255,255,0.95)", borderRadius:12,
        padding:"10px 14px", boxShadow:"0 2px 12px rgba(0,0,0,0.1)",
      }}>
        {[["#22C55E",">70% HIGH"],["#F97316","20-70% LOW"],["#EF4444","<20% CRITICAL"]].map(([color,label]) => (
          <div key={label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:color, flexShrink:0 }}/>
            <span style={{ fontSize:11, fontWeight:600, color:"#1A1A1A", fontFamily:"'Plus Jakarta Sans', sans-serif" }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── ZOOM + LOCATE controls — right side ── */}
      <div style={{
        position:"absolute", right:16, bottom:180, top:"auto", transform:"none",
        zIndex:1000, background:"#FFFFFF", borderRadius:16,
        boxShadow:"0 2px 12px rgba(0,0,0,0.12)", overflow:"hidden",
        display:"flex", flexDirection:"column",
      }}>
        <button onClick={handleLocate} style={{
          width:44, height:44, background:"transparent", border:"none",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          borderBottom:"0.5px solid #F5EDE8",
        }}>
          {locating ? (
            <div style={{ width:16,height:16,border:"2.5px solid #E0F2FE",borderTopColor:"#0EA5E9",borderRadius:"50%",animation:"hwy-spin 0.8s linear infinite" }}/>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="7"/>
              <circle cx="12" cy="12" r="2" fill="#1A1A1A"/>
              <line x1="12" y1="2" x2="12" y2="5"/>
              <line x1="12" y1="19" x2="12" y2="22"/>
              <line x1="2" y1="12" x2="5" y2="12"/>
              <line x1="19" y1="12" x2="22" y2="12"/>
            </svg>
          )}
        </button>
        <button onClick={() => leafletRef.current?.zoomIn()} style={{
          width:44, height:44, background:"transparent", border:"none",
          cursor:"pointer", fontSize:20, fontWeight:300, color:"#1A1A1A",
          display:"flex", alignItems:"center", justifyContent:"center",
          borderBottom:"0.5px solid #F5EDE8",
        }}>+</button>
        <button onClick={() => leafletRef.current?.zoomOut()} style={{
          width:44, height:44, background:"transparent", border:"none",
          cursor:"pointer", fontSize:20, fontWeight:300, color:"#1A1A1A",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>−</button>
      </div>

      {/* ── Location error toast ── */}
      {locErr && (
        <div style={{
          position:"absolute", bottom:80, left:16, right:16,
          background:"#FEE2E2", border:"1px solid #FCA5A5",
          borderRadius:12, padding:"10px 14px",
          fontSize:12, color:"#DC2626", fontWeight:600,
          zIndex:1000, boxShadow:"0 2px 8px rgba(0,0,0,0.1)",
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
        }}>
          <span>📡 {locErr}</span>
          <button onClick={() => setLocErr("")} style={{ background:"none",border:"none",cursor:"pointer",color:"#DC2626",fontSize:16,padding:0 }}>✕</button>
        </div>
      )}

      {/* ── RIGHT SIDE CARD — pump detail ── */}
      {sheetPump && (
        <div style={{
          position: "fixed",
          right: 16,
          bottom: 80,
          width: 220,
          zIndex: 1000,
          animation: "slideInRight 0.3s cubic-bezier(.34,1.2,.64,1)",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}>
          <div style={{
            background: "rgba(255,255,255,0.88)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: 20,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.7)",
          }}>

            {/* Pump name + close */}
            <div style={{
              padding: "12px 12px 8px",
              borderBottom: "1px solid rgba(229,213,204,0.5)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
            }}>
              <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"flex-start", gap:6 }}>
                {sheetPump.wm_cert_number && (
                  <div style={{
                    width: 16, height: 16,
                    background: "#16A34A",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}>
                    <img src="/design/badges/wm-shield.svg"
                      style={{ width:8, height:8, filter:"brightness(0) invert(1)" }}
                    />
                  </div>
                )}
                <div style={{ minWidth:0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 800,
                    color: "#1A1A1A",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    lineHeight: 1.3,
                    marginBottom: 2,
                  }}>{sheetPump.name}</div>
                  {sheetPump.distance != null && (
                    <div style={{ fontSize:11, color:"#9CA3AF", fontWeight:500 }}>
                      {sheetPump.distance < 1
                        ? `${(sheetPump.distance*1000).toFixed(0)} m away`
                        : `${sheetPump.distance.toFixed(1)} km away`}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSheetPump(null)}
                style={{
                  width: 22, height: 22,
                  borderRadius: "50%",
                  background: "#F5EDE8",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7B2D00",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}>✕</button>
            </div>

            {/* KPI row */}
            <div style={{
              display:"grid", gridTemplateColumns:"1fr 1fr",
              gap:0,
              borderBottom:"1px solid rgba(229,213,204,0.5)",
            }}>
              {/* Petrol */}
              <div style={{
                padding:"10px 12px",
                borderRight:"1px solid rgba(229,213,204,0.5)",
                background:"rgba(255,248,245,0.8)",
              }}>
                <div style={{
                  fontSize:9, fontWeight:700,
                  color:"#F97316",
                  letterSpacing:"0.08em",
                  marginBottom:4,
                }}>PETROL</div>
                <div style={{
                  fontSize:22, fontWeight:900,
                  color:"#1A1A1A",
                  fontFamily:"'Outfit', sans-serif",
                  lineHeight:1,
                }}>{pp}%</div>
                <div style={{
                  fontSize:10, fontWeight:600, marginTop:3,
                  color:pp>70?"#16A34A":pp>20?"#F97316":"#EF4444",
                }}>
                  {pp>70?"Full":pp>20?"Mid":"Low"}
                </div>
              </div>
              {/* Diesel */}
              <div style={{
                padding:"10px 12px",
                background:"rgba(240,247,255,0.8)",
              }}>
                <div style={{
                  fontSize:9, fontWeight:700,
                  color:"#2563EB",
                  letterSpacing:"0.08em",
                  marginBottom:4,
                }}>DIESEL</div>
                <div style={{
                  fontSize:22, fontWeight:900,
                  color:"#2563EB",
                  fontFamily:"'Outfit', sans-serif",
                  lineHeight:1,
                }}>{dp}%</div>
                <div style={{
                  fontSize:10, fontWeight:600, marginTop:3,
                  color:dp>70?"#16A34A":dp>20?"#F97316":"#EF4444",
                }}>
                  {dp>70?"Full":dp>20?"Mid":"Low"}
                </div>
              </div>
            </div>

            {/* Navigate button */}
            <button
              onClick={e => { e.stopPropagation(); openDirections(e, sheetPump); }}
              style={{
                width:"100%",
                padding:"12px",
                background:"linear-gradient(90deg,#7B2D00 0%,#C84B00 40%,#F97316 100%)",
                border:"none", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                gap:8, fontSize:13, fontWeight:700, color:"white",
                fontFamily:"'Plus Jakarta Sans', sans-serif",
              }}>
              <IC.Navigate size={14} color="white"/>
              Navigate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRAVEL MODE
// ─────────────────────────────────────────────────────────────────────────────
/*
  IMPORTANT: Before this feature works,
  run this SQL in Supabase SQL Editor:

  CREATE OR REPLACE FUNCTION
  get_pumps_along_route(
    route_linestring TEXT,
    corridor_meters INTEGER DEFAULT 500
  )
  RETURNS SETOF pumps AS $$
  BEGIN
    RETURN QUERY
    SELECT p.*
    FROM pumps p
    WHERE
      p.is_suspended = false
      AND ST_DWithin(
        ST_SetSRID(
          ST_MakePoint(p.lng, p.lat), 4326
        )::geography,
        ST_GeomFromText(
          route_linestring, 4326
        )::geography,
        corridor_meters
      )
    ORDER BY
      ST_LineLocatePoint(
        ST_GeomFromText(route_linestring, 4326),
        ST_SetSRID(
          ST_MakePoint(p.lng, p.lat), 4326
        )
      ) ASC;
  END;
  $$ LANGUAGE plpgsql;
*/
function TravelMode({ onExit }) {
  const [screen, setScreen] = useState("input");
  const [destination, setDestination] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [route, setRoute] = useState(null);
  const [pumpsOnRoute, setPumpsOnRoute] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterAvailable, setFilterAvailable] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    loadGooglePlaces(GOOGLE_API_KEY)
      .then(() => {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      })
      .catch(console.error);
  }, []);

  async function searchPlaces(query) {
    if (!query || query.length < 3) { setSuggestions([]); return; }
    setSearching(true);
    try {
      await loadGooglePlaces(GOOGLE_API_KEY);
      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }
      const { suggestions } = await window.google.maps.places
        .AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ["in"],
        });
        setSuggestions(suggestions
          .filter(s => s.placePrediction)
          .map(s => {
            const pred = s.placePrediction;
            const mainText = pred.structuredFormat?.mainText?.text
              || pred.text?.text
              || "";
            const secondaryText = pred.structuredFormat?.secondaryText?.text
              || "";
            return {
              place_id: pred.placeId,
              description: pred.text?.text || mainText,
              structured_formatting: {
                main_text: mainText,
                secondary_text: secondaryText,
              },
              placePrediction: pred,
            };
          })
        );
    } catch(e) {
      console.error("Autocomplete error:", e);
      setSuggestions([]);
    }
    setSearching(false);
  }

  async function selectPlace(prediction) {
    setSuggestions([]);
    setSearchText(prediction.structured_formatting?.main_text || prediction.description);
    try {
      await loadGooglePlaces(GOOGLE_API_KEY);
      const place = prediction.placePrediction
        ? await prediction.placePrediction.toPlace()
        : new window.google.maps.places.Place({ id: prediction.place_id });
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location"],
      });
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
      setDestination({
        lat: place.location.lat(),
        lng: place.location.lng(),
        name: place.displayName,
        address: place.formattedAddress,
      });
    } catch(e) {
      console.error("Place details error:", e);
      setError("Could not get location details.");
    }
  }

  function getUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) { reject(new Error("GPS not available")); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        err => reject(err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }

  async function getRoute(fromLat, fromLng, toLat, toLng) {
    const url =
      `https://router.project-osrm.org/route/v1/driving/` +
      `${fromLng},${fromLat};${toLng},${toLat}` +
      `?overview=full&geometries=geojson&steps=false`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.code !== "Ok") throw new Error("Route not found");
    const r = data.routes[0];
    const coords = r.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    const linestring =
      "LINESTRING(" +
      r.geometry.coordinates.map(([lng, lat]) => `${lng} ${lat}`).join(", ") +
      ")";
    return {
      coords,
      linestring,
      distanceKm: (r.distance / 1000).toFixed(1),
      durationMin: Math.round(r.duration / 60),
    };
  }

  async function getPumpsOnRoute(linestring) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_pumps_along_route`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ route_linestring: linestring, corridor_meters: 500 }),
    });
    const data = await res.json();
    return data || [];
  }

  async function findRoute() {
    if (!destination) { setError("Please select a destination first."); return; }
    setLoading(true);
    setError("");
    try {
      const loc = await getUserLocation();
      setUserLoc(loc);
      const routeData = await getRoute(loc.lat, loc.lng, destination.lat, destination.lng);
      setRoute(routeData);
      const pumps = await getPumpsOnRoute(routeData.linestring);
      setPumpsOnRoute(pumps);
      setScreen("concierge");
    } catch(e) {
      setError(
        e.message === "Route not found"
          ? "Could not find a route to this destination."
          : "Could not get your location. Please allow GPS access."
      );
    }
    setLoading(false);
  }

  function navigate(pump) {
    const url = [
      "https://www.google.com/maps/dir/?api=1",
      `&origin=${userLoc.lat},${userLoc.lng}`,
      `&destination=${destination.lat},${destination.lng}`,
      `&waypoints=${pump.lat},${pump.lng}`,
      "&travelmode=driving",
    ].join("");
    window.open(url, "_blank");
  }

  function navigateDirect() {
    const availablePump = pumpsOnRoute.find(p => lvl(p.petrol, p.max_petrol) > 20 || lvl(p.diesel, p.max_diesel) > 20);
    if (availablePump) {
      navigate(availablePump);
    } else {
      const url = [
        "https://www.google.com/maps/dir/?api=1",
        `&origin=${userLoc.lat},${userLoc.lng}`,
        `&destination=${destination.lat},${destination.lng}`,
        "&travelmode=driving",
      ].join("");
      window.open(url, "_blank");
    }
  }

  function fmtETA(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  }

  function pumpETA(pump) {
    if (!route) return "";
    const ratio = pump.distance_along_route || 0;
    const mins = Math.round(ratio * parseInt(route.durationMin));
    return fmtETA(mins);
  }

  function pumpDistKm(pump) {
    if (!route) return "";
    const ratio = pump.distance_along_route || 0;
    return (ratio * parseFloat(route.distanceKm)).toFixed(1);
  }

  const miniMapRef = useRef(null);
  const miniLeaflet = useRef(null);

  useEffect(() => {
    if (screen !== "concierge" || !route) return;
    if (miniLeaflet.current) return;

    function initMiniMap(L) {
      if (!miniMapRef.current) return;
      const map = L.map(miniMapRef.current, {
        zoomControl: false, dragging: false, touchZoom: false,
        scrollWheelZoom: false, doubleClickZoom: false, attributionControl: false,
      });
      miniLeaflet.current = map;
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        { maxZoom: 15, subdomains: "abcd" }
      ).addTo(map);
      const polyline = L.polyline(route.coords, { color: "#C84B00", weight: 4, opacity: 0.85 }).addTo(map);
      map.fitBounds(polyline.getBounds(), { padding: [20, 20] });
      pumpsOnRoute.forEach(pump => {
        const overall = Math.round((lvl(pump.petrol, pump.max_petrol) + lvl(pump.diesel, pump.max_diesel)) / 2);
        const color = overall > 20 ? "#16A34A" : "#EF4444";
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:10px;height:10px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
          iconSize: [10, 10], iconAnchor: [5, 5],
        });
        L.marker([pump.lat, pump.lng], { icon }).addTo(map);
      });
    }

    if (window.L) {
      initMiniMap(window.L);
    } else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      script.onload = () => initMiniMap(window.L);
      document.body.appendChild(script);
    }
    return () => {
      if (miniLeaflet.current) { miniLeaflet.current.remove(); miniLeaflet.current = null; }
    };
  }, [screen, route]);

  const filtered = filterAvailable
    ? pumpsOnRoute.filter(p => lvl(p.petrol, p.max_petrol) > 0 || lvl(p.diesel, p.max_diesel) > 0 || (p.max_cng && lvl(p.cng, p.max_cng) > 0))
    : pumpsOnRoute;

  if (screen === "input") return (
    <div style={{ minHeight:"100vh", background:"#FDF3EE", fontFamily:"'Plus Jakarta Sans', sans-serif", paddingBottom:80 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 16px", background:"#FFFFFF", borderBottom:"1px solid #F5EDE8" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={onExit} style={{ background:"none", border:"none", cursor:"pointer", padding:4, display:"flex", alignItems:"center" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C84B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <span style={{ fontSize:20, fontWeight:800, color:"#1A1A1A", fontFamily:"'Plus Jakarta Sans', sans-serif" }}>Fuel Concierge</span>
        </div>
        <div style={{ width:40, height:40, borderRadius:"50%", border:"2.5px solid #C84B00", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", background:"#FDE8D8" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#C84B00">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
      </div>

      <div style={{ padding:"32px 16px 16px" }}>
        <div style={{ fontSize:24, fontWeight:800, color:"#1A1A1A", marginBottom:6, fontFamily:"'Outfit', sans-serif" }}>Where are you going?</div>
        <div style={{ fontSize:14, color:"#9CA3AF", marginBottom:24 }}>We'll find fuel stops along your route</div>

        <div style={{ position:"relative" }}>
          <div style={{ display:"flex", alignItems:"center", background:"#FFFFFF", borderRadius:14, border:"1.5px solid #E5D5CC", padding:"0 16px", height:54, gap:10, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/>
            </svg>
            <input
              ref={searchRef}
              value={searchText}
              onChange={e => { setSearchText(e.target.value); searchPlaces(e.target.value); }}
              placeholder="Search destination..."
              style={{ flex:1, border:"none", background:"transparent", fontSize:15, color:"#1A1A1A", outline:"none", fontFamily:"inherit" }}
            />
            {searching && (
              <div style={{ width:16, height:16, border:"2px solid #E5D5CC", borderTopColor:"#C84B00", borderRadius:"50%", animation:"spin-slow 0.8s linear infinite" }}/>
            )}
          </div>

          {suggestions.length > 0 && (
            <div style={{ position:"absolute", top:60, left:0, right:0, background:"#FFFFFF", borderRadius:14, border:"1px solid #E5D5CC", boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:100, overflow:"hidden" }}>
              {suggestions.map((s, i) => (
                <div
                  key={s.place_id}
                  onClick={() => selectPlace(s)}
                  style={{ padding:"14px 16px", borderBottom:i < suggestions.length-1 ? "1px solid #F5EDE8":"none", cursor:"pointer", display:"flex", gap:12, alignItems:"flex-start" }}
                  onMouseEnter={e => e.currentTarget.style.background="#FDF3EE"}
                  onMouseLeave={e => e.currentTarget.style.background="#FFFFFF"}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C84B00" strokeWidth="2" strokeLinecap="round" style={{ flexShrink:0, marginTop:2 }}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#1A1A1A", marginBottom:2 }}>{s.structured_formatting?.main_text || s.description}</div>
                    <div style={{ fontSize:12, color:"#9CA3AF" }}>{s.structured_formatting?.secondary_text || ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {destination && (
          <div style={{ marginTop:16, background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="5 12 10 17 19 8"/>
            </svg>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#166534" }}>{destination.name}</div>
              <div style={{ fontSize:11, color:"#16A34A", marginTop:1 }}>{destination.address}</div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ marginTop:16, background:"#FEE2E2", border:"1px solid #FCA5A5", borderRadius:12, padding:"12px 16px", fontSize:13, color:"#DC2626" }}>{error}</div>
        )}

        <button
          onClick={findRoute}
          disabled={!destination || loading}
          style={{
            width:"100%", height:54, marginTop:24, borderRadius:999,
            background: !destination || loading ? "#E5D5CC" : "linear-gradient(90deg,#7B2D00,#C84B00,#F97316)",
            border:"none", cursor: !destination || loading ? "not-allowed":"pointer",
            fontSize:16, fontWeight:700, color:"white", fontFamily:"inherit",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            boxShadow: destination && !loading ? "0 4px 20px rgba(123,45,0,0.3)":"none",
            transition:"all 0.2s",
          }}>
          {loading ? (
            <>
              <div style={{ width:18, height:18, border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"white", borderRadius:"50%", animation:"spin-slow 0.8s linear infinite" }}/>
              Finding pumps on your route...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
              Find Fuel Stops
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"#FDF3EE", fontFamily:"'Plus Jakarta Sans', sans-serif", paddingBottom:80 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 16px", background:"#FFFFFF", borderBottom:"1px solid #F5EDE8" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => setScreen("input")} style={{ background:"none", border:"none", cursor:"pointer", padding:4, display:"flex", alignItems:"center" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C84B00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <span style={{ fontSize:20, fontWeight:800, color:"#1A1A1A" }}>Fuel Concierge</span>
        </div>
        <div style={{ width:40, height:40, borderRadius:"50%", border:"2.5px solid #C84B00", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", background:"#FDE8D8" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#C84B00">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
      </div>

      <div ref={miniMapRef} style={{ height:180, width:"100%", background:"#F5EDE8" }}/>

      <div style={{ padding:"14px 16px 12px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#C84B00"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
          <span style={{ fontSize:17, fontWeight:800, color:"#1A1A1A" }}>Travel Mode: {destination?.name}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#6B7280" }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:"#16A34A", flexShrink:0 }}/>
          ETA: {fmtETA(parseInt(route?.durationMin))}&nbsp;•&nbsp;{route?.distanceKm} km remaining
        </div>
      </div>

      <div style={{ height:1, background:"#F5EDE8", margin:"0 16px" }}/>

      <div style={{ display:"flex", gap:10, padding:"14px 16px" }}>
        <button onClick={() => setFilterAvailable(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:999, border:"none", cursor:"pointer", background:filterAvailable?"#FDE8D8":"#FFFFFF", color:filterAvailable?"#7B2D00":"#6B7280", fontSize:13, fontWeight:600, fontFamily:"inherit", boxShadow:filterAvailable?"none":"0 0 0 1px #E5D5CC" }}>
          {filterAvailable && (
            <div style={{ width:18, height:18, borderRadius:"50%", background:"#16A34A", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 10 17 19 8"/></svg>
            </div>
          )}
          Available fuel
        </button>
        <button onClick={() => setFilterAvailable(false)} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:999, border:"1px solid #E5D5CC", cursor:"pointer", background:!filterAvailable?"#FDE8D8":"#FFFFFF", color:!filterAvailable?"#7B2D00":"#6B7280", fontSize:13, fontWeight:600, fontFamily:"inherit" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
            <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
          </svg>
          All pumps
        </button>
      </div>

      <div style={{ padding:"0 16px" }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"48px 20px", color:"#9CA3AF", fontSize:14 }}>
            <img src="/design/icons/gas-station-not-found.svg" alt="" style={{ width:60, height:60, opacity:0.3, marginBottom:12, display:"block", margin:"0 auto 12px" }}/>
            No fuel stops found on this route
          </div>
        )}

        {filtered.map((pump, i) => {
          const pp = lvl(pump.petrol, pump.max_petrol);
          const dp = lvl(pump.diesel, pump.max_diesel);
          const cp = pump.max_cng ? lvl(pump.cng, pump.max_cng) : 0;
          const hasAnyFuel = pp > 0 || dp > 0 || cp > 0;
          const dotColor = hasAnyFuel ? "#7B2D00" : "#EF4444";
          const isLast = i === filtered.length - 1;

          return (
            <div key={pump.id} style={{ display:"flex", gap:0, marginBottom:isLast?0:8 }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:24, flexShrink:0, paddingTop:6 }}>
                <div style={{ width:12, height:12, borderRadius:"50%", background:hasAnyFuel?"#7B2D00":"#EF4444", border:"2px solid white", boxShadow:`0 0 0 1px ${dotColor}`, flexShrink:0, zIndex:1 }}/>
                {!isLast && <div style={{ width:2, flex:1, minHeight:40, background:"#E5D5CC", marginTop:4 }}/>}
              </div>

              <div style={{ flex:1, marginLeft:12, marginBottom:16, background:"#FFFFFF", borderRadius:16, padding:"14px 16px", boxShadow:hasAnyFuel?"0 2px 12px rgba(0,0,0,0.06)":"none", opacity:hasAnyFuel?1:0.7, border:"1px solid #F5EDE8" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6, gap:8 }}>
                  <div style={{ fontSize:16, fontWeight:800, color:hasAnyFuel?"#1A1A1A":"#9CA3AF", lineHeight:1.3, flex:1 }}>{pump.name}</div>
                  {hasAnyFuel ? (
                    <div style={{ display:"flex", alignItems:"center", gap:4, background:"#DCFCE7", borderRadius:999, padding:"3px 10px", flexShrink:0 }}>
                      <div style={{ width:14, height:14, borderRadius:"50%", background:"#16A34A", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 10 17 19 8"/></svg>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:"#16A34A" }}>AVAILABLE</span>
                    </div>
                  ) : (
                    <div style={{ background:"#FEE2E2", borderRadius:999, padding:"3px 10px", fontSize:11, fontWeight:700, color:"#EF4444", flexShrink:0 }}>NO FUEL</div>
                  )}
                </div>

                <div style={{ fontSize:13, color:"#9CA3AF", marginBottom:12 }}>
                  {pumpDistKm(pump)} km away{pumpETA(pump) ? ` • Next ${pumpETA(pump)}` : ""}
                </div>

                {hasAnyFuel ? (
                  <div style={{ display:"grid", gridTemplateColumns:`repeat(${[pp>0,dp>0,cp>0].filter(Boolean).length},1fr)`, gap:8 }}>
                    {pp > 0 && (
                      <div style={{ background:"#FFF8F5", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:9, fontWeight:700, color:"#9CA3AF", letterSpacing:"0.08em", marginBottom:4 }}>PETROL</div>
                        <div style={{ fontSize:16, fontWeight:800, color:"#C84B00" }}>In Stock</div>
                      </div>
                    )}
                    {dp > 0 && (
                      <div style={{ background:"#EFF6FF", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:9, fontWeight:700, color:"#9CA3AF", letterSpacing:"0.08em", marginBottom:4 }}>DIESEL</div>
                        <div style={{ fontSize:16, fontWeight:800, color:"#2563EB" }}>In Stock</div>
                      </div>
                    )}
                    {cp > 0 && (
                      <div style={{ background:"#F0FDF4", borderRadius:10, padding:"10px 12px" }}>
                        <div style={{ fontSize:9, fontWeight:700, color:"#9CA3AF", letterSpacing:"0.08em", marginBottom:4 }}>CNG</div>
                        <div style={{ fontSize:16, fontWeight:800, color:"#16A34A" }}>In Stock</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:9, fontWeight:700, color:"#9CA3AF", letterSpacing:"0.08em", marginBottom:4 }}>PETROL</div>
                    <div style={{ fontSize:18, fontWeight:800, color:"#EF4444" }}>Out of Stock</div>
                  </div>
                )}

                {hasAnyFuel && (
                  <button onClick={() => navigate(pump)} style={{ width:"100%", marginTop:12, padding:"10px", borderRadius:10, background:"transparent", border:"1.5px solid #E5D5CC", cursor:"pointer", fontSize:13, fontWeight:600, color:"#7B2D00", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#7B2D00"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                    Stop here
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ position:"fixed", bottom:64, left:0, right:0, padding:"12px 16px", background:"linear-gradient(transparent, #FDF3EE 40%)" }}>
        <button onClick={navigateDirect} style={{ width:"100%", height:56, borderRadius:999, background:"linear-gradient(90deg,#7B2D00 0%,#C84B00 50%,#F97316 100%)", border:"none", cursor:"pointer", fontSize:16, fontWeight:700, color:"white", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:"0 4px 20px rgba(123,45,0,0.35)" }}>
          Start Navigation
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC PAGE  — full redesign
// ─────────────────────────────────────────────────────────────────────────────
function PublicPage({ onGoToOwner }) {
  useEffect(()=>injectStyles(),[]);

  const [pumps,setPumps]         = useState([]);
  const [loading,setLoading]     = useState(true);
  const [error,setError]         = useState("");
  const [userLoc,setUserLoc]     = useState(null);
  const [locLoading,setLL]       = useState(false);
  const [filter,setFilter]       = useState("all");
  const [brandFilter,setBrandFilter] = useState("all");
  const [sortBy,setSortBy]       = useState("distance");
  const [search,setSearch]       = useState("");
  const [selected,setSelected]   = useState(null);
  const [selectedTab,setSelectedTab] = useState("fuel");
  const [lastRefresh,setLastRefresh] = useState(new Date());
  const [highwayMode,setHighwayMode] = useState(false);
  const [mapView,setMapView]         = useState(false);
  const [filterVerified,setFilterVerified] = useState(false);
  const [filterOpenNow,setFilterOpenNow]   = useState(false);
  const [showFilters,setShowFilters]       = useState(false);

  const load = useCallback(()=>{
    getPumps().then(d=>{setPumps(d||[]);setLoading(false);setLastRefresh(new Date());}).catch(()=>{setError("Could not load pump data. Check Supabase setup.");setLoading(false);});
  },[]);

  useEffect(()=>{ load(); },[load]);
  useEffect(()=>{ const t=setInterval(()=>load(),60000); return()=>clearInterval(t); },[load]);

  function requestLocation(){
    setLL(true);
    navigator.geolocation?.getCurrentPosition(
      p=>{setUserLoc({lat:p.coords.latitude,lng:p.coords.longitude});setLL(false);},
      ()=>setLL(false)
    );
  }

  const withDist = pumps.map(p=>({...p,distance:userLoc?haversine(userLoc.lat,userLoc.lng,p.lat,p.lng):null}));
  const filtered = withDist.filter(p=>{
    const q=search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q);
    if(!matchSearch) return false;
    if(brandFilter!=="all"&&p.brand!==brandFilter) return false;
    if(filterVerified && !p.wm_cert_number) return false;
    if(filterOpenNow && !getOpenStatus(p).isOpen) return false;
    if(filter==="petrol") return lvl(p.petrol,p.max_petrol)>0;
    if(filter==="diesel") return lvl(p.diesel,p.max_diesel)>0;
    if(filter==="cng")    return p.max_cng>0&&lvl(p.cng,p.max_cng)>0;
    return true;
  });
  const sorted=[...filtered].sort((a,b)=>{
    const aOpen = getOpenStatus(a).isOpen;
    const bOpen = getOpenStatus(b).isOpen;
    // Always sort open pumps above closed ones
    if(aOpen && !bOpen) return -1;
    if(!aOpen && bOpen) return 1;
    // Within same open/closed group — apply chosen sort
    if(sortBy==="distance"&&a.distance!==null) return a.distance-b.distance;
    if(sortBy==="stock") return (lvl(b.petrol,b.max_petrol)+lvl(b.diesel,b.max_diesel))-(lvl(a.petrol,a.max_petrol)+lvl(a.diesel,a.max_diesel));
    return 0;
  });

  // Find nearest open pump across ALL pumps (ignores filters) — for the safety banner
  const allOpen = [...pumps]
    .map(p=>({...p, distance:userLoc?haversine(userLoc.lat,userLoc.lng,p.lat,p.lng):null}))
    .filter(p=>getOpenStatus(p).isOpen)
    .sort((a,b)=>(a.distance||0)-(b.distance||0));
  const anyOpenInFiltered = sorted.some(p=>getOpenStatus(p).isOpen);
  const nearestOpenAnywhere = allOpen[0] || null;

  // Highway mode — full screen takeover
  if (highwayMode) return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column" }}>
      <div style={{ flex:1, overflowY:"auto" }}>
        <TravelMode onExit={() => setHighwayMode(false)}/>
      </div>
      <nav className="bottom-nav">
        {[
          { id:"home",  Icon:IC.Home,    label:"Home",   action:()=>setHighwayMode(false) },
          { id:"map",   Icon:IC.Map,     label:"Map",    action:()=>setHighwayMode(false) },
          { id:"hwy",   Icon:IC.Highway, label:"Travel", action:()=>{} },
          { id:"owner", Icon:IC.Owner,   label:"Portal", action:onGoToOwner },
        ].map(tab => {
          const active = tab.id === "hwy";
          return (
            <button key={tab.id}
              className={`bottom-nav-tab${active?" active":""}`}
              onClick={tab.action}>
              <span className="nav-icon-wrap">
                <img
                  src={
                    tab.id==="home"  ? "/design/icons/light--home-rounded.svg" :
                    tab.id==="map"   ? "/design/icons/map-light.svg" :
                    tab.id==="hwy"   ? "/design/icons/travel.svg" :
                    "/design/icons/portal-owner.svg"
                  }
                  alt={tab.label}
                  style={{
                    width:24, height:24,
                    filter: active
                      ? "brightness(0) saturate(100%) invert(15%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(80%)"
                      : "brightness(0) saturate(100%) invert(65%) sepia(0%) saturate(0%) brightness(90%)",
                  }}
                />
              </span>
              <span className="nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(180deg,#FDE8D8 0%,#FDF3EE 30%,#FDF3EE 100%)", fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:80 }}>

      {/* ── HEADER ── */}
      <header style={{ background:"rgba(253,243,238,0.85)", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Fuel station building icon */}
          <img src="/design/icons/gas-station.svg" alt="FuelDost"
            style={{ width:24, height:24, filter:"brightness(0) saturate(100%) invert(15%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(80%)" }}/>
          <span style={{ fontSize:20, fontWeight:700, color:"#7B2D00", letterSpacing:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>FUELDOST</span>
        </div>
        <button
          onClick={()=>setShowFilters(v=>!v)}
          style={{ background:"none", border:"none", cursor:"pointer", padding:4, display:"flex", alignItems:"center" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round">
            <line x1="4" y1="5" x2="20" y2="5"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="19" x2="20" y2="19"/>
            <circle cx="8" cy="5" r="2.5" fill="white" stroke="#1A1A1A" strokeWidth="1.8"/>
            <circle cx="16" cy="12" r="2.5" fill="white" stroke="#1A1A1A" strokeWidth="1.8"/>
            <circle cx="8" cy="19" r="2.5" fill="white" stroke="#1A1A1A" strokeWidth="1.8"/>
          </svg>
        </button>
      </header>

      {/* ── MAIN CONTENT ── */}
      <div style={{ padding:"0 16px" }}>

        {/* Setup warning */}
        {SUPABASE_URL==="YOUR_SUPABASE_URL"&&(
          <div style={{ background:"#FFFBEB",border:"1.5px solid rgba(249,115,22,0.3)",borderRadius:12,padding:"10px 14px",marginBottom:12,fontSize:13,color:"#C84B00",display:"flex",gap:8,alignItems:"flex-start" }}>
            <span style={{ flexShrink:0 }}>⚙️</span>
            <span><strong>Setup needed:</strong> Replace YOUR_SUPABASE_URL and YOUR_SUPABASE_ANON_KEY at the top of the file.</span>
          </div>
        )}

        {/* ── SEARCH BAR ── */}
        <div className="search-bar-wrap" style={{ marginBottom:12, marginTop:12 }}>
          <IC.Search size={18} color="#9CA3AF"/>
          <input
            className="search-input"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Search petrol pumps..."
            autoComplete="off" autoCorrect="off" spellCheck={false}
          />
          <button
            onClick={requestLocation}
            disabled={locLoading}
            title="Near Me"
            style={{ width:36, height:36, borderRadius:"50%", background:"#FFFFFF", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:"0 1px 4px rgba(0,0,0,0.15)" }}>
            {locLoading
              ? <div style={{ width:14,height:14,border:"2px solid #7B2D00",borderTopColor:"transparent",borderRadius:"50%",animation:"spin-slow 0.8s linear infinite" }}/>
              : <IC.Crosshair size={15} color="#7B2D00"/>
            }
          </button>
        </div>

        {/* ── FILTER PILLS ── */}
        <div className="chips-scroll" style={{ marginBottom:16, padding:"4px 0" }}>
          {/* Near Me */}
          <button
            onClick={requestLocation}
            style={{ height:36, borderRadius:999, padding:"0 14px", fontSize:14, fontWeight:500, border:"none", display:"flex", alignItems:"center", gap:6, cursor:"pointer", background: userLoc ? "linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)" : "linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)", color:"#FFFFFF", flexShrink:0, whiteSpace:"nowrap", fontFamily:"inherit" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
            Near Me
            {userLoc && <span style={{ width:6,height:6,background:"#4ADE80",borderRadius:"50%",flexShrink:0 }}/>}
          </button>
          {/* Verified */}
          <button
            onClick={()=>setFilterVerified(v=>!v)}
            style={{ height:36, borderRadius:999, padding:"0 14px", fontSize:14, fontWeight:500, cursor:"pointer", display:"flex", alignItems:"center", gap:6, background:filterVerified?"linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)":"#F5EDE8", color:filterVerified?"#FFFFFF":"#7B2D00", border:filterVerified?"none":"1px solid #E5D5CC", flexShrink:0, whiteSpace:"nowrap", fontFamily:"inherit" }}>
            <IC.Shield size={13} color={filterVerified?"white":"#7B2D00"}/>
            Verified
          </button>
          {/* Open Now */}
          <button
            onClick={()=>setFilterOpenNow(v=>!v)}
            style={{ height:36, borderRadius:999, padding:"0 14px", fontSize:14, fontWeight:500, cursor:"pointer", display:"flex", alignItems:"center", gap:6, background:filterOpenNow?"linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)":"#F5EDE8", color:filterOpenNow?"#FFFFFF":"#7B2D00", border:filterOpenNow?"none":"1px solid #E5D5CC", flexShrink:0, whiteSpace:"nowrap", fontFamily:"inherit" }}>
            <IC.Clock size={13} color={filterOpenNow?"white":"#7B2D00"}/>
            Open Now
          </button>
          {/* Fuel type pills */}
          {["petrol","diesel","cng"].map(f=>(
            <button key={f}
              onClick={()=>setFilter(prev=>prev===f?"all":f)}
              style={{ height:36, borderRadius:999, padding:"0 14px", fontSize:14, fontWeight:500, cursor:"pointer", background:filter===f?"linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)":"#F5EDE8", color:filter===f?"#FFFFFF":"#7B2D00", border:filter===f?"none":"1px solid #E5D5CC", flexShrink:0, whiteSpace:"nowrap", fontFamily:"inherit" }}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>

        {/* Advanced filters panel */}
        {showFilters&&(
          <div style={{ background:"#FFFFFF", borderRadius:16, padding:"14px 16px", marginBottom:16, boxShadow:"0 2px 12px rgba(0,0,0,0.08)", animation:"fadeUp 0.2s ease" }}>
            <div style={{ fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10 }}>Brand Filter</div>
            <div className="chips-scroll" style={{ marginBottom:14 }}>
              {BRANDS.map(b=>(
                <button key={b.id} className={`brand-chip${brandFilter===b.id?" active":""}`}
                  style={{ "--bc":b.color,"--bb":b.bg }}
                  onClick={()=>setBrandFilter(b.id)}>
                  {brandFilter===b.id&&b.id!=="all"&&<div className="brand-dot"/>}
                  {b.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10 }}>Sort By</div>
            <div style={{ display:"flex",gap:8 }}>
              {[["distance","📍 Nearest"],["stock","📊 Best Stock"]].map(([k,l])=>(
                <button key={k} className="sort-btn" onClick={()=>setSortBy(k)}
                  style={{ flex:1,border:`1.5px solid ${sortBy===k?"#C84B00":"#E5D5CC"}`,background:sortBy===k?"rgba(200,75,0,0.08)":"#F5EDE8",color:sortBy===k?"#C84B00":"#6B7280",borderRadius:999,padding:"8px 12px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit" }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",textTransform:"uppercase",margin:"14px 0 6px" }}>Map View</div>
            <button
              onClick={()=>setMapView(v=>!v)}
              style={{ display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600,color:mapView?"#C84B00":"#6B7280",background:mapView?"rgba(200,75,0,0.08)":"#F5EDE8",border:`1.5px solid ${mapView?"#C84B00":"#E5D5CC"}`,borderRadius:999,padding:"8px 16px",cursor:"pointer",fontFamily:"inherit" }}>
              {mapView?(
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              ):(
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
              )}
              {mapView ? "Show Cards" : "Show Map"}
            </button>
          </div>
        )}

        {/* Results count */}
        {!loading&&!error&&(
          <div style={{ fontSize:12,color:"#9CA3AF",marginBottom:12,fontWeight:500 }}>
            {sorted.length===0?"No pumps match your search":`${sorted.length} pump${sorted.length!==1?"s":""}`}
            {search&&<span style={{ color:"#C84B00" }}> matching "{search}"</span>}
          </div>
        )}
      </div>

      {/* ── MAP VIEW ── */}
      {mapView&&(
        <MapView pumps={sorted} userLoc={userLoc} onSelectPump={(pump)=>{setSelected(pump);setSelectedTab("fuel");setMapView(false);}}/>
      )}

      {/* ── CARDS ── */}
      {!mapView&&<div style={{ padding:"0 16px 80px" }}>

        {/* All nearby pumps closed warning */}
        {!loading&&!error&&sorted.length>0&&!anyOpenInFiltered&&(
          <div style={{ background:"rgba(200,75,0,0.06)",border:"1.5px solid rgba(200,75,0,0.25)",borderRadius:14,padding:"14px 16px",marginBottom:16,animation:"fadeUp 0.4s ease" }}>
            <div style={{ display:"flex",alignItems:"flex-start",gap:10 }}>
              <span style={{ fontSize:20,flexShrink:0 }}>🌙</span>
              <div>
                <div style={{ fontSize:13,fontWeight:800,color:"#C84B00",marginBottom:4 }}>All nearby pumps are currently closed</div>
                <div style={{ fontSize:12,color:"#92400E",lineHeight:1.6 }}>
                  The pumps below show their reopening times.
                  {nearestOpenAnywhere&&<> The nearest open pump right now is <strong>{nearestOpenAnywhere.name}</strong>
                  {nearestOpenAnywhere.distance&&<span> ({nearestOpenAnywhere.distance<1?(nearestOpenAnywhere.distance*1000).toFixed(0)+"m":nearestOpenAnywhere.distance.toFixed(1)+"km"} away)</span>}.</>}
                </div>
                {nearestOpenAnywhere&&(
                  <button
                    onClick={()=>{setSelected(nearestOpenAnywhere);setSelectedTab("fuel");}}
                    style={{ marginTop:10,display:"inline-flex",alignItems:"center",gap:6,background:"#C84B00",border:"none",borderRadius:9,padding:"8px 14px",fontSize:12,fontWeight:700,color:"#fff",cursor:"pointer",fontFamily:"inherit" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                    View nearest open pump
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {error&&(
          <div style={{ background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:14,padding:"14px 16px",color:"#DC2626",fontSize:13,marginBottom:16,display:"flex",gap:8,alignItems:"flex-start" }}>
            <span style={{ flexShrink:0 }}>⚠️</span> {error}
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr" }}>
          {loading
            ? Array.from({length:3}).map((_,i)=><SkeletonCard key={i}/>)
            : sorted.map((p,i)=><PumpCard key={p.id} pump={p} distance={p.distance} onClick={(pump,tab)=>{setSelected(pump);setSelectedTab(tab||"fuel");}} onProductClick={(pump)=>{setSelected(pump);setSelectedTab("products");}} animDelay={i*0.05}/>)
          }
        </div>

        {!loading&&sorted.length===0&&!error&&(
          <div style={{ textAlign:"center",padding:"48px 20px",animation:"fadeUp 0.4s ease" }}>
            <img src="/design/icons/gas-station-not-found.svg" alt="No pumps found"
              style={{ width:80, height:80, filter:"brightness(0) saturate(100%) invert(55%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(100%)", marginBottom:12 }}/>
            <div style={{ fontSize:17,fontWeight:700,color:"#1A1A1A",marginBottom:6,fontFamily:"'Outfit',sans-serif" }}>No pumps found</div>
            <div style={{ fontSize:13,color:"#9CA3AF" }}>Try a different filter or clear your search</div>
          </div>
        )}

        {/* Footer note */}
        {!loading&&sorted.length>0&&(
          <div style={{ textAlign:"center",paddingTop:8,paddingBottom:8 }}>
            <div style={{ fontSize:11,color:"#9CA3AF",lineHeight:1.9 }}>
              Auto-refreshes every 60s &nbsp;·&nbsp;
              <span style={{ color:"#C84B00",cursor:"pointer" }} onClick={onGoToOwner}>Pump owner? Login here</span>
            </div>
          </div>
        )}
      </div>}

      {/* ── DETAIL MODAL ── */}
      {selected&&<PumpModal pump={selected} onClose={()=>setSelected(null)} initialTab={selectedTab}/>}

      {/* ── FLOATING ACTION BUTTON ── */}
      <button
        onClick={()=>setShowFilters(v=>!v)}
        style={{ position:"fixed", bottom:80, right:16, width:52, height:52, borderRadius:"50%", background:"linear-gradient(135deg,#7B2D00 0%,#C84B00 100%)", boxShadow:"0 4px 16px rgba(123,45,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", border:"none", cursor:"pointer", zIndex:49 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
          <line x1="4" y1="5" x2="20" y2="5"/>
          <line x1="4" y1="12" x2="20" y2="12"/>
          <line x1="4" y1="19" x2="20" y2="19"/>
          <circle cx="8" cy="5" r="2.3" fill="rgba(123,45,0,0.6)" stroke="white" strokeWidth="1.6"/>
          <circle cx="16" cy="12" r="2.3" fill="rgba(123,45,0,0.6)" stroke="white" strokeWidth="1.6"/>
          <circle cx="8" cy="19" r="2.3" fill="rgba(123,45,0,0.6)" stroke="white" strokeWidth="1.6"/>
        </svg>
      </button>

      {/* ── BOTTOM NAV ── */}
      <nav className="bottom-nav">
        {[
          { id:"home",  Icon:IC.Home,    label:"Home",   action:()=>{setMapView(false);setHighwayMode(false);setShowFilters(false);} },
          { id:"map",   Icon:IC.Map,     label:"Map",    action:()=>{setMapView(true); setHighwayMode(false);setShowFilters(false);} },
          { id:"hwy",   Icon:IC.Highway, label:"Travel", action:()=>setHighwayMode(true) },
          { id:"owner", Icon:IC.Owner,   label:"Portal", action:onGoToOwner },
        ].map(tab=>{
          const active = tab.id==="map"?mapView:tab.id==="home"?(!mapView&&!highwayMode):tab.id==="hwy"?highwayMode:false;
          return(
            <button key={tab.id} className={`bottom-nav-tab${active?" active":""}`} onClick={tab.action}>
              <span className="nav-icon-wrap">
                <img
                  src={
                    tab.id === "home"  ? "/design/icons/light--home-rounded.svg" :
                    tab.id === "map"   ? "/design/icons/map-light.svg" :
                    tab.id === "hwy"   ? "/design/icons/travel.svg" :
                    "/design/icons/portal-owner.svg"
                  }
                  alt={tab.label}
                  style={{
                    width:24, height:24,
                    filter: active
                      ? "brightness(0) saturate(100%) invert(15%) sepia(80%) saturate(800%) hue-rotate(345deg) brightness(80%)"
                      : "brightness(0) saturate(100%) invert(65%) sepia(0%) saturate(0%) brightness(90%)",
                    transition:"filter 0.15s",
                  }}
                />
              </span>
              <span className="nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DARK FUEL BAR (owner/admin — keep dark portals)
// ─────────────────────────────────────────────────────────────────────────────
function FuelBar({ label, current, max }) {
  const pct=max?Math.min(100,Math.round((current/max)*100)):0, color=sColor(pct);
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
        <span style={{ fontSize:11,fontWeight:700,letterSpacing:"0.08em",color:"#94a3b8",textTransform:"uppercase" }}>{label}</span>
        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
          <span style={{ fontSize:13,fontWeight:700,color:"#f1f5f9" }}>{max?fmtL(current):"N/A"}</span>
          {max>0&&<span style={{ fontSize:10,background:color+"22",color,border:`1px solid ${color}44`,borderRadius:4,padding:"1px 6px",fontWeight:700 }}>{pct}%</span>}
        </div>
      </div>
      {max>0&&<div style={{ height:6,background:"#1e293b",borderRadius:99,overflow:"hidden" }}>
        <div style={{ width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${color}88,${color})`,borderRadius:99,transition:"width 0.8s" }}/>
      </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DARK MODAL (owner/admin)
// ─────────────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, width=480 }) {
  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"#000b",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:20,padding:28,width:"100%",maxWidth:width,boxShadow:"0 40px 100px #000",maxHeight:"90vh",overflowY:"auto" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
          <div style={{ fontSize:16,fontWeight:800,color:"#f1f5f9" }}>{title}</div>
          <button onClick={onClose} style={{ background:"#1e293b",border:"none",color:"#94a3b8",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:11,fontWeight:700,letterSpacing:"0.08em",color:"#94a3b8",textTransform:"uppercase",display:"block",marginBottom:6 }}>{label}</label>
      {children}
    </div>
  );
}
const inpStyle = { width:"100%",boxSizing:"border-box",background:"#0a1628",border:"1px solid #1e293b",borderRadius:9,padding:"10px 13px",fontSize:13,color:"#f1f5f9",outline:"none",fontFamily:"inherit" };


// ─────────────────────────────────────────────────────────────────────────────
// PRICING PAGE
// ─────────────────────────────────────────────────────────────────────────────
function PricingPage({ onBack }) {
  useEffect(()=>injectStyles(),[]);
  const basic = [
    "Live fuel stock display (Petrol, Diesel, CNG)",
    "Listed on FuelStock public map & card view",
    "Opening hours display (full weekly schedule)",
    "W&M certificate verification badge ✦",
    "Directions button for customers",
    "14-day free trial — no credit card needed",
  ];
  const pro = [
    "Everything in Basic",
    "Product listing (oils, additives, accessories)",
    "Directions click analytics — daily & weekly trends",
    "Fuel type breakdown (what customers searched for)",
    "Highway mode priority listing ⭐",
    "Premium fuel grades display (Power Petrol, XP100, V-Power…)",
    "Monthly analytics report",
    "14-day free trial — no credit card needed",
  ];
  return (
    <div style={{ minHeight:"100vh",background:"#FFFBF5",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <header style={{ background:"rgba(255,251,245,0.95)",backdropFilter:"blur(12px)",borderBottom:"1px solid #F3EDE4",padding:"0 16px",position:"sticky",top:0,zIndex:50 }}>
        <div style={{ maxWidth:960,margin:"0 auto",padding:"12px 0",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ width:36,height:36,background:"linear-gradient(135deg,#F59E0B,#D97706)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17 }}>⛽</div>
            <div style={{ fontSize:17,fontWeight:900,color:"#1C1917",fontFamily:"'Outfit',sans-serif" }}>FuelStock</div>
          </div>
          <button onClick={onBack} style={{ fontSize:12,fontWeight:700,color:"#78716C",background:"#F5F0EB",border:"1px solid #E8DDD0",borderRadius:10,padding:"8px 14px",cursor:"pointer",fontFamily:"inherit" }}>← Back</button>
        </div>
      </header>

      <div style={{ maxWidth:720,margin:"0 auto",padding:"40px 16px 80px" }}>
        {/* Hero */}
        <div style={{ textAlign:"center",marginBottom:40 }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:99,padding:"5px 14px",fontSize:11,fontWeight:700,color:"#D97706",marginBottom:16 }}>
            ✨ Simple, transparent pricing
          </div>
          <h1 style={{ fontSize:"clamp(26px,5vw,40px)",fontWeight:900,color:"#1C1917",lineHeight:1.1,marginBottom:12,fontFamily:"'Outfit',sans-serif",letterSpacing:"-0.03em" }}>
            List your pump on FuelStock
          </h1>
          <p style={{ fontSize:14,color:"#78716C",maxWidth:400,margin:"0 auto",lineHeight:1.6 }}>
            Both plans include a 14-day free trial. No credit card required. Cancel anytime.
          </p>
        </div>

        {/* Cards */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:32 }}>

          {/* Basic */}
          <div style={{ background:"#fff",border:"2px solid #E8DDD0",borderRadius:24,padding:"28px 24px" }}>
            <div style={{ fontSize:13,fontWeight:700,color:"#78716C",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Basic</div>
            <div style={{ display:"flex",alignItems:"flex-end",gap:4,marginBottom:6 }}>
              <span style={{ fontSize:42,fontWeight:900,color:"#1C1917",fontFamily:"'Outfit',sans-serif",lineHeight:1 }}>₹99</span>
              <span style={{ fontSize:14,color:"#A8A29E",marginBottom:6 }}>/month</span>
            </div>
            <div style={{ fontSize:12,color:"#22C55E",fontWeight:600,marginBottom:24,background:"#DCFCE7",borderRadius:8,padding:"4px 10px",display:"inline-block" }}>14-day free trial</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10,marginBottom:28 }}>
              {basic.map((f,i)=>(
                <div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start" }}>
                  <div style={{ width:18,height:18,background:"#DCFCE7",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <span style={{ fontSize:13,color:"#44403C",lineHeight:1.5 }}>{f}</span>
                </div>
              ))}
            </div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I want to list my petrol pump on FuelStock on the Basic plan (₹99/month). Please contact me.")}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display:"block",textAlign:"center",padding:"13px",background:"#F5F0EB",border:"2px solid #E8DDD0",borderRadius:12,fontSize:14,fontWeight:800,color:"#78716C",textDecoration:"none",transition:"all 0.15s" }}>
              Get Started →
            </a>
          </div>

          {/* Pro */}
          <div style={{ background:"linear-gradient(145deg,#1C1917,#292524)",border:"2px solid #44403C",borderRadius:24,padding:"28px 24px",position:"relative",overflow:"hidden" }}>
            <div style={{ position:"absolute",top:0,right:0,background:"linear-gradient(135deg,#F59E0B,#D97706)",padding:"6px 16px",borderRadius:"0 24px 0 14px",fontSize:11,fontWeight:800,color:"#fff" }}>POPULAR</div>
            <div style={{ position:"absolute",top:-40,left:-40,width:140,height:140,background:"radial-gradient(circle,#F59E0B22,transparent 70%)",borderRadius:"50%",pointerEvents:"none" }}/>
            <div style={{ fontSize:13,fontWeight:700,color:"#F59E0B",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>Pro ⭐</div>
            <div style={{ display:"flex",alignItems:"flex-end",gap:4,marginBottom:6 }}>
              <span style={{ fontSize:42,fontWeight:900,color:"#F9FAFB",fontFamily:"'Outfit',sans-serif",lineHeight:1 }}>₹299</span>
              <span style={{ fontSize:14,color:"#78716C",marginBottom:6 }}>/month</span>
            </div>
            <div style={{ fontSize:12,color:"#22C55E",fontWeight:600,marginBottom:24,background:"#22c55e18",borderRadius:8,padding:"4px 10px",display:"inline-block",border:"1px solid #22c55e33" }}>14-day free trial</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10,marginBottom:28 }}>
              {pro.map((f,i)=>(
                <div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start" }}>
                  <div style={{ width:18,height:18,background:"#F59E0B22",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,border:"1px solid #F59E0B44" }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <span style={{ fontSize:13,color:"#E7E5E4",lineHeight:1.5 }}>{f}</span>
                </div>
              ))}
            </div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I want to list my petrol pump on FuelStock on the Pro plan (₹299/month). Please contact me.")}`}
              target="_blank" rel="noopener noreferrer"
              style={{ display:"block",textAlign:"center",padding:"13px",background:"linear-gradient(135deg,#F59E0B,#D97706)",border:"none",borderRadius:12,fontSize:14,fontWeight:800,color:"#fff",textDecoration:"none",boxShadow:"0 4px 16px rgba(217,119,6,0.4)" }}>
              Get Started →
            </a>
          </div>
        </div>

        {/* W&M legal note */}
        <div style={{ background:"#F0FDF4",border:"1.5px solid #86EFAC",borderRadius:16,padding:"16px 20px",marginBottom:24,display:"flex",gap:12,alignItems:"flex-start" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0,marginTop:1 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
          <div>
            <div style={{ fontSize:12,fontWeight:800,color:"#15803D",marginBottom:4 }}>✦ W&M Certificate — included on both plans</div>
            <div style={{ fontSize:12,color:"#166534",lineHeight:1.6 }}>
              Weights & Measures certification display is available to every listed pump regardless of plan. It is a legal requirement under the Legal Metrology Act, 2009 and every certified pump has the right to display it. FuelStock treats this as a basic consumer right, not a premium feature.
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ background:"#fff",border:"1.5px solid #F3EDE4",borderRadius:20,padding:"24px",marginBottom:24 }}>
          <div style={{ fontSize:15,fontWeight:800,color:"#1C1917",marginBottom:20,fontFamily:"'Outfit',sans-serif" }}>Common Questions</div>
          {[
            ["Do I need a credit card to start?","No. Both plans start with a 14-day free trial. You only pay after the trial ends if you decide to continue."],
            ["How do I update my fuel stock?","Log into the Owner Portal with your email and password. Update the sliders for each fuel type and tap Publish. Takes under a minute."],
            ["Can I upgrade from Basic to Pro later?","Yes — contact FuelStock admin on WhatsApp at any time. Your data and settings carry over."],
            ["What is highway priority?","Pro pumps appear first in Highway Mode — the full-screen view designed for drivers on the road. Basic pumps still appear, just below Pro pumps."],
          ].map(([q,a],i)=>(
            <div key={i} style={{ borderTop:i>0?"1px solid #F3EDE4":"none",paddingTop:i>0?16:0,marginTop:i>0?16:0 }}>
              <div style={{ fontSize:13,fontWeight:700,color:"#1C1917",marginBottom:6 }}>{q}</div>
              <div style={{ fontSize:13,color:"#78716C",lineHeight:1.6 }}>{a}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:13,color:"#A8A29E",marginBottom:12 }}>Questions? Reach us on WhatsApp</div>
          <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I have a question about FuelStock pricing.")}`}
            target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-flex",alignItems:"center",gap:8,background:"#25D366",borderRadius:12,padding:"12px 24px",fontSize:14,fontWeight:700,color:"#fff",textDecoration:"none" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Chat with us on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN PAGE  (shared owner + admin — stays dark/professional)
// ─────────────────────────────────────────────────────────────────────────────
function LoginPage({ title, subtitle, accentColor="#f59e0b", onLogin, onBack, isAdmin=false }) {
  const [email,setEmail]     = useState(isAdmin?ADMIN_EMAIL:"");
  const [password,setPassword] = useState("");
  const [error,setError]     = useState("");
  const [loading,setLoading] = useState(false);

  async function handleSubmit(){
    if(!email||!password){setError("Please enter email and password");return;}
    setLoading(true);setError("");
    try{
      const data=await loginApi(email,password);
      if(isAdmin){
        if(email!==ADMIN_EMAIL)throw new Error("Not authorised as admin");
        // Store token globally so Edge Function calls always have it fresh
        window.__fuelstockAdminToken   = data.access_token;
        window.__fuelstockRefreshToken = data.refresh_token;
        onLogin(data.access_token,email);
      }else{
        const pump=await getMyPump(email,data.access_token);
        if(!pump){setError("No pump found for this account.");setLoading(false);return;}
        onLogin(data.access_token,email,pump);
      }
    }catch(e){setError(e.message.includes("Invalid")?"Invalid email or password":e.message);}
    setLoading(false);
  }

  return(
    <div style={{ minHeight:"100vh",background:"#070d1a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ width:"100%",maxWidth:400 }}>
        <div style={{ textAlign:"center",marginBottom:32 }}>
          <div style={{ width:56,height:56,background:`linear-gradient(135deg,${accentColor},${accentColor}cc)`,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",boxShadow:`0 8px 24px ${accentColor}44` }}>
                {isAdmin?<IC.Shield size={26} color="white"/>:<IC.FuelDrop size={26} color="white"/>}
              </div>
          <div style={{ fontSize:24,fontWeight:900,color:"#f1f5f9",fontFamily:"'Outfit',sans-serif" }}>{title}</div>
          <div style={{ fontSize:13,color:"#475569",marginTop:6 }}>{subtitle}</div>
        </div>
        <div style={{ background:"#0f172a",border:"1px solid #1e293b",borderRadius:20,padding:28 }}>
          <Field label="Email"><input value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" style={inpStyle} onFocus={e=>e.target.style.borderColor=accentColor} onBlur={e=>e.target.style.borderColor="#1e293b"}/></Field>
          <Field label="Password"><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" style={inpStyle} onFocus={e=>e.target.style.borderColor=accentColor} onBlur={e=>e.target.style.borderColor="#1e293b"} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/></Field>
          {error&&<div style={{ fontSize:13,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444433",borderRadius:8,padding:"10px 14px",marginBottom:16 }}>⚠️ {error}</div>}
          <button onClick={handleSubmit} disabled={loading} style={{ width:"100%",padding:13,background:`linear-gradient(135deg,${accentColor},${accentColor}cc)`,border:"none",borderRadius:10,fontSize:14,fontWeight:800,color:"#0a0a0a",cursor:"pointer",opacity:loading?0.6:1,fontFamily:"inherit" }}>{loading?"Verifying...":"Log In →"}</button>
        </div>
        {onBack&&<div style={{ textAlign:"center",marginTop:18 }}><button onClick={onBack} style={{ fontSize:12,color:"#475569",background:"none",border:"none",cursor:"pointer" }}>← Back to public dashboard</button></div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS PANEL
// ─────────────────────────────────────────────────────────────────────────────
function AnalyticsPanel({ rows }) {
  if(!rows||rows.length===0) return(
    <div style={{ textAlign:"center",padding:"28px 20px",color:"#475569" }}>
      <div style={{ fontSize:36,marginBottom:10 }}>📊</div>
      <div style={{ fontSize:14,fontWeight:700,color:"#64748b",marginBottom:4 }}>No clicks yet</div>
      <div style={{ fontSize:12,color:"#334155" }}>Directions clicks appear here once customers start tapping the Directions button on your pump.</div>
    </div>
  );
  const days=Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(6-i));return d.toISOString().slice(0,10);});
  const byDate={};
  days.forEach(d=>{byDate[d]={petrol:0,diesel:0,cng:0,total:0};});
  rows.forEach(r=>{if(byDate[r.date]){byDate[r.date][r.fuel_type]=(byDate[r.date][r.fuel_type]||0)+1;byDate[r.date].total+=1;}});
  const maxDay=Math.max(...days.map(d=>byDate[d].total),1),total=rows.length,today=byDate[days[6]]?.total||0;
  const ft={petrol:0,diesel:0,cng:0};
  rows.forEach(r=>{if(ft[r.fuel_type]!==undefined)ft[r.fuel_type]++;});
  const fC={petrol:"#22c55e",diesel:"#3b82f6",cng:"#8b5cf6"};
  return(
    <div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20 }}>
        {[{l:"Today",v:today,c:"#f59e0b"},{l:"This Week",v:total,c:"#0ea5e9"},{l:"Daily Avg",v:Math.round(total/7),c:"#22c55e"}].map(s=>(
          <div key={s.l} style={{ background:"#0a1628",border:`1px solid ${s.c}22`,borderRadius:12,padding:"14px 12px",textAlign:"center" }}>
            <div style={{ fontSize:26,fontWeight:900,color:s.c,lineHeight:1.1 }}>{s.v}</div>
            <div style={{ fontSize:10,color:"#475569",marginTop:3 }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ background:"#0a1628",borderRadius:14,padding:"18px 16px",marginBottom:16 }}>
        <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:16 }}>Directions Clicks — Last 7 Days</div>
        <div style={{ display:"flex",alignItems:"flex-end",gap:5,height:100 }}>
          {days.map(date=>{
            const d=byDate[date],isT=date===days[6],bH=Math.max((d.total/maxDay)*72,d.total>0?6:3);
            return(
              <div key={date} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,height:"100%",justifyContent:"flex-end" }}>
                {d.total>0&&<div style={{ fontSize:10,fontWeight:800,color:isT?"#f59e0b":"#64748b" }}>{d.total}</div>}
                <div style={{ width:"100%",height:bH,borderRadius:5,overflow:"hidden",display:"flex",flexDirection:"column-reverse" }}>
                  {d.total===0?<div style={{ flex:1,background:"#1e293b" }}/>:["petrol","diesel","cng"].map(f=>d[f]?<div key={f} style={{ width:"100%",flex:d[f],background:fC[f] }}/>:null)}
                </div>
                <div style={{ fontSize:9,color:isT?"#f59e0b":"#475569",fontWeight:isT?700:400,textAlign:"center",lineHeight:1.2,whiteSpace:"nowrap" }}>{dayLabel(date)}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex",gap:14,marginTop:14,justifyContent:"center" }}>
          {Object.entries(fC).map(([f,c])=>(<div key={f} style={{ display:"flex",alignItems:"center",gap:4 }}><div style={{ width:8,height:8,borderRadius:2,background:c }}/><span style={{ fontSize:10,color:"#64748b",textTransform:"capitalize" }}>{f}</span></div>))}
        </div>
      </div>
      <div style={{ background:"#0a1628",borderRadius:14,padding:16,marginBottom:14 }}>
        <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:14 }}>What Fuel Customers Needed</div>
        {Object.entries(ft).map(([f,count])=>{
          const pct=total>0?Math.round((count/total)*100):0,c=fC[f];
          return(<div key={f} style={{ marginBottom:14 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}><span style={{ fontSize:13,fontWeight:700,color:"#cbd5e1",textTransform:"capitalize" }}>{f}</span><div style={{ display:"flex",gap:8,alignItems:"center" }}><span style={{ fontSize:13,fontWeight:800,color:c }}>{count} clicks</span><span style={{ fontSize:11,color:"#475569" }}>{pct}%</span></div></div>
            <div style={{ height:6,background:"#1e293b",borderRadius:99,overflow:"hidden" }}><div style={{ width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${c}88,${c})`,borderRadius:99,transition:"width 0.8s" }}/></div>
          </div>);
        })}
      </div>
      <div style={{ padding:"10px 14px",background:"#0a1628",border:"1px solid #1e293b33",borderRadius:10,fontSize:11,color:"#475569",lineHeight:1.7 }}>
        💡 Each click = one customer who tapped Directions to your pump. Each click = one customer who tapped Directions to navigate to your pump.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OWNER DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function OwnerDashboard({ token, email, pump:initialPump, onLogout }) {
  const [pump,setPump]       = useState(initialPump);
  const [petrol,setPetrol]   = useState(initialPump.petrol);
  const [diesel,setDiesel]   = useState(initialPump.diesel);
  const [cng,setCng]         = useState(initialPump.cng);
  const [saving,setSaving]   = useState(false);
  const [saved,setSaved]     = useState(false);
  const [saveErr,setSaveErr] = useState("");
  const [tab,setTab]         = useState("stock");
  const [clickRows,setClickRows] = useState(null);
  // Premium fuel state — initialise from pump data
  const premiumDefs = getPremiumFuels(initialPump.brand);
  const [premiumVals,setPremiumVals] = useState(
    () => Object.fromEntries(premiumDefs.map(pf=>[pf.key, initialPump[pf.key]||0]))
  );
  // Hours state — initialise from pump data
  const [streak,setStreak] = useState(null);
  useEffect(()=>{
    // Load streak — how many consecutive days updated
    getUpdateLog(30,token).then(rows=>{
      if(!rows?.length){ setStreak(0); return; }
      const myRows=rows.filter(r=>r.pump_id===initialPump.id);
      let s=0;
      const today=new Date();
      for(let i=0;i<30;i++){
        const d=new Date(today); d.setDate(d.getDate()-i);
        const ds=d.toISOString().slice(0,10);
        if(myRows.some(r=>r.date===ds)) s++;
        else break;
      }
      setStreak(s);
    }).catch(()=>setStreak(0));
  },[]);

  const [hoursData,setHoursData] = useState(()=>{
    const h={};
    DAYS.forEach(day=>{
      const key=`hours_${day}`;
      try{ h[day]=initialPump[key]?JSON.parse(initialPump[key]):{...DEFAULT_HOURS}; }
      catch{ h[day]={...DEFAULT_HOURS}; }
    });
    return h;
  });
  const [hoursSaving,setHoursSaving]=useState(false);
  const [hoursSaved,setHoursSaved]=useState(false);

  async function handleSaveHours(){
    setHoursSaving(true);
    try{
      const payload={};
      DAYS.forEach(day=>{ payload[`hours_${day}`]=JSON.stringify(hoursData[day]); });
      const u=await sb(`/rest/v1/pumps?id=eq.${pump.id}`,{method:"PATCH",headers:{...authHdrs(token),"Prefer":"return=representation"},body:JSON.stringify(payload)});
      if(u?.[0])setPump(u[0]);
      setHoursSaved(true);setTimeout(()=>setHoursSaved(false),3000);
    }catch{} 
    setHoursSaving(false);
  }

  // Shop tab state
  const [shopProducts,setShopProducts] = useState(null);
  const [shopSales,setShopSales]       = useState(null);
  const [shopLoading,setShopLoading]   = useState(false);
  const [showAddProduct,setShowAddProduct] = useState(false);
  const [editingProduct,setEditingProduct] = useState(null);
  const [saleFlash,setSaleFlash]       = useState(null); // product id that just sold

  function reloadShop(){
    setShopLoading(true);
    Promise.all([
      ownerGetProducts(pump.id, token),
      getOwnerSales(pump.id, token),
    ]).then(([prods, sales])=>{
      setShopProducts(prods||[]);
      setShopSales(sales||[]);
      setShopLoading(false);
    }).catch(()=>setShopLoading(false));
  }

  useEffect(()=>{
    if(tab==="analytics"&&clickRows===null){
      getOwnerClicks(pump.id,token).then(r=>setClickRows(r||[])).catch(()=>setClickRows([]));
    }
    if(tab==="shop"&&shopProducts===null){ reloadShop(); }
  },[tab]);

  // Record a sale — deduct 1 from stock, log sale
  async function handleSell(product){
    if(product.stock_qty!==null&&product.stock_qty<=0) return;
    setSaleFlash(product.id);
    setTimeout(()=>setSaleFlash(null), 800);
    try {
      const newQty = product.stock_qty!==null ? Math.max(0, product.stock_qty-1) : null;
      await Promise.all([
        ownerUpdateProduct(product.id, { stock_qty: newQty, in_stock: newQty===null||newQty>0 }, token),
        recordSale({ pump_id:pump.id, product_id:product.id, product_name:product.name, qty:1, price_at_sale:product.price, sold_at:new Date().toISOString() }, token),
      ]);
      setShopProducts(prev=>prev.map(p=>p.id===product.id?{...p,stock_qty:newQty,in_stock:newQty===null||newQty>0}:p));
    } catch(e){ console.error(e); }
  }

  // Restock — add quantity
  async function handleRestock(product, qty){
    const newQty = (product.stock_qty||0) + qty;
    try {
      await ownerUpdateProduct(product.id, { stock_qty:newQty, in_stock:true }, token);
      setShopProducts(prev=>prev.map(p=>p.id===product.id?{...p,stock_qty:newQty,in_stock:true}:p));
    } catch(e){ console.error(e); }
  }

  async function handleSave(){
    setSaving(true);setSaveErr("");setSaved(false);
    try{
      const cleanPremium = Object.fromEntries(
        Object.entries(premiumVals).map(([k,v])=>[k,Math.max(0,Number(v)||0)])
      );
      const u=await updateStock(pump.id,Math.max(0,petrol),Math.max(0,diesel),Math.max(0,cng),cleanPremium,token);
      if(u?.[0])setPump(u[0]);
      // Log the update for the daily tracking system
      await logStockUpdate(pump.id, token).catch(()=>{}); // silent fail — don't block save
      setSaved(true);setTimeout(()=>setSaved(false),4000);
    }
    catch{setSaveErr("Failed to save. Please try again.");}
    setSaving(false);
  }

  const numInp=(val,set,max,color)=>(<input type="number" value={val} onChange={e=>set(Number(e.target.value))} min={0} max={max} style={{ width:90,background:"#0a1628",border:`1px solid ${color}44`,borderRadius:8,padding:"6px 10px",fontSize:14,fontWeight:700,color,textAlign:"right",outline:"none",fontFamily:"inherit" }}/>);

  return(
    <div style={{ minHeight:"100vh",background:"#070d1a",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ background:"#0a1628",borderBottom:"1px solid #1e293b",padding:"0 24px" }}>
        <div style={{ maxWidth:700,margin:"0 auto",padding:"16px 0",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div><div style={{ fontSize:16,fontWeight:900,color:"#f1f5f9",fontFamily:"'Outfit',sans-serif" }}>⛽ Owner Dashboard</div><div style={{ fontSize:11,color:"#64748b" }}>{pump.name}</div></div>
          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
            <span style={{ fontSize:12,color:"#64748b" }}>👤 {email}</span>
            <button onClick={onLogout} style={{ fontSize:12,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontWeight:700,fontFamily:"inherit" }}>Logout</button>
          </div>
        </div>
      </div>
      <div style={{ maxWidth:700,margin:"0 auto",padding:"28px 24px" }}>
        <div style={{ background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,padding:"20px 22px",marginBottom:20 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4 }}>
            <div style={{ fontSize:17,fontWeight:900,color:"#f1f5f9",fontFamily:"'Outfit',sans-serif" }}>{pump.name}</div>
            {streak!==null&&(
              <div style={{ display:"flex",alignItems:"center",gap:5,background:streak>=7?"#f59e0b18":streak>=3?"#22c55e18":"#1e293b",border:`1px solid ${streak>=7?"#f59e0b33":streak>=3?"#22c55e33":"#334155"}`,borderRadius:8,padding:"4px 10px",flexShrink:0 }}>
                <span style={{ fontSize:13 }}>{streak>=7?<IC.Flame size={14} color="#f59e0b"/>:streak>=3?<IC.Shield size={14} color="#22c55e"/>:<IC.Clock size={14} color="#64748b"/>}</span>
                <div>
                  <div style={{ fontSize:11,fontWeight:800,color:streak>=7?"#f59e0b":streak>=3?"#22c55e":"#64748b",lineHeight:1 }}>{streak}-day streak</div>
                  <div style={{ fontSize:9,color:"#475569",marginTop:1 }}>{streak>=7?"On fire! Keep going":streak>=3?"Great consistency!":"Update daily to build streak"}</div>
                </div>
              </div>
            )}
          </div>
          <div style={{ fontSize:13,color:"#64748b",marginBottom:16 }}>{pump.address}</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10 }}>
            {[["Petrol",pump.petrol,pump.max_petrol,"#22c55e"],["Diesel",pump.diesel,pump.max_diesel,"#3b82f6"],pump.max_cng>0?["CNG",pump.cng,pump.max_cng,"#8b5cf6"]:null].filter(Boolean).map(([l,v,m,c])=>(
              <div key={l} style={{ background:"#0a1628",border:`1px solid ${c}22`,borderRadius:12,padding:"14px 16px" }}>
                <div style={{ fontSize:10,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6 }}>{l}</div>
                <div style={{ fontSize:26,fontWeight:900,color:c,lineHeight:1,fontFamily:"'Outfit',sans-serif" }}>{fmtL(v)}</div>
                <div style={{ fontSize:11,color:"#475569",marginTop:4 }}>{m?Math.round((v/m)*100):0}% full</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:"flex",gap:8,marginBottom:20,flexWrap:"wrap" }}>
          {[["stock","Stock"],["shop","Shop"],["hours","Hours"],["analytics","Analytics"]].map(([k,l])=>{
            const isPro=(pump.tier||"basic")==="pro";
            const locked=k==="analytics"&&!isPro;
            const TabIcon = k==="stock"?IC.Package:k==="shop"?IC.ShoppingBag:k==="hours"?IC.Clock:IC.BarChart;
            const tabColor = tab===k?"#f59e0b":locked?"#334155":"#64748b";
            return(
              <button key={k} onClick={()=>locked?null:setTab(k)} title={locked?"Analytics available on Pro plan only":""} style={{ flex:1,padding:"10px 4px",borderRadius:10,fontSize:10,fontWeight:700,cursor:locked?"not-allowed":"pointer",border:`1px solid ${tab===k?"#f59e0b":locked?"#1e293b22":"#1e293b"}`,background:tab===k?"#f59e0b18":"#0f172a",color:tabColor,fontFamily:"inherit",whiteSpace:"nowrap",opacity:locked?0.45:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4 }}>
                <TabIcon size={16} color={tabColor}/>
                {locked?"🔒 ":""}{l}
              </button>
            );
          })}
        </div>
        {(pump.tier||"basic")!=="pro"&&tab==="analytics"&&(
          <div style={{ background:"#1e293b",border:"1px solid #334155",borderRadius:14,padding:"32px 24px",textAlign:"center",marginBottom:20 }}>
            <div style={{ fontSize:28,marginBottom:12 }}>⭐</div>
            <div style={{ fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:8 }}>Analytics is a Pro feature</div>
            <div style={{ fontSize:12,color:"#64748b",lineHeight:1.7,marginBottom:16 }}>Upgrade to Pro (₹299/month) to see daily and weekly directions click trends, fuel breakdown, and monthly reports.</div>
            <div style={{ fontSize:11,color:"#475569" }}>Contact FuelStock admin to upgrade your account.</div>
          </div>
        )}
        {tab==="stock"&&(
          <div style={{ background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,padding:24 }}>
            <div style={{ fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:4,fontFamily:"'Outfit',sans-serif" }}>Update Today's Stock</div>
            <div style={{ fontSize:12,color:"#475569",marginBottom:24 }}>Last updated: <span style={{ color:"#94a3b8" }}>{pump.last_updated}</span></div>
            {[["🟢 Petrol (Litres)",petrol,setPetrol,pump.max_petrol,"#22c55e"],["🔵 Diesel (Litres)",diesel,setDiesel,pump.max_diesel,"#3b82f6"],pump.max_cng>0?["🟣 CNG (kg)",cng,setCng,pump.max_cng,"#8b5cf6"]:null].filter(Boolean).map(([l,v,set,m,c])=>(
              <div key={l} style={{ marginBottom:24 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}><label style={{ fontSize:13,fontWeight:700,color:c }}>{l}</label>{numInp(v,set,m,c)}</div>
                <div style={{ height:6,backgroundColor:"#FDE8D8",borderRadius:3,margin:"8px 0 4px",overflow:"hidden" }}>
                  <div style={{ height:6,borderRadius:3,backgroundColor:c,width:`${m?Math.min(100,Math.round((v/m)*100)):0}%`,transition:"width 0.3s ease" }}/>
                </div>
                <div style={{ display:"flex",justifyContent:"space-between",fontSize:10,color:"#334155",marginTop:4 }}><span>0</span><span>{fmtL(m)} max</span></div>
              </div>
            ))}
            {/* Premium fuel sliders — only shown if this brand has premium grades */}
            {premiumDefs.filter(pf=>pump[pf.max_key]>0).length>0&&(
              <div style={{ marginTop:8,marginBottom:16,borderTop:"1px solid #1e293b",paddingTop:20 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:16,display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ fontSize:10,background:"#f59e0b",color:"#0a0a0a",borderRadius:4,padding:"2px 6px",fontWeight:800,letterSpacing:"0.04em" }}>PREMIUM</span>
                  Premium Fuel Grades
                </div>
                {premiumDefs.filter(pf=>pump[pf.max_key]>0).map(pf=>{
                  const val=premiumVals[pf.key]||0, max=pump[pf.max_key]||pf.default;
                  return(
                    <div key={pf.key} style={{ marginBottom:22 }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}>
                        <label style={{ fontSize:13,fontWeight:700,color:pf.color,display:"flex",alignItems:"center",gap:6 }}>
                          {pf.icon} {pf.label}
                        </label>
                        <input type="number" value={val} min={0} max={max}
                          onChange={e=>setPremiumVals(prev=>({...prev,[pf.key]:Number(e.target.value)}))}
                          style={{ width:90,background:"#0a1628",border:`1px solid ${pf.color}44`,borderRadius:8,padding:"6px 10px",fontSize:14,fontWeight:700,color:pf.color,textAlign:"right",outline:"none",fontFamily:"inherit" }}/>
                      </div>
                      <div style={{ height:6,backgroundColor:"#FDE8D8",borderRadius:3,margin:"8px 0 4px",overflow:"hidden" }}>
                        <div style={{ height:6,borderRadius:3,backgroundColor:pf.color,width:`${max?Math.min(100,Math.round((val/max)*100)):0}%`,transition:"width 0.3s ease" }}/>
                      </div>
                      <div style={{ display:"flex",justifyContent:"space-between",fontSize:10,color:"#334155",marginTop:4 }}><span>0</span><span>{fmtL(max)} max</span></div>
                    </div>
                  );
                })}
              </div>
            )}
            {saved&&<div style={{ background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:"#22c55e",fontWeight:700 }}>✅ Stock updated! Customers can see your latest figures.</div>}
            {saveErr&&<div style={{ background:"#ef444412",border:"1px solid #ef444433",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:"#ef4444" }}>⚠️ {saveErr}</div>}
            <button onClick={handleSave} disabled={saving} style={{ width:"100%",padding:14,background:"linear-gradient(135deg,#f59e0b,#d97706)",border:"none",borderRadius:10,fontSize:14,fontWeight:800,color:"#0a0a0a",cursor:"pointer",opacity:saving?0.6:1,fontFamily:"inherit" }}>{saving?"⏳ Saving...":"💾 Publish Stock Update"}</button>
          </div>
        )}
        {/* ── SHOP TAB ── */}
        {tab==="shop"&&(()=>{
          const today = new Date().toISOString().slice(0,10);
          const todaySales  = (shopSales||[]).filter(s=>s.sold_at?.slice(0,10)===today);
          const todayRev    = todaySales.reduce((sum,s)=>(sum+(s.price_at_sale*s.qty)),0);
          const weekRev     = (shopSales||[]).reduce((sum,s)=>(sum+(s.price_at_sale*s.qty)),0);
          const lowStock    = (shopProducts||[]).filter(p=>p.stock_qty!==null&&p.stock_qty<=5&&p.stock_qty>0);
          const outOfStock  = (shopProducts||[]).filter(p=>p.stock_qty===0||p.in_stock===false);
          const byCategory  = {};
          (shopProducts||[]).forEach(p=>{ if(!byCategory[p.category])byCategory[p.category]=[]; byCategory[p.category].push(p); });

          return (
            <div>
              {/* Revenue summary */}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16 }}>
                {[
                  { l:"Today's Sales", v:`₹${todayRev.toLocaleString("en-IN")}`, c:"#22c55e", sub:`${todaySales.length} transactions` },
                  { l:"This Week",     v:`₹${weekRev.toLocaleString("en-IN")}`,  c:"#f59e0b", sub:"last 7 days" },
                  { l:"Products",      v:shopProducts?.length||0,                c:"#0ea5e9", sub:`${outOfStock.length} out of stock` },
                ].map(s=>(
                  <div key={s.l} style={{ background:"#0a1628",border:`1px solid ${s.c}22`,borderRadius:12,padding:"12px 10px",textAlign:"center" }}>
                    <div style={{ fontSize:18,fontWeight:900,color:s.c,fontFamily:"'Outfit',sans-serif",lineHeight:1 }}>{s.v}</div>
                    <div style={{ fontSize:9,color:s.c,marginTop:3,opacity:0.8 }}>{s.l}</div>
                    <div style={{ fontSize:9,color:"#475569",marginTop:2 }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Low stock alerts */}
              {lowStock.length>0&&(
                <div style={{ background:"#f59e0b12",border:"1px solid #f59e0b33",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start" }}>
                  <IC.Warning size={16} color="#f59e0b"/>
                  <div>
                    <div style={{ fontSize:12,fontWeight:700,color:"#f59e0b",marginBottom:4 }}>Low stock alert</div>
                    <div style={{ fontSize:11,color:"#94a3b8" }}>{lowStock.map(p=>`${p.name} (${p.stock_qty} left)`).join(" · ")}</div>
                  </div>
                </div>
              )}

              {/* Add product button */}
              <button onClick={()=>setShowAddProduct(true)}
                style={{ width:"100%",padding:"11px",background:"#0a1628",border:"1.5px dashed #334155",borderRadius:12,fontSize:13,fontWeight:700,color:"#64748b",cursor:"pointer",fontFamily:"inherit",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add new product
              </button>

              {/* Products by category */}
              {shopLoading&&<div style={{ textAlign:"center",color:"#64748b",padding:24 }}>⏳ Loading...</div>}
              {!shopLoading&&(shopProducts||[]).length===0&&(
                <div style={{ textAlign:"center",padding:32,color:"#475569" }}>
                  <IC.ShoppingBag size={36} color="#334155"/>
                  <div style={{ marginTop:12,fontSize:13 }}>No products yet. Add your first product above.</div>
                </div>
              )}
              {Object.entries(byCategory).map(([cat,prods])=>(
                <div key={cat} style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:"#475569",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8,paddingLeft:4 }}>{cat}</div>
                  <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                    {prods.map(p=>{
                      const outOf = p.stock_qty===0||p.in_stock===false;
                      const isFlashing = saleFlash===p.id;
                      return(
                        <div key={p.id} style={{ background:isFlashing?"#22c55e18":outOf?"#0a1628":"#0f172a",border:`1px solid ${isFlashing?"#22c55e44":outOf?"#ef444422":"#1e293b"}`,borderRadius:12,padding:"12px 14px",transition:"all 0.3s",opacity:outOf?0.65:1 }}>
                          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                            {/* Product info */}
                            <div style={{ flex:1,minWidth:0 }}>
                              <div style={{ fontSize:13,fontWeight:700,color:outOf?"#475569":"#f1f5f9",marginBottom:2 }}>{p.name}</div>
                              <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
                                <span style={{ fontSize:14,fontWeight:800,color:"#f59e0b" }}>₹{Number(p.price).toLocaleString("en-IN")}</span>
                                {p.stock_qty!==null?(
                                  <span style={{ fontSize:11,fontWeight:600,color:p.stock_qty<=5?"#f59e0b":p.stock_qty===0?"#ef4444":"#64748b" }}>
                                    {p.stock_qty===0?"Out of stock":`${p.stock_qty} in stock`}
                                  </span>
                                ):(
                                  <span style={{ fontSize:11,color:"#475569" }}>No qty tracked</span>
                                )}
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div style={{ display:"flex",gap:6,alignItems:"center",flexShrink:0 }}>
                              {/* Edit */}
                              <button onClick={()=>setEditingProduct(p)}
                                style={{ width:32,height:32,borderRadius:8,border:"1px solid #334155",background:"#0a1628",cursor:"pointer",color:"#64748b",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center" }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              </button>
                              {/* Restock + */}
                              <button onClick={()=>handleRestock(p,5)}
                                style={{ padding:"6px 10px",borderRadius:8,border:"1px solid #334155",background:"#0a1628",cursor:"pointer",fontSize:11,fontWeight:700,color:"#64748b",fontFamily:"inherit" }}>
                                +5
                              </button>
                              {/* Sell — big button */}
                              <button onClick={()=>handleSell(p)} disabled={outOf}
                                style={{ padding:"8px 16px",borderRadius:10,border:"none",background:outOf?"#1e293b":"linear-gradient(135deg,#22c55e,#16a34a)",cursor:outOf?"not-allowed":"pointer",fontSize:13,fontWeight:800,color:outOf?"#475569":"#fff",fontFamily:"inherit",minWidth:64,transform:isFlashing?"scale(0.95)":"scale(1)",transition:"transform 0.15s" }}>
                                {isFlashing?"✓ Sold!":"− Sold"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Today's sales log */}
              {todaySales.length>0&&(
                <div style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,padding:"16px 20px",marginTop:8 }}>
                  <div style={{ fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:12 }}>Today's Sales</div>
                  <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                    {todaySales.slice(0,10).map((s,i)=>(
                      <div key={i} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12 }}>
                        <span style={{ color:"#cbd5e1" }}>{s.product_name}</span>
                        <div style={{ display:"flex",gap:12,alignItems:"center" }}>
                          <span style={{ color:"#22c55e",fontWeight:700 }}>₹{Number(s.price_at_sale*s.qty).toLocaleString("en-IN")}</span>
                          <span style={{ color:"#475569" }}>{new Date(s.sold_at).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span>
                        </div>
                      </div>
                    ))}
                    {todaySales.length>10&&<div style={{ fontSize:11,color:"#475569",textAlign:"center",marginTop:4 }}>+{todaySales.length-10} more</div>}
                  </div>
                  <div style={{ borderTop:"1px solid #1e293b",paddingTop:12,marginTop:12,display:"flex",justifyContent:"space-between" }}>
                    <span style={{ fontSize:12,color:"#64748b",fontWeight:700 }}>Total today</span>
                    <span style={{ fontSize:16,fontWeight:900,color:"#22c55e",fontFamily:"'Outfit',sans-serif" }}>₹{todayRev.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Owner product add/edit modal */}
        {(showAddProduct||editingProduct)&&(
          <OwnerProductModal
            product={editingProduct}
            pumpId={pump.id}
            token={token}
            onSave={()=>{ setShowAddProduct(false); setEditingProduct(null); reloadShop(); }}
            onClose={()=>{ setShowAddProduct(false); setEditingProduct(null); }}
            onDelete={async(id)=>{ await ownerDeleteProduct(id,token); setShowAddProduct(false); setEditingProduct(null); reloadShop(); }}
          />
        )}

        {tab==="hours"&&(
          <div style={{ background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,padding:24 }}>
            <div style={{ fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:4,fontFamily:"'Outfit',sans-serif" }}>Operating Hours</div>
            <div style={{ fontSize:12,color:"#475569",marginBottom:20 }}>Set your opening and closing time for each day. Customers see this when they tap your pump.</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10,marginBottom:20 }}>
              {DAYS.map(day=>{
                const h=hoursData[day]||DEFAULT_HOURS;
                const isToday=DAYS[new Date().getDay()]===day;
                return(
                  <div key={day} style={{ background:"#0a1628",border:`1px solid ${isToday?"#f59e0b33":"#1e293b"}`,borderRadius:12,padding:"12px 16px" }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8 }}>
                      <div style={{ fontSize:13,fontWeight:700,color:isToday?"#f59e0b":"#94a3b8",textTransform:"capitalize",width:90 }}>
                        {isToday?"▶ ":""}{day.charAt(0).toUpperCase()+day.slice(1)}
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                        {/* 24 hours toggle */}
                        <button onClick={()=>setHoursData(prev=>({...prev,[day]:{...h,is24h:!h.is24h,isClosed:false}}))}
                          style={{ fontSize:11,fontWeight:700,padding:"5px 10px",borderRadius:7,border:`1px solid ${h.is24h?"#22c55e33":"#334155"}`,background:h.is24h?"#22c55e18":"transparent",color:h.is24h?"#22c55e":"#64748b",cursor:"pointer",fontFamily:"inherit" }}>24 hrs</button>
                        {/* Closed toggle */}
                        <button onClick={()=>setHoursData(prev=>({...prev,[day]:{...h,isClosed:!h.isClosed,is24h:false}}))}
                          style={{ fontSize:11,fontWeight:700,padding:"5px 10px",borderRadius:7,border:`1px solid ${h.isClosed?"#ef444433":"#334155"}`,background:h.isClosed?"#ef444418":"transparent",color:h.isClosed?"#ef4444":"#64748b",cursor:"pointer",fontFamily:"inherit" }}>Closed</button>
                        {/* Time inputs — hidden when 24h or closed */}
                        {!h.is24h&&!h.isClosed&&<>
                          <input type="time" value={h.open||"06:00"} onChange={e=>setHoursData(prev=>({...prev,[day]:{...h,open:e.target.value}}))}
                            style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:7,padding:"5px 8px",fontSize:12,color:"#f1f5f9",outline:"none",fontFamily:"inherit" }}/>
                          <span style={{ fontSize:11,color:"#475569" }}>to</span>
                          <input type="time" value={h.close||"22:00"} onChange={e=>setHoursData(prev=>({...prev,[day]:{...h,close:e.target.value}}))}
                            style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:7,padding:"5px 8px",fontSize:12,color:"#f1f5f9",outline:"none",fontFamily:"inherit" }}/>
                        </>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {hoursSaved&&<div style={{ background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:"#22c55e",fontWeight:700 }}>✅ Hours saved! Customers can now see your schedule.</div>}
            <button onClick={handleSaveHours} disabled={hoursSaving} style={{ width:"100%",padding:14,background:"linear-gradient(135deg,#f59e0b,#d97706)",border:"none",borderRadius:10,fontSize:14,fontWeight:800,color:"#0a0a0a",cursor:"pointer",opacity:hoursSaving?0.6:1,fontFamily:"inherit" }}>
              {hoursSaving?"⏳ Saving...":"💾 Save Hours"}
            </button>
          </div>
        )}
        {tab==="analytics"&&(
          <div style={{ background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,padding:24 }}>
            <div style={{ fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:4,fontFamily:"'Outfit',sans-serif" }}>Customer Analytics</div>
            <div style={{ fontSize:12,color:"#475569",marginBottom:20 }}>Directions clicks — last 7 days</div>
            {clickRows===null?<div style={{ textAlign:"center",color:"#64748b",padding:32 }}>⏳ Loading...</div>:<AnalyticsPanel rows={clickRows}/>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PUMP FORM
// ─────────────────────────────────────────────────────────────────────────────
function PumpForm({ pump=null, token, onSave, onClose }) {
  const blank={name:"",address:"",lat:"",lng:"",owner_email:"",owner_password:"",owner_phone:"",brand:"Indian Oil",max_petrol:8000,max_diesel:12000,max_cng:0,is_suspended:false};
  const [form,setForm]=useState(pump?{...pump,owner_password:""}:blank);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState("");
  const [pwVisible,setPwVisible]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  async function handleSave(){
    if(!form.name||!form.address||!form.lat||!form.lng||!form.owner_email){setErr("Please fill in all required fields.");return;}
    // Password required only when creating a new pump
    if(!pump&&!form.owner_password){setErr("Please set a login password for this owner.");return;}
    if(!pump&&form.owner_password.length<6){setErr("Password must be at least 6 characters.");return;}
    setSaving(true);setErr("");
    try{
      // Step 1 — create the Supabase Auth account (new pumps only)
      if(!pump){
        try{
          await adminCreateUser(form.owner_email, form.owner_password, token);
        }catch(authErr){
          // If account already exists, that's fine — just update the pump record
          if(!authErr.message.toLowerCase().includes("already")){
            throw new Error("Account creation failed: " + authErr.message);
          }
        }
      }
      // Step 2 — save pump record to database
      const {owner_password,...rest}=form; // never store password in DB
      const payload={...rest,lat:parseFloat(form.lat),lng:parseFloat(form.lng),max_petrol:parseInt(form.max_petrol),max_diesel:parseInt(form.max_diesel),max_cng:parseInt(form.max_cng)};
      if(pump){await adminUpdatePump(pump.id,payload,token);}else{delete payload.id;await adminAddPump(payload,token);}
      onSave();
    }catch(e){setErr(e.message);}
    setSaving(false);
  }
  return(
    <Modal title={pump?"Edit Pump":"Add New Pump"} onClose={onClose} width={540}>
      <Field label="Pump Name *"><input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Bharat Petroleum – MG Road" style={inpStyle}/></Field>
      <Field label="Address *"><input value={form.address} onChange={e=>set("address",e.target.value)} placeholder="Full street address" style={inpStyle}/></Field>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <Field label="Latitude *"><input value={form.lat} onChange={e=>set("lat",e.target.value)} placeholder="19.2183" style={inpStyle}/></Field>
        <Field label="Longitude *"><input value={form.lng} onChange={e=>set("lng",e.target.value)} placeholder="73.1745" style={inpStyle}/></Field>
      </div>
      <Field label="Fuel Company / Brand *">
        <select value={form.brand||"Indian Oil"} onChange={e=>set("brand",e.target.value)} style={{...inpStyle,cursor:"pointer"}}>
          {["Indian Oil","HPCL","BPCL","Reliance","Shell","Essar","Other"].map(b=>(
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </Field>
      <Field label="Owner Email *"><input value={form.owner_email} onChange={e=>set("owner_email",e.target.value)} placeholder="owner@pump.com" style={inpStyle}/></Field>
      <Field label="Owner WhatsApp Number">
        <div style={{ position:"relative" }}>
          <input value={form.owner_phone||""} onChange={e=>set("owner_phone",e.target.value)}
            placeholder="919876543210 (country code + number, no spaces)"
            style={inpStyle}/>
        </div>
        <div style={{ fontSize:11,color:"#475569",marginTop:4 }}>
          India: 91 + 10-digit number. Example: 919876543210
        </div>
      </Field>

      {/* Password — only shown when creating a new pump */}
      {!pump&&(
        <Field label="Owner Login Password *">
          <div style={{ position:"relative" }}>
            <input
              type={pwVisible?"text":"password"}
              value={form.owner_password}
              onChange={e=>set("owner_password",e.target.value)}
              placeholder="Min 6 characters — you will WhatsApp this to them"
              style={{...inpStyle,paddingRight:44}}
            />
            <button
              onClick={()=>setPwVisible(v=>!v)}
              style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",fontSize:16,padding:0 }}
            >{pwVisible?"🙈":"👁️"}</button>
          </div>
          {form.owner_password&&form.owner_password.length>=6&&(
            <div style={{ fontSize:11,color:"#22c55e",marginTop:4,display:"flex",alignItems:"center",gap:4 }}>
              ✅ Good — WhatsApp this password to the owner after saving
            </div>
          )}
          <div style={{ fontSize:11,color:"#475569",marginTop:4 }}>
            💡 You can reset this anytime from the pump list if they forget it
          </div>
        </Field>
      )}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <Field label="Max Petrol (L)"><input type="number" value={form.max_petrol} onChange={e=>set("max_petrol",e.target.value)} style={inpStyle}/></Field>
        <Field label="Max Diesel (L)"><input type="number" value={form.max_diesel} onChange={e=>set("max_diesel",e.target.value)} style={inpStyle}/></Field>
      </div>
      <Field label="Max CNG (kg) — 0 if no CNG"><input type="number" value={form.max_cng} onChange={e=>set("max_cng",e.target.value)} style={inpStyle}/></Field>

      {/* Operating Hours — admin can set a default schedule */}
      <div style={{ borderTop:"1px solid #334155",paddingTop:16,marginTop:4,marginBottom:4 }}>
        <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12,display:"flex",alignItems:"center",gap:6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span style={{ color:"#0ea5e9" }}>Operating Hours</span>
          <span style={{ fontSize:9,color:"#475569",fontWeight:400,textTransform:"none" }}>(owner can update from their dashboard)</span>
        </div>
        <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
          {DAYS.map(day=>{
            const key=`hours_${day}`;
            let h=DEFAULT_HOURS;
            try{ if(form[key])h=JSON.parse(form[key]); }catch{}
            return(
              <div key={day} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",background:"#0a1628",border:"1px solid #1e293b",borderRadius:10,padding:"10px 14px",flexWrap:"wrap",gap:8 }}>
                <span style={{ fontSize:12,fontWeight:600,color:"#94a3b8",textTransform:"capitalize",width:80 }}>{day.charAt(0).toUpperCase()+day.slice(1)}</span>
                <div style={{ display:"flex",alignItems:"center",gap:6,flexWrap:"wrap" }}>
                  <button type="button" onClick={()=>set(key,JSON.stringify({...h,is24h:!h.is24h,isClosed:false}))}
                    style={{ fontSize:10,fontWeight:700,padding:"4px 8px",borderRadius:6,border:`1px solid ${h.is24h?"#22c55e33":"#334155"}`,background:h.is24h?"#22c55e18":"transparent",color:h.is24h?"#22c55e":"#64748b",cursor:"pointer",fontFamily:"inherit" }}>24h</button>
                  <button type="button" onClick={()=>set(key,JSON.stringify({...h,isClosed:!h.isClosed,is24h:false}))}
                    style={{ fontSize:10,fontWeight:700,padding:"4px 8px",borderRadius:6,border:`1px solid ${h.isClosed?"#ef444433":"#334155"}`,background:h.isClosed?"#ef444418":"transparent",color:h.isClosed?"#ef4444":"#64748b",cursor:"pointer",fontFamily:"inherit" }}>Closed</button>
                  {!h.is24h&&!h.isClosed&&<>
                    <input type="time" value={h.open||"06:00"} onChange={e=>set(key,JSON.stringify({...h,open:e.target.value}))}
                      style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:6,padding:"4px 6px",fontSize:11,color:"#f1f5f9",outline:"none",fontFamily:"inherit" }}/>
                    <span style={{ fontSize:10,color:"#475569" }}>to</span>
                    <input type="time" value={h.close||"22:00"} onChange={e=>set(key,JSON.stringify({...h,close:e.target.value}))}
                      style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:6,padding:"4px 6px",fontSize:11,color:"#f1f5f9",outline:"none",fontFamily:"inherit" }}/>
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weights & Measures Certification */}
      <div style={{ borderTop:"1px solid #334155",paddingTop:16,marginTop:4,marginBottom:4 }}>
        <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12,display:"flex",alignItems:"center",gap:6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <span style={{ color:"#22c55e" }}>W&M Verification</span>
          <span style={{ fontSize:9,color:"#475569",fontWeight:400,textTransform:"none" }}>(Weights & Measures audit — admin verified)</span>
        </div>
        <Field label="Certificate Number">
          <input value={form.wm_cert_number||""} onChange={e=>set("wm_cert_number",e.target.value)} placeholder="e.g. MH/W&M/2024/001234" style={inpStyle}/>
        </Field>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
          <Field label="Audit Date">
            <input type="date" value={form.wm_audit_date||""} onChange={e=>set("wm_audit_date",e.target.value)} style={inpStyle}/>
          </Field>
          <Field label="Valid Until">
            <input type="date" value={form.wm_valid_until||""} onChange={e=>set("wm_valid_until",e.target.value)} style={inpStyle}/>
          </Field>
        </div>
        {form.wm_cert_number&&(
          <div style={{ fontSize:11,color:"#22c55e",background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:8,padding:"8px 12px",marginTop:4 }}>
            🛡️ Shield badge will appear on public card once saved
          </div>
        )}
      </div>

      {/* Premium fuel capacity fields — shown based on selected brand */}
      {getPremiumFuels(form.brand||"Indian Oil").length>0&&(
        <div style={{ borderTop:"1px solid #334155",paddingTop:16,marginTop:4,marginBottom:4 }}>
          <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12,display:"flex",alignItems:"center",gap:6 }}>
            <span style={{ fontSize:9,background:"#f59e0b",color:"#0a0a0a",borderRadius:4,padding:"2px 6px",fontWeight:800 }}>PREMIUM</span>
            Premium Fuel Capacity (set 0 to disable)
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
            {getPremiumFuels(form.brand||"Indian Oil").map(pf=>(
              <Field key={pf.key} label={`Max ${pf.label} (L)`}>
                <input type="number" value={form[pf.max_key]||0} onChange={e=>set(pf.max_key,e.target.value)}
                  style={{...inpStyle,borderColor:`${pf.color}44`,color:pf.color}}/>
              </Field>
            ))}
          </div>
        </div>
      )}
      {err&&<div style={{ fontSize:13,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:8,padding:"10px 14px",marginBottom:14 }}>⚠️ {err}</div>}
      <div style={{ display:"flex",gap:10,marginTop:4 }}>
        <button onClick={onClose} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ flex:2,padding:12,background:"linear-gradient(135deg,#6366f1,#4f46e5)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",opacity:saving?0.6:1,fontFamily:"inherit" }}>{saving?"Saving...":pump?"Save Changes":"Add Pump"}</button>
      </div>
    </Modal>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// OWNER PRODUCT MODAL — owner adds / edits their own products
// ─────────────────────────────────────────────────────────────────────────────
function OwnerProductModal({ product=null, pumpId, token, onSave, onClose, onDelete }) {
  const blank = { pump_id:pumpId, name:"", category:"Engine Oil", price:"", stock_qty:"", in_stock:true };
  const [form,setForm] = useState(product ? { ...product, price:String(product.price||""), stock_qty:String(product.stock_qty??"")} : blank);
  const [saving,setSaving] = useState(false);
  const [deleting,setDeleting] = useState(false);
  const [confirm,setConfirm] = useState(false);
  const [err,setErr] = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  async function handleSave() {
    if(!form.name||!form.price||!form.category){ setErr("Product name, category and price are required."); return; }
    setSaving(true); setErr("");
    try {
      const payload = {
        ...form,
        pump_id:   pumpId,
        price:     parseFloat(form.price)||0,
        stock_qty: form.stock_qty!==""?parseInt(form.stock_qty):null,
        in_stock:  form.stock_qty===""?form.in_stock:parseInt(form.stock_qty)>0,
      };
      if(product) { await ownerUpdateProduct(product.id, payload, token); }
      else        { delete payload.id; await ownerAddProduct(payload, token); }
      onSave();
    } catch(e) { setErr(e.message||"Failed to save"); }
    setSaving(false);
  }

  return (
    <Modal title={product?"Edit Product":"New Product"} onClose={onClose} width={440}>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <Field label="Category *">
          <select value={form.category} onChange={e=>set("category",e.target.value)} style={{ width:"100%",padding:"10px 14px",background:"#0a1628",border:"1px solid #334155",borderRadius:10,color:"#f1f5f9",fontSize:14,outline:"none",fontFamily:"inherit",cursor:"pointer" }}>
            {PRODUCT_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Price (₹) *">
          <input type="number" value={form.price} onChange={e=>set("price",e.target.value)} placeholder="e.g. 250"
            style={{ width:"100%",padding:"10px 14px",background:"#0a1628",border:"1px solid #334155",borderRadius:10,color:"#f59e0b",fontSize:14,outline:"none",fontFamily:"inherit" }}/>
        </Field>
      </div>
      <Field label="Product Name *">
        <input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Castrol GTX 15W-40 1L"
          style={{ width:"100%",padding:"10px 14px",background:"#0a1628",border:"1px solid #334155",borderRadius:10,color:"#f1f5f9",fontSize:14,outline:"none",fontFamily:"inherit" }}/>
      </Field>
      <Field label="Stock Quantity (leave blank if not tracking)">
        <input type="number" value={form.stock_qty} onChange={e=>set("stock_qty",e.target.value)} placeholder="e.g. 20"
          style={{ width:"100%",padding:"10px 14px",background:"#0a1628",border:"1px solid #334155",borderRadius:10,color:"#f1f5f9",fontSize:14,outline:"none",fontFamily:"inherit" }}/>
      </Field>
      {form.stock_qty===""&&(
        <div style={{ display:"flex",gap:8,marginBottom:12 }}>
          {[true,false].map(v=>(
            <button key={String(v)} onClick={()=>set("in_stock",v)}
              style={{ flex:1,padding:"10px",borderRadius:8,border:`1.5px solid ${form.in_stock===v?(v?"#22c55e":"#ef4444"):"#334155"}`,background:form.in_stock===v?(v?"#22c55e18":"#ef444418"):"#0a1628",color:form.in_stock===v?(v?"#22c55e":"#ef4444"):"#64748b",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
              {v?"✓ In Stock":"✕ Out of Stock"}
            </button>
          ))}
        </div>
      )}
      {err&&<div style={{ fontSize:13,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:8,padding:"10px 14px",marginBottom:12 }}>⚠️ {err}</div>}
      <div style={{ display:"flex",gap:8,marginTop:4 }}>
        <button onClick={onClose} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
        {product&&!confirm&&(
          <button onClick={()=>setConfirm(true)} style={{ padding:"12px 16px",background:"#ef444418",border:"1px solid #ef444433",borderRadius:10,fontSize:13,fontWeight:700,color:"#ef4444",cursor:"pointer",fontFamily:"inherit" }}>Delete</button>
        )}
        {product&&confirm&&(
          <button onClick={async()=>{setDeleting(true);await onDelete(product.id);}} disabled={deleting}
            style={{ padding:"12px 16px",background:"#ef4444",border:"none",borderRadius:10,fontSize:13,fontWeight:700,color:"#fff",cursor:"pointer",fontFamily:"inherit",opacity:deleting?0.6:1 }}>{deleting?"...":"Confirm Delete"}</button>
        )}
        <button onClick={handleSave} disabled={saving}
          style={{ flex:2,padding:12,background:"linear-gradient(135deg,#f59e0b,#d97706)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#0a0a0a",cursor:"pointer",opacity:saving?0.6:1,fontFamily:"inherit" }}>
          {saving?"Saving...":product?"Save Changes":"Add Product"}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT FORM  (admin)
// ─────────────────────────────────────────────────────────────────────────────
function ProductForm({ product=null, pumps, token, onSave, onClose }) {
  const blank = { pump_id:"", name:"", category:"Engine Oil", price:"", quantity:"", in_stock:true };
  const [form,setForm] = useState(product ? {...product, price:String(product.price), quantity:String(product.quantity||"")} : blank);
  const [saving,setSaving] = useState(false);
  const [err,setErr] = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  async function handleSave() {
    if(!form.pump_id||!form.name||!form.price||!form.category){ setErr("Please fill in all required fields."); return; }
    setSaving(true); setErr("");
    try {
      const payload = { ...form, price:parseFloat(form.price), quantity:form.quantity?parseInt(form.quantity):null };
      if (product) { await adminUpdateProduct(product.id, payload, token); }
      else         { delete payload.id; await adminAddProduct(payload, token); }
      onSave();
    } catch(e) { setErr(e.message); }
    setSaving(false);
  }

  return (
    <Modal title={product?"Edit Product":"Add Product"} onClose={onClose} width={480}>
      <Field label="Petrol Pump *">
        <select value={form.pump_id} onChange={e=>set("pump_id",e.target.value)} style={{...inpStyle,cursor:"pointer"}}>
          <option value="">Select a pump...</option>
          {pumps.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <Field label="Category *">
          <select value={form.category} onChange={e=>set("category",e.target.value)} style={{...inpStyle,cursor:"pointer"}}>
            {PRODUCT_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Price (₹) *">
          <input type="number" value={form.price} onChange={e=>set("price",e.target.value)} placeholder="e.g. 250" style={inpStyle}/>
        </Field>
      </div>
      <Field label="Product Name *">
        <input value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Castrol GTX 15W-40 1L" style={inpStyle}/>
      </Field>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
        <Field label="Stock Quantity (optional)">
          <input type="number" value={form.quantity} onChange={e=>set("quantity",e.target.value)} placeholder="e.g. 20" style={inpStyle}/>
        </Field>
        <Field label="Availability">
          <div style={{ display:"flex",gap:8,marginTop:2 }}>
            {[true,false].map(v=>(
              <button key={String(v)} onClick={()=>set("in_stock",v)} style={{ flex:1,padding:"10px 8px",borderRadius:8,border:`1.5px solid ${form.in_stock===v?(v?"#22c55e":"#ef4444"):"#334155"}`,background:form.in_stock===v?(v?"#22c55e18":"#ef444418"):"transparent",color:form.in_stock===v?(v?"#22c55e":"#ef4444"):"#94a3b8",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                {v?"In Stock":"Out of Stock"}
              </button>
            ))}
          </div>
        </Field>
      </div>
      {err&&<div style={{ fontSize:13,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:8,padding:"10px 14px",marginBottom:14 }}>⚠️ {err}</div>}
      <div style={{ display:"flex",gap:10,marginTop:4 }}>
        <button onClick={onClose} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ flex:2,padding:12,background:"linear-gradient(135deg,#22c55e,#16a34a)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",opacity:saving?0.6:1,fontFamily:"inherit" }}>{saving?"Saving...":product?"Save Changes":"Add Product"}</button>
      </div>
    </Modal>
  );
}

function SubBadge({ status }) {
  const map={paid:["#22c55e","Paid"],free:["#64748b","Free Trial"],overdue:["#ef4444","Overdue"]};
  const [color,label]=map[status]||map.free;
  return <span style={{ fontSize:10,fontWeight:700,color,background:color+"18",border:`1px solid ${color}33`,borderRadius:5,padding:"2px 7px" }}>{label}</span>;
}


// ─────────────────────────────────────────────────────────────────────────────
// RESET PASSWORD MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ResetPasswordModal({ pump, onClose, onSuccess, adminToken }) {
  const [newPw,  setNewPw]  = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [visible,setVisible]= useState(false);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  async function handleReset() {
    if (!newPw)            { setErr("Please enter a new password.");           return; }
    if (newPw.length < 6)  { setErr("Password must be at least 6 characters."); return; }
    if (newPw !== newPw2)  { setErr("Passwords don't match.");                 return; }
    setSaving(true); setErr("");
    try {
      await adminResetPassword(pump.owner_email, newPw, adminToken);
      onSuccess(pump.name);
    } catch(e) {
      setErr(e.message);
    }
    setSaving(false);
  }

  const inp2 = { ...inpStyle, paddingRight: 44 };

  return (
    <Modal title="Reset Owner Password" onClose={onClose} width={420}>
      {/* Pump info */}
      <div style={{ background:"#0a1628",borderRadius:12,padding:"12px 16px",marginBottom:20 }}>
        <div style={{ fontSize:13,fontWeight:700,color:"#f1f5f9",marginBottom:2 }}>{pump.name}</div>
        <div style={{ fontSize:11,color:"#64748b" }}>👤 {pump.owner_email}</div>
      </div>

      <div style={{ fontSize:12,color:"#64748b",marginBottom:16,lineHeight:1.6,background:"#0ea5e912",border:"1px solid #0ea5e933",borderRadius:10,padding:"10px 14px" }}>
        💡 Set a new password below, then <strong style={{ color:"#0ea5e9" }}>WhatsApp it directly to the owner</strong>. They use it to log into the Owner Portal.
      </div>

      <Field label="New Password">
        <div style={{ position:"relative" }}>
          <input type={visible?"text":"password"} value={newPw} onChange={e=>setNewPw(e.target.value)}
            placeholder="Min 6 characters" style={inp2}/>
          <button onClick={()=>setVisible(v=>!v)}
            style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",fontSize:16,padding:0 }}>
            {visible?"🙈":"👁️"}
          </button>
        </div>
      </Field>

      <Field label="Confirm New Password">
        <div style={{ position:"relative" }}>
          <input type={visible?"text":"password"} value={newPw2} onChange={e=>setNewPw2(e.target.value)}
            placeholder="Re-enter password" style={inp2}
            onKeyDown={e=>e.key==="Enter"&&handleReset()}/>
          <button onClick={()=>setVisible(v=>!v)}
            style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",fontSize:16,padding:0 }}>
            {visible?"🙈":"👁️"}
          </button>
        </div>
        {newPw&&newPw2&&newPw===newPw2&&<div style={{ fontSize:11,color:"#22c55e",marginTop:4 }}>✅ Passwords match</div>}
        {newPw&&newPw2&&newPw!==newPw2&&<div style={{ fontSize:11,color:"#ef4444",marginTop:4 }}>❌ Passwords don't match</div>}
      </Field>

      {err&&<div style={{ fontSize:13,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444433",borderRadius:8,padding:"10px 14px",marginBottom:14 }}>⚠️ {err}</div>}

      <div style={{ display:"flex",gap:10,marginTop:4 }}>
        <button onClick={onClose} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
        <button onClick={handleReset} disabled={saving||newPw!==newPw2||newPw.length<6}
          style={{ flex:2,padding:12,background:"linear-gradient(135deg,#0ea5e9,#0284c7)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",opacity:(saving||newPw!==newPw2||newPw.length<6)?0.5:1,fontFamily:"inherit" }}>
          {saving?"Updating...":"🔑 Set New Password"}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function AdminDashboard({ token, email, onLogout }) {
  const [tab,setTab]=useState("pumps");
  const [pumps,setPumps]=useState([]);
  const [subs,setSubs]=useState({});
  const [allClicks,setAllClicks]=useState(null);
  const [loading,setLoading]=useState(true);
  const [showForm,setShowForm]=useState(false);
  const [editPump,setEditPump]=useState(null);
  const [confirmDel,setConfirmDel]=useState(null);
  const [msg,setMsg]=useState("");
  const [resetPwPump,setResetPwPump]=useState(null);
  const [allProducts,setAllProducts]=useState(null);
  const [prodLoading,setProdLoading]=useState(false);
  const [allSales,setAllSales]=useState(null);
  const [salesLoading,setSalesLoading]=useState(false);
  const [showProdForm,setShowProdForm]=useState(false);
  const [editProd,setEditProd]=useState(null);
  const [confirmDelProd,setConfirmDelProd]=useState(null);
  const [updateLog,setUpdateLog]=useState(null);
  const [logLoading,setLogLoading]=useState(false);
  const [logDays,setLogDays]=useState(7);

  const loadPumps=useCallback(async()=>{
    setLoading(true);
    try{
      const[ps,ss]=await Promise.all([getAllPumps(token),getSubs(token).catch(()=>[])]);
      setPumps(ps||[]);const m={};(ss||[]).forEach(s=>{m[s.pump_id]=s;});setSubs(m);
    }catch(e){console.error(e);}
    setLoading(false);
  },[token]);

  useEffect(()=>{loadPumps();},[loadPumps]);
  useEffect(()=>{
    if(tab==="analytics"&&allClicks===null){getAllClicks(token).then(r=>setAllClicks(r||[])).catch(()=>setAllClicks([]));}
    if(tab==="sales"&&allSales===null){setSalesLoading(true);getAllSales(token).then(r=>{setAllSales(r||[]);setSalesLoading(false);}).catch(()=>setSalesLoading(false));}
  },[tab]);
  useEffect(()=>{
    if(tab==="products"&&allProducts===null){
      setProdLoading(true);
      getAllProducts(token).then(r=>{setAllProducts(r||[]);setProdLoading(false);}).catch(()=>{setAllProducts([]);setProdLoading(false);});
    }
    if(tab==="log"){
      setLogLoading(true);
      getUpdateLog(logDays,token).then(r=>{setUpdateLog(r||[]);setLogLoading(false);}).catch(()=>{setUpdateLog([]);setLogLoading(false);});
    }
  },[tab]);
  const reloadProducts=()=>{ setProdLoading(true); getAllProducts(token).then(r=>{setAllProducts(r||[]);setProdLoading(false);}).catch(()=>setProdLoading(false)); };

  async function handleSuspend(pump) {
    try {
      await adminSuspend(pump.id, !pump.is_suspended, token);
      flash(pump.is_suspended ? "Pump restored — now visible to public." : "Pump suspended — hidden from public.");
      await loadPumps();
    } catch(e) {
      flash("Error: " + (e.message || "Could not update pump. Check Supabase RLS policy allows admin to update is_suspended."));
    }
  }
  async function handleDelete(id){ await adminDeletePump(id,token); setConfirmDel(null); flash("Pump deleted."); await loadPumps(); }
  async function handleTierChange(pumpId, tier) {
    try {
      await adminUpdatePump(pumpId, { tier }, token);
      flash(`Pump upgraded to ${tier === "pro" ? "⭐ Pro" : "Basic"}.`);
      await loadPumps();
    } catch(e) { flash("Error: " + e.message); }
  }

  async function handleSubChange(pumpId, status, billingDate) {
    try {
      // Check if a subscription row already exists for this pump
      const existing = await getSubForPump(pumpId, token);
      if (existing && existing.length > 0) {
        // Row exists — PATCH it
        await patchSub(existing[0].id, { status, billing_date: billingDate || null }, token);
      } else {
        // No row yet — INSERT a new one
        await insertSub({ pump_id: pumpId, status, billing_date: billingDate || null }, token);
      }
      flash("Subscription updated.");
      await loadPumps();
    } catch(e) {
      flash("Error updating subscription: " + e.message);
    }
  }
  function flash(m){setMsg(m);setTimeout(()=>setMsg(""),3500);}

  const paidCount=Object.values(subs).filter(s=>s.status==="paid").length;
  const overdueCount=Object.values(subs).filter(s=>s.status==="overdue").length;
  const proPaidCount=pumps.filter(p=>(p.tier||"basic")==="pro"&&subs[p.id]?.status==="paid").length;
  const basicPaidCount=paidCount-proPaidCount;
  const revenue=proPaidCount*299+basicPaidCount*99;

  return(
    <div style={{ minHeight:"100vh",background:"#050b14",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ background:"linear-gradient(180deg,#0a0f1e,#050b14)",borderBottom:"1px solid #1e293b",padding:"0 24px" }}>
        <div style={{ maxWidth:1100,margin:"0 auto",padding:"16px 0",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <div style={{ width:38,height:38,background:"linear-gradient(135deg,#6366f1,#4f46e5)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>🛡️</div>
            <div><div style={{ fontSize:16,fontWeight:900,color:"#f1f5f9",fontFamily:"'Outfit',sans-serif" }}>FuelStock Admin</div><div style={{ fontSize:10,color:"#475569",letterSpacing:"0.08em" }}>SUPER ADMIN PANEL</div></div>
          </div>
          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
            <span style={{ fontSize:11,color:"#475569" }}>🔐 {email}</span>
            <button onClick={onLogout} style={{ fontSize:12,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontWeight:700,fontFamily:"inherit" }}>Logout</button>
          </div>
        </div>
      </div>
      <div style={{ maxWidth:1100,margin:"0 auto",padding:"28px 24px" }}>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,marginBottom:28 }}>
          {[{label:"Total Pumps",value:pumps.length,color:"#6366f1",icon:"🏪"},{label:"Live",value:pumps.filter(p=>!p.is_suspended).length,color:"#22c55e",icon:"✅"},{label:"Suspended",value:pumps.filter(p=>p.is_suspended).length,color:"#ef4444",icon:"🚫"},{label:"Paid Subs",value:paidCount,color:"#22c55e",icon:"💳"},{label:"Overdue",value:overdueCount,color:"#ef4444",icon:"⚠️"},{label:"Monthly Rev",value:`₹${revenue.toLocaleString()}`,color:"#f59e0b",icon:"💰"},{label:"Clicks (30d)",value:allClicks?.length||"—",color:"#0ea5e9",icon:"📍"}].map(s=>(
            <div key={s.label} style={{ background:"#0a1628",border:`1px solid ${s.color}22`,borderRadius:12,padding:"14px 16px" }}>
              <div style={{ fontSize:18 }}>{s.icon}</div>
              <div style={{ fontSize:22,fontWeight:900,color:s.color,lineHeight:1.1,marginTop:4,fontFamily:"'Outfit',sans-serif" }}>{s.value}</div>
              <div style={{ fontSize:10,color:"#475569",marginTop:3 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {msg&&<div style={{ background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:10,padding:"12px 16px",marginBottom:20,fontSize:13,color:"#22c55e",fontWeight:700 }}>✅ {msg}</div>}
        <div style={{ display:"flex",gap:8,marginBottom:24,flexWrap:"wrap" }}>
          {[["pumps","Pumps"],["products","Products"],["log","Daily Log"],["sales","Sales"],["analytics","Analytics"],["subscriptions","Subscriptions"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{ padding:"10px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",border:`1px solid ${tab===k?"#6366f1":"#1e293b"}`,background:tab===k?"#6366f118":"#0a1628",color:tab===k?"#818cf8":"#64748b",fontFamily:"inherit" }}>{l}</button>
          ))}
        </div>

        {tab==="pumps"&&(
          <div>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
              <div style={{ fontSize:14,fontWeight:700,color:"#94a3b8" }}>{pumps.length} pump{pumps.length!==1?"s":""} registered</div>
              <button onClick={()=>{setEditPump(null);setShowForm(true);}} style={{ padding:"10px 18px",background:"linear-gradient(135deg,#6366f1,#4f46e5)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",fontFamily:"inherit" }}>+ Add Pump</button>
            </div>
            {loading?<div style={{ textAlign:"center",color:"#64748b",padding:48 }}>⏳ Loading...</div>:(
              <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                {pumps.map(p=>{
                  const sub=subs[p.id],pp=lvl(p.petrol,p.max_petrol),dp=lvl(p.diesel,p.max_diesel);
                  return(
                    <div key={p.id} style={{ background:"#0a1628",border:`1px solid ${p.is_suspended?"#ef444422":"#1e293b"}`,borderRadius:14,padding:"16px 20px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",opacity:p.is_suspended?0.7:1 }}>
                      <div style={{ flex:1,minWidth:200 }}>
                        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap" }}>
                          <div style={{ fontSize:14,fontWeight:800,color:"#f1f5f9" }}>{p.name}</div>
                          {p.is_suspended&&<span style={{ fontSize:10,fontWeight:700,color:"#ef4444",background:"#ef444412",border:"1px solid #ef444422",borderRadius:4,padding:"1px 6px" }}>SUSPENDED</span>}
                          <SubBadge status={sub?.status||"free"}/>
                        </div>
                        <div style={{ fontSize:11,color:"#475569" }}>{p.address}</div>
                        <div style={{ fontSize:11,color:"#334155",marginTop:2 }}>👤 {p.owner_email}</div>
                      </div>
                      <div style={{ display:"flex",gap:16,alignItems:"center",flexShrink:0 }}>
                        <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:"#475569",marginBottom:2 }}>PETROL</div><div style={{ fontSize:14,fontWeight:800,color:sColor(pp) }}>{pp}%</div></div>
                        <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:"#475569",marginBottom:2 }}>DIESEL</div><div style={{ fontSize:14,fontWeight:800,color:sColor(dp) }}>{dp}%</div></div>
                        {p.max_cng>0&&<div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:"#475569",marginBottom:2 }}>CNG</div><div style={{ fontSize:14,fontWeight:800,color:sColor(lvl(p.cng,p.max_cng)) }}>{lvl(p.cng,p.max_cng)}%</div></div>}
                      </div>
                      <div style={{ display:"flex",gap:8,flexShrink:0,flexWrap:"wrap" }}>
                        <button onClick={()=>{setEditPump(p);setShowForm(true);}} style={{ fontSize:11,fontWeight:700,color:"#818cf8",background:"#6366f114",border:"1px solid #6366f133",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit" }}>✏️ Edit</button>
                        <button onClick={()=>setResetPwPump(p)} style={{ fontSize:11,fontWeight:700,color:"#0ea5e9",background:"#0ea5e914",border:"1px solid #0ea5e933",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit" }}>🔑 Password</button>
                        <button onClick={()=>handleSuspend(p)} style={{ fontSize:11,fontWeight:700,color:p.is_suspended?"#22c55e":"#f59e0b",background:p.is_suspended?"#22c55e14":"#f59e0b14",border:`1px solid ${p.is_suspended?"#22c55e33":"#f59e0b33"}`,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit" }}>{p.is_suspended?"▶ Restore":"⏸ Suspend"}</button>
                        <button onClick={()=>setConfirmDel(p)} style={{ fontSize:11,fontWeight:700,color:"#ef4444",background:"#ef444414",border:"1px solid #ef444433",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit" }}>🗑 Delete</button>
                      </div>
                    </div>
                  );
                })}
                {pumps.length===0&&<div style={{ textAlign:"center",color:"#475569",padding:48,fontSize:14 }}>No pumps yet. Click "+ Add Pump" to get started.</div>}
              </div>
            )}
          </div>
        )}

        {/* ── PRODUCTS TAB ── */}
        {tab==="products"&&(
          <div>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
              <div style={{ fontSize:14,fontWeight:700,color:"#94a3b8" }}>{allProducts?.length||0} product{allProducts?.length!==1?"s":""} listed</div>
              <button onClick={()=>{setEditProd(null);setShowProdForm(true);}} style={{ padding:"10px 18px",background:"linear-gradient(135deg,#22c55e,#16a34a)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",fontFamily:"inherit" }}>+ Add Product</button>
            </div>
            {prodLoading&&<div style={{ textAlign:"center",color:"#64748b",padding:48 }}>⏳ Loading products...</div>}
            {!prodLoading&&allProducts&&allProducts.length===0&&(
              <div style={{ textAlign:"center",color:"#475569",padding:48 }}>
                <div style={{ fontSize:32,marginBottom:8 }}>🛒</div>
                <div style={{ fontSize:14 }}>No products yet. Click "+ Add Product" to start.</div>
              </div>
            )}
            {!prodLoading&&allProducts&&allProducts.length>0&&(
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {/* Group by pump */}
                {[...new Set(allProducts.map(p=>p.pump_id))].map(pid=>{
                  const pumpName = pumps.find(p=>p.id===pid)?.name || "Unknown Pump";
                  const pumpProducts = allProducts.filter(p=>p.pump_id===pid);
                  return (
                    <div key={pid} style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,padding:"16px 20px" }}>
                      <div style={{ fontSize:13,fontWeight:800,color:"#f1f5f9",marginBottom:12 }}>⛽ {pumpName}</div>
                      <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                        {pumpProducts.map(prod=>(
                          <div key={prod.id} style={{ display:"flex",alignItems:"center",gap:12,background:"#0f172a",borderRadius:10,padding:"10px 14px",flexWrap:"wrap" }}>
                            <div style={{ flex:1,minWidth:120 }}>
                              <div style={{ fontSize:13,fontWeight:700,color:"#f1f5f9" }}>{prod.name}</div>
                              <div style={{ fontSize:11,color:"#475569",marginTop:1 }}>{prod.category}{prod.quantity!=null?` · Qty: ${prod.quantity}`:""}</div>
                            </div>
                            <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
                              <span style={{ fontSize:15,fontWeight:800,color:"#f59e0b" }}>₹{prod.price}</span>
                              <span style={{ fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:99,background:prod.in_stock?"#22c55e18":"#ef444418",color:prod.in_stock?"#22c55e":"#ef4444",border:`1px solid ${prod.in_stock?"#22c55e33":"#ef444433"}` }}>{prod.in_stock?"In Stock":"Out"}</span>
                              <button onClick={()=>{setEditProd(prod);setShowProdForm(true);}} style={{ fontSize:11,fontWeight:700,color:"#818cf8",background:"#6366f114",border:"1px solid #6366f133",borderRadius:7,padding:"5px 10px",cursor:"pointer",fontFamily:"inherit" }}>Edit</button>
                              <button onClick={()=>setConfirmDelProd(prod)} style={{ fontSize:11,fontWeight:700,color:"#ef4444",background:"#ef444414",border:"1px solid #ef444433",borderRadius:7,padding:"5px 10px",cursor:"pointer",fontFamily:"inherit" }}>Del</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── DAILY LOG TAB ── */}
        {tab==="log"&&(()=>{
          const today = new Date().toISOString().slice(0,10);

          // Build a map: pumpId -> array of dates updated
          const logMap = {};
          (updateLog||[]).forEach(r=>{
            if(!logMap[r.pump_id]) logMap[r.pump_id]=[];
            logMap[r.pump_id].push({ date:r.date, time:r.updated_at });
          });

          // Build last N days array
          const days = Array.from({length:logDays},(_,i)=>{
            const d=new Date(); d.setDate(d.getDate()-(logDays-1-i));
            return d.toISOString().slice(0,10);
          });

          const updatedToday   = pumps.filter(p=>logMap[p.id]?.some(r=>r.date===today));
          const notUpdatedToday= pumps.filter(p=>!logMap[p.id]?.some(r=>r.date===today)&&!p.is_suspended);

          const waMsg = (pump) => encodeURIComponent(
            `Hi! This is a reminder from FuelDost to update your fuel stock for today. Please log in and update: https://fueldost.in/?owner=true\n\nPump: ${pump.name}`
          );

          return (
            <div>
              {/* Summary cards */}
              <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20 }}>
                {[
                  { l:"Updated Today", v:updatedToday.length,    c:"#22c55e", bg:"#22c55e18" },
                  { l:"Not Updated",   v:notUpdatedToday.length, c:"#ef4444", bg:"#ef444418" },
                  { l:"Total Active",  v:pumps.filter(p=>!p.is_suspended).length, c:"#94a3b8", bg:"#1e293b" },
                ].map(s=>(
                  <div key={s.l} style={{ background:s.bg,border:`1px solid ${s.c}33`,borderRadius:12,padding:"14px 10px",textAlign:"center" }}>
                    <div style={{ fontSize:26,fontWeight:900,color:s.c,fontFamily:"'Outfit',sans-serif",lineHeight:1 }}>{s.v}</div>
                    <div style={{ fontSize:10,color:s.c,marginTop:4,opacity:0.85 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Days selector */}
              <div style={{ display:"flex",gap:6,marginBottom:16,alignItems:"center" }}>
                <span style={{ fontSize:12,color:"#64748b" }}>Show last:</span>
                {[7,14,30].map(d=>(
                  <button key={d} onClick={()=>{ setLogDays(d); setUpdateLog(null); setLogLoading(true); getUpdateLog(d,token).then(r=>{setUpdateLog(r||[]);setLogLoading(false);}).catch(()=>setLogLoading(false)); }}
                    style={{ fontSize:11,fontWeight:700,padding:"5px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",border:`1px solid ${logDays===d?"#f59e0b":"#334155"}`,background:logDays===d?"#f59e0b18":"transparent",color:logDays===d?"#f59e0b":"#64748b" }}>
                    {d} days
                  </button>
                ))}
              </div>

              {/* NOT UPDATED TODAY — action section */}
              {notUpdatedToday.length>0&&(
                <div style={{ background:"#0a1628",border:"1px solid #ef444433",borderRadius:14,padding:"16px 20px",marginBottom:16 }}>
                  <div style={{ fontSize:13,fontWeight:800,color:"#ef4444",marginBottom:12,display:"flex",alignItems:"center",gap:8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {notUpdatedToday.length} pump{notUpdatedToday.length!==1?"s":""} not updated today
                  </div>
                  <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                    {notUpdatedToday.map(p=>{
                      const lastUpdate = logMap[p.id]?.[0];
                      return (
                        <div key={p.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",background:"#0f172a",borderRadius:10,padding:"10px 14px",flexWrap:"wrap",gap:8 }}>
                          <div>
                            <div style={{ fontSize:13,fontWeight:700,color:"#f1f5f9" }}>{p.name}</div>
                            <div style={{ fontSize:11,color:"#475569",marginTop:2 }}>
                              {lastUpdate
                                ? `Last updated: ${lastUpdate.date} at ${new Date(lastUpdate.time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}`
                                : "Never updated"}
                            </div>
                          </div>
                          <a href={`https://wa.me/${p.owner_phone||""}?text=${waMsg(p)}`} target="_blank" rel="noopener noreferrer"
                            style={{ display:"flex",alignItems:"center",gap:6,background:"#25D36618",border:"1px solid #25D36633",borderRadius:8,padding:"7px 12px",fontSize:11,fontWeight:700,color:"#25D366",textDecoration:"none",flexShrink:0 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            Remind on WhatsApp
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* FULL LOG TABLE — all pumps, all days */}
              <div style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,overflow:"hidden" }}>
                <div style={{ padding:"14px 20px",borderBottom:"1px solid #1e293b",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                  <div style={{ fontSize:13,fontWeight:700,color:"#94a3b8" }}>Update history — last {logDays} days</div>
                  <div style={{ fontSize:11,color:"#475569" }}>
                    <span style={{ display:"inline-block",width:10,height:10,background:"#22c55e",borderRadius:2,marginRight:4,verticalAlign:"middle" }}/>Updated
                    <span style={{ display:"inline-block",width:10,height:10,background:"#1e293b",borderRadius:2,margin:"0 4px 0 10px",verticalAlign:"middle",border:"1px solid #334155" }}/>Not updated
                  </div>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%",borderCollapse:"collapse",minWidth:500 }}>
                    <thead>
                      <tr style={{ background:"#0f172a" }}>
                        <th style={{ padding:"8px 16px",fontSize:11,fontWeight:700,color:"#64748b",textAlign:"left",borderBottom:"1px solid #1e293b",whiteSpace:"nowrap" }}>Pump</th>
                        {days.map(d=>(
                          <th key={d} style={{ padding:"8px 8px",fontSize:10,fontWeight:600,color:d===today?"#f59e0b":"#64748b",textAlign:"center",borderBottom:"1px solid #1e293b",whiteSpace:"nowrap",background:d===today?"#f59e0b08":"transparent" }}>
                            {new Date(d+'T12:00:00').toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"})}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pumps.filter(p=>!p.is_suspended).map((p,pi)=>(
                        <tr key={p.id} style={{ background:pi%2===0?"#0a1628":"#0f172a" }}>
                          <td style={{ padding:"8px 16px",fontSize:12,fontWeight:600,color:"#f1f5f9",borderBottom:"1px solid #1e293b22",whiteSpace:"nowrap" }}>
                            {p.name.length>22?p.name.slice(0,22)+"…":p.name}
                          </td>
                          {days.map(d=>{
                            const upd = logMap[p.id]?.find(r=>r.date===d);
                            return (
                              <td key={d} style={{ padding:"6px 8px",textAlign:"center",borderBottom:"1px solid #1e293b22",background:d===today?"#f59e0b06":"transparent" }}>
                                {upd?(
                                  <div title={`Updated at ${new Date(upd.time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}`}
                                    style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:20,background:"#22c55e18",border:"1px solid #22c55e44",borderRadius:5,cursor:"default" }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                  </div>
                                ):(
                                  <div style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:20,background:"#1e293b",border:"1px solid #334155",borderRadius:5 }}>
                                    <div style={{ width:6,height:1,background:"#475569" }}/>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {logLoading&&<div style={{ textAlign:"center",color:"#64748b",padding:24 }}>⏳ Loading log...</div>}
            </div>
          );
        })()}

        {/* ── ADMIN SALES TAB ── */}
        {tab==="sales"&&(()=>{
          const today   = new Date().toISOString().slice(0,10);
          const sales   = allSales||[];
          const todayS  = sales.filter(s=>s.sold_at?.slice(0,10)===today);
          const totalRev= sales.reduce((a,s)=>a+(s.price_at_sale*s.qty),0);
          const todayRev= todayS.reduce((a,s)=>a+(s.price_at_sale*s.qty),0);

          // Revenue per pump
          const pumpRev={};
          sales.forEach(s=>{ pumpRev[s.pump_id]=(pumpRev[s.pump_id]||0)+(s.price_at_sale*s.qty); });

          // Top products by units sold
          const prodCount={};
          sales.forEach(s=>{ prodCount[s.product_name]=(prodCount[s.product_name]||0)+s.qty; });
          const topProds=Object.entries(prodCount).sort((a,b)=>b[1]-a[1]).slice(0,5);

          // Daily revenue last 7 days
          const days7=Array.from({length:7},(_,i)=>{ const d=new Date();d.setDate(d.getDate()-(6-i));return d.toISOString().slice(0,10); });
          const dayRev={};
          days7.forEach(d=>{dayRev[d]=0;});
          sales.forEach(s=>{ const d=s.sold_at?.slice(0,10); if(dayRev[d]!==undefined)dayRev[d]+=(s.price_at_sale*s.qty); });
          const maxRev=Math.max(...Object.values(dayRev),1);

          return (
            <div>
              {/* Summary */}
              <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20 }}>
                {[
                  { l:"Today Revenue",  v:`₹${todayRev.toLocaleString("en-IN")}`,  c:"#22c55e" },
                  { l:"30-Day Revenue", v:`₹${totalRev.toLocaleString("en-IN")}`,  c:"#f59e0b" },
                  { l:"Transactions",   v:sales.length,                             c:"#0ea5e9" },
                ].map(s=>(
                  <div key={s.l} style={{ background:"#0a1628",border:`1px solid ${s.c}22`,borderRadius:12,padding:"14px 10px",textAlign:"center" }}>
                    <div style={{ fontSize:20,fontWeight:900,color:s.c,fontFamily:"'Outfit',sans-serif",lineHeight:1 }}>{s.v}</div>
                    <div style={{ fontSize:10,color:s.c,marginTop:4,opacity:0.8 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* 7-day bar chart */}
              <div style={{ background:"#0a1628",borderRadius:14,padding:"18px 16px",marginBottom:16 }}>
                <div style={{ fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:14 }}>Daily Revenue — Last 7 Days</div>
                <div style={{ display:"flex",alignItems:"flex-end",gap:6,height:90 }}>
                  {days7.map(date=>{
                    const rev=dayRev[date];
                    const barH=Math.max((rev/maxRev)*70,rev>0?6:3);
                    const isT=date===today;
                    return(
                      <div key={date} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,height:"100%",justifyContent:"flex-end" }}>
                        {rev>0&&<div style={{ fontSize:9,fontWeight:700,color:isT?"#22c55e":"#64748b" }}>₹{rev>=1000?Math.round(rev/1000)+"k":rev}</div>}
                        <div style={{ width:"100%",height:barH,borderRadius:4,background:isT?"#22c55e":"#334155" }}/>
                        <div style={{ fontSize:9,color:isT?"#22c55e":"#475569",fontWeight:isT?700:400 }}>{new Date(date+"T12:00:00").toLocaleDateString("en-IN",{weekday:"short"})}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top products */}
              {topProds.length>0&&(
                <div style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,padding:"16px 20px",marginBottom:16 }}>
                  <div style={{ fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:14 }}>Top Products (units sold)</div>
                  {topProds.map(([name,qty],i)=>(
                    <div key={name} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                      <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                        <div style={{ width:20,height:20,borderRadius:6,background:i===0?"#f59e0b22":"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:i===0?"#f59e0b":"#64748b" }}>{i+1}</div>
                        <span style={{ fontSize:13,color:"#cbd5e1" }}>{name}</span>
                      </div>
                      <span style={{ fontSize:13,fontWeight:700,color:"#22c55e" }}>{qty} sold</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Per-pump revenue */}
              <div style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,padding:"16px 20px" }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:14 }}>Revenue by Pump (30 days)</div>
                {pumps.filter(p=>pumpRev[p.id]).sort((a,b)=>(pumpRev[b.id]||0)-(pumpRev[a.id]||0)).map(p=>(
                  <div key={p.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #1e293b22" }}>
                    <span style={{ fontSize:12,color:"#cbd5e1" }}>{p.name}</span>
                    <span style={{ fontSize:14,fontWeight:800,color:"#f59e0b" }}>₹{Number(pumpRev[p.id]).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                {pumps.every(p=>!pumpRev[p.id])&&<div style={{ fontSize:12,color:"#475569",textAlign:"center",padding:16 }}>No sales recorded yet.</div>}
              </div>
              {salesLoading&&<div style={{ textAlign:"center",color:"#64748b",padding:16 }}>⏳ Loading...</div>}
            </div>
          );
        })()}

        {tab==="analytics"&&(
          <div>
            <div style={{ fontSize:14,fontWeight:700,color:"#94a3b8",marginBottom:20 }}>Platform-wide directions clicks — all pumps combined</div>
            {allClicks===null?<div style={{ textAlign:"center",color:"#64748b",padding:48 }}>⏳ Loading...</div>:<>
              <AnalyticsPanel rows={allClicks}/>
              {pumps.length>0&&<>
                <div style={{ fontSize:13,fontWeight:700,color:"#64748b",marginTop:28,marginBottom:14,letterSpacing:"0.06em",textTransform:"uppercase" }}>Per-Pump Clicks (last 30 days)</div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {[...pumps].sort((a,b)=>(allClicks.filter(c=>c.pump_id===b.id).length)-(allClicks.filter(c=>c.pump_id===a.id).length)).map(p=>{
                    const count=allClicks.filter(c=>c.pump_id===p.id).length;
                    const maxC=Math.max(...pumps.map(pp=>allClicks.filter(c=>c.pump_id===pp.id).length),1);
                    return(<div key={p.id} style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:12,padding:"12px 16px" }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}><div style={{ fontSize:13,fontWeight:700,color:"#cbd5e1" }}>{p.name}</div><div style={{ fontSize:14,fontWeight:900,color:"#0ea5e9" }}>{count} clicks</div></div>
                      <div style={{ height:5,background:"#1e293b",borderRadius:99,overflow:"hidden" }}><div style={{ width:`${Math.round((count/maxC)*100)}%`,height:"100%",background:"linear-gradient(90deg,#0ea5e988,#0ea5e9)",borderRadius:99,transition:"width 0.8s" }}/></div>
                    </div>);
                  })}
                </div>
              </>}
            </>}
          </div>
        )}

        {tab==="subscriptions"&&(
          <div>
            <div style={{ fontSize:14,fontWeight:700,color:"#94a3b8",marginBottom:20 }}>Manage owner subscriptions — ₹299/month per pump</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10,marginBottom:24 }}>
              {pumps.map(p=>{
                const sub=subs[p.id]||{status:"free",billing_date:""};
                return(<div key={p.id} style={{ background:"#0a1628",border:"1px solid #1e293b",borderRadius:14,padding:"16px 20px" }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12 }}>
                    <div>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap" }}><div style={{ fontSize:14,fontWeight:800,color:"#f1f5f9" }}>{p.name}</div><SubBadge status={sub.status}/></div>
                      <div style={{ fontSize:11,color:"#475569" }}>{p.owner_email}</div>
                      {sub.billing_date&&<div style={{ fontSize:11,color:"#64748b",marginTop:2 }}>Next billing: {sub.billing_date}</div>}
                    </div>
                    <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
                      {/* Tier toggle */}
                      <div style={{ display:"flex",borderRadius:8,overflow:"hidden",border:"1px solid #334155" }}>
                        {["basic","pro"].map(t=>(
                          <button key={t} onClick={()=>handleTierChange(p.id,t)}
                            style={{ padding:"7px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",border:"none",
                              background:( p.tier||"basic")===t?(t==="pro"?"#6366f1":"#334155"):"#0f172a",
                              color:(p.tier||"basic")===t?"#fff":"#64748b",textTransform:"uppercase",letterSpacing:"0.04em" }}>
                            {t==="pro"?"⭐ Pro":"Basic"}
                          </button>
                        ))}
                      </div>
                      <select value={sub.status} onChange={e=>handleSubChange(p.id,e.target.value,sub.billing_date)} style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:8,padding:"7px 12px",fontSize:12,color:"#f1f5f9",outline:"none",fontFamily:"inherit",cursor:"pointer" }}>
                        <option value="free">Free Trial</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                      </select>
                      <input type="date" value={sub.billing_date||""} onChange={e=>handleSubChange(p.id,sub.status,e.target.value)} style={{ background:"#0f172a",border:"1px solid #334155",borderRadius:8,padding:"7px 10px",fontSize:12,color:"#94a3b8",outline:"none",fontFamily:"inherit" }}/>
                    </div>
                  </div>
                </div>);
              })}
            </div>
            <div style={{ background:"#0a1628",border:"1px solid #f59e0b22",borderRadius:14,padding:"20px 22px" }}>
              <div style={{ fontSize:13,fontWeight:800,color:"#f59e0b",marginBottom:14 }}>💰 Revenue Summary</div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16 }}>
                {[{label:"Paid",count:paidCount,color:"#22c55e"},{label:"Overdue",count:overdueCount,color:"#ef4444"},{label:"Free Trial",count:pumps.length-paidCount-overdueCount,color:"#64748b"}].map(s=>(
                  <div key={s.label} style={{ textAlign:"center",background:"#0f172a",borderRadius:10,padding:"12px 8px" }}>
                    <div style={{ fontSize:24,fontWeight:900,color:s.color,fontFamily:"'Outfit',sans-serif" }}>{s.count}</div>
                    <div style={{ fontSize:11,color:"#475569",marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop:"1px solid #1e293b",paddingTop:16,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div style={{ fontSize:13,color:"#64748b" }}>Monthly recurring (paid × ₹299)</div>
                <div style={{ fontSize:24,fontWeight:900,color:"#f59e0b",fontFamily:"'Outfit',sans-serif" }}>₹{revenue.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showForm&&<PumpForm pump={editPump} token={token} onSave={()=>{setShowForm(false);setEditPump(null);flash(editPump?"Pump updated.":"New pump added.");loadPumps();}} onClose={()=>{setShowForm(false);setEditPump(null);}}/>}
      {resetPwPump&&<ResetPasswordModal pump={resetPwPump} adminToken={token} onClose={()=>setResetPwPump(null)} onSuccess={(name)=>{setResetPwPump(null);flash(`Password updated for ${name}. WhatsApp the new password to the owner.`);}}/>}
      {showProdForm&&<ProductForm product={editProd} pumps={pumps} token={token} onSave={()=>{setShowProdForm(false);setEditProd(null);flash(editProd?"Product updated.":"Product added.");reloadProducts();}} onClose={()=>{setShowProdForm(false);setEditProd(null);}}/>}
      {confirmDelProd&&(
        <Modal title="Delete Product" onClose={()=>setConfirmDelProd(null)} width={380}>
          <div style={{ fontSize:14,color:"#94a3b8",marginBottom:20,lineHeight:1.7 }}>Delete <strong style={{ color:"#f1f5f9" }}>{confirmDelProd.name}</strong> from this pump? This cannot be undone.</div>
          <div style={{ display:"flex",gap:10 }}>
            <button onClick={()=>setConfirmDelProd(null)} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
            <button onClick={async()=>{await adminDeleteProduct(confirmDelProd.id,token);setConfirmDelProd(null);flash("Product deleted.");reloadProducts();}} style={{ flex:1,padding:12,background:"linear-gradient(135deg,#ef4444,#dc2626)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",fontFamily:"inherit" }}>Delete</button>
          </div>
        </Modal>
      )}
      {confirmDel&&(
        <Modal title="Delete Pump" onClose={()=>setConfirmDel(null)} width={400}>
          <div style={{ fontSize:14,color:"#94a3b8",marginBottom:20,lineHeight:1.7 }}>Are you sure you want to permanently delete <strong style={{ color:"#f1f5f9" }}>{confirmDel.name}</strong>? This cannot be undone.</div>
          <div style={{ display:"flex",gap:10 }}>
            <button onClick={()=>setConfirmDel(null)} style={{ flex:1,padding:12,background:"#1e293b",border:"1px solid #334155",borderRadius:10,fontSize:13,fontWeight:700,color:"#94a3b8",cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
            <button onClick={()=>handleDelete(confirmDel.id)} style={{ flex:1,padding:12,background:"linear-gradient(135deg,#ef4444,#dc2626)",border:"none",borderRadius:10,fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",fontFamily:"inherit" }}>Yes, Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [page,setPage]           = useState(getInitialPage);
  const [token,setToken]         = useState(null);
  const [userEmail,setUserEmail] = useState(null);
  const [ownerPump,setOwnerPump] = useState(null);

  function handleOwnerLogin(t,email,pump){ setToken(t);setUserEmail(email);setOwnerPump(pump);setPage("owner"); }
  function handleAdminLogin(t,email)    { setToken(t);setUserEmail(email);setPage("admin"); }
  function handleLogout()               { setToken(null);setUserEmail(null);setOwnerPump(null);setPage("public"); }

  if(page==="public")      return <PublicPage onGoToOwner={()=>setPage("owner-login")}/>;
  if(page==="pricing")     return <PricingPage onBack={()=>setPage("public")}/>;
  if(page==="owner-login") return <LoginPage title="Owner Portal" subtitle="Update your daily fuel stock" onLogin={handleOwnerLogin} onBack={()=>setPage("public")}/>;
  if(page==="owner")       return <OwnerDashboard token={token} email={userEmail} pump={ownerPump} onLogout={handleLogout}/>;
  if(page==="admin-login") return <LoginPage title="Admin Panel" subtitle="Super admin access only" accentColor="#6366f1" isAdmin onLogin={handleAdminLogin}/>;
  if(page==="admin")       return <AdminDashboard token={token} email={userEmail} onLogout={handleLogout}/>;
  return null;
}
